import { Renderer, type PointLight, type MeshInstance, type PomLayerOption } from "./engine/renderer";
import { t, setText, initI18n, getLang } from "./i18n";
import { SfxLoop, playOneShot, loadSfx, unlockSfx } from "./engine/sfx";
import {
  installMobileGestureGuards, enterFullscreen, canRequestFullscreen,
  isStandalone, isIOS,
} from "./engine/mobileShell";
import { Input, Btn } from "./engine/input";
import { initTouchControls, type TouchControlsHandle } from "./engine/touchControls";
import { buildArena, pickClientGrid, PLAYER_RADIUS, type GridMap, type LevelGridManifestBounds, type MeshCollisionEntry } from "./engine/world";
import {
  applyInput,
  PredictionBuffer,
  MOVE_SPEED,
  TICK_DT,
  VISUAL_FEET_SMOOTH_TIME,
  VISUAL_FEET_SMOOTH_MAX_SPEED,
  VISUAL_FEET_SMOOTH_SNAP,
} from "./engine/prediction";
import { SnapshotBuffer } from "./engine/interpolation";
import {
  packBodyHitsUniform,
  BODY_FLESH_DEBRIS_CUBE_SIZE, BLOOD_DEBRIS_COLOR, MEAT_MESH_DEBRIS_COLOR,
} from "./engine/voxel";
import { syncBodyVoxCellDebris } from "./engine/bodyVoxDebris";
import { applyServerBodyVox } from "./engine/bodyVoxMerge";
import {
  buildRealVoxelStableCellMap,
  precomputeInnerSubmeshCentroids,
  updateRealVoxelSubmeshHidden,
} from "./engine/realVoxBody";
import {
  initBodyVoxLimbDetach, updateInnerMeshLimbDetach, bodyVoxLegsCanSupportClient,
} from "./engine/bodyVoxLimbs";
import { setBaseAnim, startUpperLayer, stopUpperLayer } from "./engine/animation";
import {
  findJointSubtreeIndices, FP_HIDDEN_BONE_NAMES,
  canSkinInnerToOuterPose, type LoadedMesh,
} from "./engine/meshLoader";
import { clamp, lerp, lerpAngle } from "./engine/math";
import { ChunkRegistry, ensureColumnMaps, sampleChunkCubeColor } from "./engine/chunks";
import { FallingGroupRegistry } from "./engine/falling";
import {
  markTileDamaged,
  floorAtColumn,
  PLAYER_COLLIDER_HEIGHT,
  PLAYER_COLLIDER_HEIGHT_CROUCH,
  setMansionDoorGhostChunks,
  setMeshCollision,
  setMansionDoorMeshGhost,
  setInteriorDoorGhostBase,
  getInteriorDoorBases,
  feetNearMansionDoorInteract,
  feetNearInteriorDoorInteract,
} from "./engine/wallCollision";
import { fetchTerrainHeightmap, setTerrainHeightmap } from "./engine/terrainHeightmap";
import { buildMaterialAtlas, loadTexture } from "./engine/textures";
import { PALETTE_MATERIAL_ORDER } from "./engine/renderer";
import { ParticleSimulation } from "./engine/particles/simulate";
import { presetFireEmitter, presetSmokeEmitter } from "./engine/particles/types";
import {
  Npc, NPC_MAX_HP, NPC_STATE_ALIVE, ACTOR_RADIUS, ACTOR_HEIGHT,
  intersectCylinder,
  triggerHurtFlinch, stopHurtFlinchIfDone, HURT_IDLE_TIMEOUT_MS,
} from "./engine/npc";
import { WebSocketTransport } from "./net/transport";
import { WeaponsClient } from "./weapons/weaponsClient"; // weapons
import { ArtifactsClient } from "./artifacts/artifactsClient"; // artifacts
import { StoryClient } from "./story/storyClient"; // story
import {
  ChunkRole, EvType, MsgType, NpcBodyDamagedFlagHeadshot, PickupType, Reader, ShotSurf, Writer, decompressChunkInit, readChunkInit, readEventBatch, readHello, readPickupInit, readSnapshot, writeInput,
  HelloInteractKindMansionDoor,
  MatchMode, MatchPhase, Team,
  type HelloInteractHotspot,
  type PickupInit,
  type GameEvent,
  type SnapshotNpc,
} from "./net/protocol";
// Диагностика запуска с телефонов: шаги загрузки и ошибки — в лог сервера.
import { installClientDiag, diag, diagStage } from "./engine/clientDiag";
import { installAnalytics, track, trackAuthOnce } from "./analytics";
installClientDiag();
installAnalytics();
// Заставка при запуске — вся логика в src/intro (см. блок «Заставка» внизу файла).
import { initIntro, introBlocksMusic, warmHttpCache, warmLevel } from "./intro";
// characters: персонажи, выбор на старте, апгрейды (client/src/characters/*).
import {
  registerCharacterVisual, getCharacterVisual, ensureCharacterVisual, type CharacterVisual,
} from "./characters/models";
import {
  appendCharacterWsParams, applyHelloCharacter, charIdOf, getSelectedCharId, maxHpOf,
  noteSnapshotCharacters, resetCharacterSession,
} from "./characters/state";
import { hideCharacterStep, initCharacterMenu, onAuthAppliedForCharacters } from "./characters/menu";

// i18n: язык (?lang / выбор в меню / язык браузера) и перевод index.html —
// до первого показа меню.
initI18n();

/** До инициализации остального main: обработчики UI (spStart и др.) читают transport. */
let transport: WebSocketTransport | null = null;

const canvas = document.getElementById("game") as HTMLCanvasElement;
/** Должен быть инициализирован до `new Renderer()`: при падении компиляции шейдеров модуль обрывается и `let` ниже по файлу не выполняется — иначе TDZ при вызове start() из обработчика. */
let startLock = false;
/** Ложь до первого кадра после снятия лоадера: не шлём input, не считаем сессию «в игре». */
let gameplayLive = false;
const hud = document.getElementById("hud") as HTMLDivElement;
const profilerEl = document.getElementById("profiler") as HTMLDivElement;
const scoreboard = document.getElementById("scoreboard") as HTMLDivElement;
const overlay = document.getElementById("overlay") as HTMLDivElement;
const pauseResumeRow = document.getElementById("pauseResumeRow") as HTMLDivElement;
const resumeGameBtn = document.getElementById("resumeGameBtn") as HTMLButtonElement;
const playBtn = document.getElementById("play") as HTMLButtonElement;
const playLabel = document.getElementById("playLabel") as HTMLSpanElement;
const playSub = document.getElementById("playSub") as HTMLSpanElement;
const mainMenu = document.getElementById("mainMenu") as HTMLDivElement;
const crosshairEl = document.querySelector(".crosshair") as HTMLDivElement;
/** Пока идёт fp_fire.webm — не показываем fp_weapon (иначе два альфа-слоя). */
let fpWeaponSuppressedForMuzzleFlash = false;
/** FP оружие: walk-слой vs idle — гистерезис по `playerSpeedSmoothed` (как baseAnim). */
let fpWeaponOverlayWalk = false;
/** 0 = idle, 1 = walk — сглаживается для кроссфейда между WebM. */
let fpWeaponWalkBlend = 0;
const FP_WEAPON_WALK_ENTER = 0.4;
const FP_WEAPON_WALK_EXIT = 0.22;
/** λ для экспон. сглаживания blend (~200–350 мс до цели при 60 FPS). */
const FP_WEAPON_BLEND_LAMBDA = 11;

// weapons: вид от первого лица — 3D-модель оружия и руки (src/weapons).
// Старые видео-оверлеи — только для сравнения по ?fpvideo=1 (без элементов
// весь их код ниже молчит).
const FP_VIDEO_WEAPON = new URLSearchParams(location.search).get("fpvideo") === "1";
const _fpWeaponIdleVideoEl = FP_VIDEO_WEAPON ? document.getElementById("fp-weapon-video-idle") as HTMLVideoElement | null : null;
const _fpWeaponWalkVideoEl = FP_VIDEO_WEAPON ? document.getElementById("fp-weapon-video-walk") as HTMLVideoElement | null : null;
if (_fpWeaponIdleVideoEl) {
  _fpWeaponIdleVideoEl.addEventListener("error", () => {
    const err = _fpWeaponIdleVideoEl!.error;
    console.warn("[fp-weapon-video-idle] load/decode error", err?.code, err?.message);
  });
}
if (_fpWeaponWalkVideoEl) {
  _fpWeaponWalkVideoEl.addEventListener("error", () => {
    const err = _fpWeaponWalkVideoEl!.error;
    console.warn("[fp-weapon-video-walk] load/decode error", err?.code, err?.message);
  });
}
const _fpWeaponPixelCanvas = document.getElementById("fp-weapon-pixel") as HTMLCanvasElement | null;
const _fpWeaponPixelCtx = _fpWeaponPixelCanvas?.getContext("2d") ?? null;
const _fpFireVideoEl = FP_VIDEO_WEAPON ? document.getElementById("fp-fire-video") as HTMLVideoElement | null : null;
if (_fpFireVideoEl) {
  _fpFireVideoEl.addEventListener("error", () => {
    const err = _fpFireVideoEl!.error;
    console.warn("[fp-fire-video] load/decode error", err?.code, err?.message);
    fpWeaponSuppressedForMuzzleFlash = false;
  });
  _fpFireVideoEl.addEventListener("ended", () => {
    _fpFireVideoEl.classList.add("hidden");
    fpWeaponSuppressedForMuzzleFlash = false;
  });
}
// ── Safari не декодирует альфа-канал VP9/WebM (оверлей оружия превращается в
// непрозрачный прямоугольник) — подменяем источники на HEVC-alpha .mov (hvc1).
// UA-детект вместо canPlayType: Chrome на машинах с hw-HEVC отвечает
// "probably" на hvc1, но альфа-слой при этом не декодирует.
const isSafariNoWebmAlpha =
  /safari/i.test(navigator.userAgent) &&
  !/chrome|chromium|crios|edg|android/i.test(navigator.userAgent);

/** Гарантирует правильные <source> у видео-оверлеев оружия: Safari → .mov
 *  (HEVC-alpha), остальные → .webm. Также ВОССТАНАВЛИВАЕТ источники после
 *  mobile-lite-блока (он их удаляет), когда PerfGovernor поднимает качество
 *  обратно выше tier 2. Ничего не делает, если src уже правильный. */
/** ?noweapon=1 — полностью выгрузить альфа-видео оружия, как это делает
 *  мобильный профиль. Диагностика Safari: на десктопе видео ДЕКОДИРУЮТСЯ
 *  всегда (CSS .fp-pixelsrc прячет их через visibility, что сохраняет
 *  декодирование), и в Safari это HEVC-с-альфой — расход, которого нет ни
 *  в Chrome (там VP9), ни на айфоне (там src снят совсем). */
/** ?nofootsteps=1 — не трогать футстеп-лупы вообще (диагностика Safari). */
const NO_FOOTSTEP_AUDIO = new URLSearchParams(location.search).get("nofootsteps") === "1";
const NO_WEAPON_VIDEO = new URLSearchParams(location.search).get("noweapon") === "1";

function unloadWeaponVideos(): void {
  for (const el of [_fpWeaponIdleVideoEl, _fpWeaponWalkVideoEl, _fpFireVideoEl]) {
    if (!el) continue;
    try {
      el.pause();
      el.removeAttribute("src");
      for (const s of el.querySelectorAll("source")) s.remove();
      el.load();
      el.style.display = "none";
    } catch { /* */ }
  }
}

function ensureWeaponVideoSources(): void {
  if (NO_WEAPON_VIDEO) { unloadWeaponVideos(); return; }
  const pairs: Array<[HTMLVideoElement | null, string]> = [
    [_fpWeaponIdleVideoEl, "fp_weapon_idle"],
    [_fpWeaponWalkVideoEl, "fp_weapon_walk"],
    [_fpFireVideoEl, "fp_fire"],
  ];
  for (const [el, base] of pairs) {
    if (!el) continue;
    const want = isSafariNoWebmAlpha ? `/video/${base}.mov` : `/video/${base}.webm`;
    const wantType = isSafariNoWebmAlpha ? "video/quicktime" : "video/webm";
    let srcEl = el.querySelector("source");
    if (!srcEl) {
      srcEl = document.createElement("source");
      srcEl.src = want;
      srcEl.type = wantType;
      el.appendChild(srcEl);
      el.load();
    } else if (srcEl.getAttribute("src") !== want) {
      srcEl.src = want;
      srcEl.type = wantType;
      el.load();
    }
  }
}
/** Safari (и строгие autoplay-политики): программный play() видео вне
 *  пользовательского жеста даёт NotAllowedError → видео-оружие вечно стоит на
 *  кадре 0 и оверлей невидим. Прогреваем все три видео синхронно ВНУТРИ
 *  первого реального жеста (play+pause помечает элемент user-activated,
 *  после этого программные play() из игрового цикла разрешены). */
let fpVideosWarmed = false;
function warmUpFpVideosOnGesture(): void {
  // Web Audio требует user-gesture для resume() — цепляемся к тому же жесту.
  unlockSfx();
  if (fpVideosWarmed) return;
  fpVideosWarmed = true;
  for (const el of [_fpWeaponIdleVideoEl, _fpWeaponWalkVideoEl, _fpFireVideoEl]) {
    if (!el) continue;
    try {
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          // не глушим, если игра уже идёт и апдейтер сам управляет видео
          if (!gameplayLive) { el.pause(); el.currentTime = 0; }
        }).catch(() => { fpVideosWarmed = false; /* ретрай на следующем жесте */ });
      }
    } catch { fpVideosWarmed = false; }
  }
}
document.addEventListener("pointerdown", warmUpFpVideosOnGesture, { capture: true });
document.addEventListener("keydown", warmUpFpVideosOnGesture, { capture: true });

const killfeedEl = document.getElementById("killfeed") as HTMLDivElement;
const loaderEl = document.getElementById("loader") as HTMLDivElement;
const loaderText = document.getElementById("loaderText") as HTMLDivElement;
const loaderFill = document.getElementById("loaderFill") as HTMLDivElement;
const loaderBar = document.getElementById("loaderBar") as HTMLDivElement;
const loaderErrorRow = document.getElementById("loaderErrorRow") as HTMLDivElement;
const loaderOk = document.getElementById("loaderOk") as HTMLButtonElement;
const npcHpBarEl = document.getElementById("npc-hpbar") as HTMLDivElement;
const npcHpBarFillEl = document.getElementById("npc-hpbar-fill") as HTMLDivElement;
const hpOverlayEl = document.getElementById("hp-overlay") as HTMLDivElement;
const hpValueEl = document.getElementById("hp-value") as HTMLDivElement;
const hpBarFillEl = document.getElementById("hp-bar-fill") as HTMLDivElement;
const hpDeadEl = document.getElementById("hp-dead") as HTMLDivElement;
const ammoStatEl = document.getElementById("ammo-stat") as HTMLDivElement;
const ammoValueEl = document.getElementById("ammo-value") as HTMLDivElement;
const ammoMaxEl = document.getElementById("ammo-max") as HTMLDivElement;
const ammoBarFillEl = document.getElementById("ammo-bar-fill") as HTMLDivElement;
const damageFlashEl = document.getElementById("damage-flash") as HTMLDivElement;
const touchControlsEl = document.getElementById("touch-controls") as HTMLDivElement;
const touchPauseBtn = document.getElementById("touch-pause-btn") as HTMLButtonElement | null;

function detectTouchUi(): boolean {
  // ?touch=1 — принудительно включить мобильный интерфейс на десктопе:
  // без этого тач-управление нельзя проверить нигде, кроме реального телефона.
  if (new URLSearchParams(location.search).get("touch") === "1") return true;
  const coarse =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const touch = (navigator.maxTouchPoints ?? 0) > 0;
  if (coarse && touch) return true;
  if (touch && window.innerWidth < 600) return true;
  return false;
}
const touchUi = detectTouchUi();
// Гасим браузерные жесты (пинч-зум, лупа, двойной тап, pull-to-refresh) —
// то, что CSS на iOS не покрывает. См. engine/mobileShell.ts.
if (touchUi) installMobileGestureGuards();
if (touchUi) {
  const hint = document.getElementById("interact-hint");
  hint?.classList.add("interact-hint--tap");
  hint?.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    pulseTouchUse();
  });
}

/** iPhone не умеет Fullscreen API — единственный способ убрать интерфейс
 *  браузера это запуск с домашнего экрана. Показываем подсказку один раз. */
const LS_A2HS_DISMISSED = "tipaduma.a2hsTipDismissed";
function maybeShowAddToHomeScreenTip(): void {
  if (!touchUi || !isIOS() || isStandalone()) return;
  try { if (localStorage.getItem(LS_A2HS_DISMISSED) === "1") return; } catch { /* */ }
  const tip = document.createElement("div");
  tip.id = "a2hsTip";
  const text = document.createElement("div");
  text.style.flex = "1 1 auto";
  text.textContent = t(
    "Хотите на весь экран? «Поделиться» → «На экран «Домой»» — " +
    "оттуда игра запускается без панелей браузера.");
  const close = document.createElement("button");
  close.id = "a2hsTipClose";
  close.type = "button";
  close.setAttribute("aria-label", t("Закрыть подсказку"));
  close.textContent = "×";
  close.addEventListener("click", () => {
    tip.remove();
    try { localStorage.setItem(LS_A2HS_DISMISSED, "1"); } catch { /* */ }
  });
  tip.append(text, close);
  document.body.appendChild(tip);
}

/** Подсказка живёт только в меню: в бою она перекрывала бы прицел/HUD. */
function hideAddToHomeScreenTip(): void {
  document.getElementById("a2hsTip")?.remove();
}
maybeShowAddToHomeScreenTip();

/** Телефоны: VP9/WebM с альфой в video на iOS часто без прозрачности; экономим GPU и трафик. */
function detectMobileLiteGraphics(): boolean {
  const q = new URLSearchParams(location.search);
  if (q.get("gfx") === "desktop") return false;
  if (q.get("gfx") === "high" || q.get("mobileGfx") === "0") return false;
  if (q.get("mobileGfx") === "1") return true;
  try {
    if (localStorage.getItem("tipaduma.forceDesktopGfx") === "1") return false;
  } catch { /* */ }
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  const plat = navigator.platform || "";
  if ((plat === "MacIntel" || plat === "iPad") && (navigator.maxTouchPoints ?? 0) > 1) return true;
  if (/Android/i.test(ua)) return true;
  if (touchUi && window.innerWidth <= 1024) return true;
  return false;
}
/** Базовый детект (телефоны/планшеты — всегда lite). Поверх него PerfGovernor
 *  может включать lite и на слабых десктопах — см. applyGfxTier. */
const mobileLiteBase = detectMobileLiteGraphics();
let mobileLiteGraphics = mobileLiteBase;
// Десктоп: сразу выставляем правильный формат видео-оружия (Safari → .mov).
// На мобильном источники всё равно вырезаются lite-блоком ниже.
if (!mobileLiteBase) ensureWeaponVideoSources();

const authStatus = document.getElementById("authStatus") as HTMLParagraphElement;
const authGuestCard = document.getElementById("authGuestCard") as HTMLDivElement;
const authProfileCard = document.getElementById("authProfileCard") as HTMLDivElement;
const authLogout = document.getElementById("authLogout") as HTMLButtonElement;
const authYandex = document.getElementById("authYandex") as HTMLButtonElement;
const authGoogle = document.getElementById("authGoogle") as HTMLButtonElement;
const authEmail = document.getElementById("authEmail") as HTMLInputElement;
const authEmailSend = document.getElementById("authEmailSend") as HTMLButtonElement;
const profileAvatar = document.getElementById("profileAvatar") as HTMLDivElement;
const profileName = document.getElementById("profileName") as HTMLDivElement;
const profileEmail = document.getElementById("profileEmail") as HTMLDivElement;
const profileNickEdit = document.getElementById("profileNickEdit") as HTMLButtonElement;
// ─── new v7 dialog elements ────────────────────────────────────────────
const nickCard = document.getElementById("nickCard") as HTMLDivElement;
const nickInput = document.getElementById("nickInput") as HTMLInputElement;
const nickStatus = document.getElementById("nickStatus") as HTMLDivElement;
const nickSave = document.getElementById("nickSave") as HTMLButtonElement;
const nickCancel = document.getElementById("nickCancel") as HTMLButtonElement;
const spSettingsCard = document.getElementById("spSettingsCard") as HTMLDivElement;
const spScene = document.getElementById("spScene") as HTMLSelectElement;
const spNpcRange = document.getElementById("spNpcRange") as HTMLInputElement;
const spNpcValue = document.getElementById("spNpcValue") as HTMLSpanElement;
const spStart = document.getElementById("spStart") as HTMLButtonElement;
const spStory = document.getElementById("spStory") as HTMLButtonElement | null; // story
const spCancel = document.getElementById("spCancel") as HTMLButtonElement;
const menuDeathmatch = document.getElementById("menuDeathmatch") as HTMLButtonElement;
const menuProfile = document.getElementById("menuProfile") as HTMLButtonElement;
const menuGameSettings = document.getElementById("menuGameSettings") as HTMLButtonElement;
const gameSettingsCard = document.getElementById("gameSettingsCard") as HTMLDivElement;
const gsSoundRange = document.getElementById("gsSoundRange") as HTMLInputElement;
const gsSoundValue = document.getElementById("gsSoundValue") as HTMLSpanElement;
const gsMusicRange = document.getElementById("gsMusicRange") as HTMLInputElement;
const gsMusicValue = document.getElementById("gsMusicValue") as HTMLSpanElement;
const gsBrightRange = document.getElementById("gsBrightRange") as HTMLInputElement;
const gsBrightValue = document.getElementById("gsBrightValue") as HTMLSpanElement;
const gsPostProcess = document.getElementById("gsPostProcess") as HTMLInputElement;
const gsBloom = document.getElementById("gsBloom") as HTMLInputElement;
const gsBack = document.getElementById("gsBack") as HTMLButtonElement;
const dmMenuCard = document.getElementById("dmMenuCard") as HTMLDivElement;
const dmCreateOpen = document.getElementById("dmCreateOpen") as HTMLButtonElement;
const dmJoinOpen = document.getElementById("dmJoinOpen") as HTMLButtonElement;
const dmBack = document.getElementById("dmBack") as HTMLButtonElement;
const dmCreateCard = document.getElementById("dmCreateCard") as HTMLDivElement;
const dmScene = document.getElementById("dmScene") as HTMLSelectElement;
const dmMaxPlayers = document.getElementById("dmMaxPlayers") as HTMLInputElement;
const dmMaxPlayersValue = document.getElementById("dmMaxPlayersValue") as HTMLSpanElement;
const dmPrivateToggle = document.getElementById("dmPrivateToggle") as HTMLInputElement;
const dmCreateStatus = document.getElementById("dmCreateStatus") as HTMLDivElement;
const dmCreateBack = document.getElementById("dmCreateBack") as HTMLButtonElement;
const dmCreateSubmit = document.getElementById("dmCreateSubmit") as HTMLButtonElement;
const dmListCard = document.getElementById("dmListCard") as HTMLDivElement;
const dmListRows = document.getElementById("dmListRows") as HTMLDivElement;
const dmListStatus = document.getElementById("dmListStatus") as HTMLDivElement;
const dmListBack = document.getElementById("dmListBack") as HTMLButtonElement;
const dmListRefresh = document.getElementById("dmListRefresh") as HTMLButtonElement;
const dmInviteCard = document.getElementById("dmInviteCard") as HTMLDivElement;
const dmInviteTitle = document.getElementById("dmInviteTitle") as HTMLDivElement;
const dmInviteSubtitle = document.getElementById("dmInviteSubtitle") as HTMLDivElement;
const inviteUrlInput = document.getElementById("inviteUrlInput") as HTMLInputElement;
const inviteCopyBtn = document.getElementById("inviteCopyBtn") as HTMLButtonElement;
const inviteBack = document.getElementById("inviteBack") as HTMLButtonElement;
const inviteStart = document.getElementById("inviteStart") as HTMLButtonElement;
const joinPrivateCard = document.getElementById("joinPrivateCard") as HTMLDivElement;
const joinPrivateInfo = document.getElementById("joinPrivateInfo") as HTMLDivElement;
const joinPrivateOk = document.getElementById("joinPrivateOk") as HTMLButtonElement;
const joinPrivateCancel = document.getElementById("joinPrivateCancel") as HTMLButtonElement;
const profileViewCard = document.getElementById("profileViewCard") as HTMLDivElement;
const profileViewTitle = document.getElementById("profileViewTitle") as HTMLDivElement;
const profileViewBody = document.getElementById("profileViewBody") as HTMLDivElement;
const profileViewBack = document.getElementById("profileViewBack") as HTMLButtonElement;
// ── v22: матч (режимы/раунды), рейтинг, команды ─────────────────────────
const dmMode = document.getElementById("dmMode") as HTMLSelectElement;
const dmRoundTime = document.getElementById("dmRoundTime") as HTMLSelectElement;
const dmRounds = document.getElementById("dmRounds") as HTMLSelectElement;
const matchHudEl = document.getElementById("match-hud") as HTMLDivElement;
const matchTimerEl = document.getElementById("match-timer") as HTMLDivElement;
const matchRoundEl = document.getElementById("match-round") as HTMLDivElement;
const matchTeamsEl = document.getElementById("match-teams") as HTMLDivElement;
const matchTeamAKillsEl = document.getElementById("match-teamA-kills") as HTMLSpanElement;
const matchTeamBKillsEl = document.getElementById("match-teamB-kills") as HTMLSpanElement;
const matchTeamsWinsEl = document.getElementById("match-teams-wins") as HTMLSpanElement;
const matchBannerEl = document.getElementById("match-banner") as HTMLDivElement;
const matchEndEl = document.getElementById("match-end") as HTMLDivElement;
const matchEndTitleEl = document.getElementById("match-end-title") as HTMLHeadingElement;
const matchEndRowsEl = document.getElementById("match-end-rows") as HTMLTableSectionElement;
const matchEndExitBtn = document.getElementById("match-end-exit") as HTMLButtonElement;
const menuLeaderboard = document.getElementById("menuLeaderboard") as HTMLButtonElement;
const menuTeam = document.getElementById("menuTeam") as HTMLButtonElement;
const menuTeamBadge = document.getElementById("menuTeamBadge") as HTMLSpanElement;
const menuTeamSub = document.getElementById("menuTeamSub") as HTMLSpanElement;
const leaderboardCard = document.getElementById("leaderboardCard") as HTMLDivElement;
const leaderboardBody = document.getElementById("leaderboardBody") as HTMLDivElement;
const leaderboardBack = document.getElementById("leaderboardBack") as HTMLButtonElement;
const leaderboardRefresh = document.getElementById("leaderboardRefresh") as HTMLButtonElement;
const teamCard = document.getElementById("teamCard") as HTMLDivElement;
const teamCreateSection = document.getElementById("teamCreateSection") as HTMLDivElement;
const teamInvitesSection = document.getElementById("teamInvitesSection") as HTMLDivElement;
const teamInvitesRows = document.getElementById("teamInvitesRows") as HTMLDivElement;
const teamViewSection = document.getElementById("teamViewSection") as HTMLDivElement;
const teamViewHeader = document.getElementById("teamViewHeader") as HTMLDivElement;
const teamMembersRows = document.getElementById("teamMembersRows") as HTMLDivElement;
const teamNameInput = document.getElementById("teamNameInput") as HTMLInputElement;
const teamTagInput = document.getElementById("teamTagInput") as HTMLInputElement;
const teamCreateBtn = document.getElementById("teamCreateBtn") as HTMLButtonElement;
const teamInviteNick = document.getElementById("teamInviteNick") as HTMLInputElement;
const teamInviteBtn = document.getElementById("teamInviteBtn") as HTMLButtonElement;
const teamLeaveBtn = document.getElementById("teamLeaveBtn") as HTMLButtonElement;
const teamStatus = document.getElementById("teamStatus") as HTMLDivElement;
const teamBack = document.getElementById("teamBack") as HTMLButtonElement;

/** Состояние /api/auth/me — до первого fetch null. */
type AuthInfo =
  | { ok: true; guest: true }
  | { ok: true; guest: false; userId: number; nick: string; displayName: string; email: string; nickRequired: boolean }
  | { ok: false; error?: string };
let authInfo: AuthInfo | null = null;

/**
 * Настройки, с которыми клиент запустит комнату. Наполняется либо из
 * SP-settings modal, либо из DM create/join/ссылки. null = пока не задано.
 */
type RoomSettings = {
  mode: "single" | "dm_join" | "dm_create_public" | "dm_create_private" | "dm_by_token";
  roomId?: string;       // для dm_join / dm_by_token
  sceneId?: string;
  npcCount?: number;
  maxPlayers?: number;
  restart?: boolean;     // SP: close старую и recreate
  story?: boolean;       // story: сюжетный режим «Книга заклинаний» (?mode=story)
};
let pendingRoom: RoomSettings | null = null;
// Токен pending-private-invite из ?room=<token>. Сохраняется в sessionStorage
// до прохождения OAuth-redirect'а (см. setupPrivateInviteOnBoot).
let pendingInviteToken: string | null = null;

function setLoader(visible: boolean, text?: string, pct?: number): void {
  if (visible) {
    loaderEl.classList.remove("hidden");
    mainMenu.style.display = "none";
    loaderErrorRow.classList.add("hidden");
    loaderBar.classList.remove("hidden");
    if (text) loaderText.textContent = text;
    if (pct !== undefined) loaderFill.style.width = `${Math.round(pct * 100)}%`;
    if (text) diagStage(text, pct);
  } else {
    diagStage("лоадер скрыт");
    loaderEl.classList.add("hidden");
    mainMenu.style.display = "";
    loaderErrorRow.classList.add("hidden");
    loaderBar.classList.remove("hidden");
  }
}

/** Ошибка старта: лоадер с текстом и кнопкой «В меню» (не gameplayLive). */
const START_ERR_MODEL =
  "Не удалось загрузить модель солдата (/models/soldier.glb). Проверьте сеть и обновите страницу.";
const START_ERR_WS =
  "Не удалось подключиться к серверу. Убедитесь, что сервер запущен, и попробуйте снова.";
const START_ERR_HANDSHAKE =
  "Сервер не прислал ID игрока (таймаут). Попробуйте снова или обновите страницу.";
const START_ERR_WS_CLOSED =
  "Соединение с сервером разорвано до готовности. Попробуйте снова.";

function showStartFailure(message: string): void {
  diag("start-fail", message);
  loaderEl.classList.remove("hidden");
  mainMenu.style.display = "none";
  loaderText.textContent = t(message);
  loaderFill.style.width = "0%";
  loaderBar.classList.add("hidden");
  loaderErrorRow.classList.remove("hidden");
}

loaderOk.addEventListener("click", () => {
  loaderErrorRow.classList.add("hidden");
  loaderBar.classList.remove("hidden");
  setLoader(false);
});

/** Ждём Hello/Snapshot, пока `myId` не станет ≥ 0 (после WS open). */
async function waitForMyId(timeoutMs: number): Promise<void> {
  const t0 = performance.now();
  while (myId < 0) {
    if (!transport?.isOpen()) throw new Error("WS_CLOSED");
    if (performance.now() - t0 >= timeoutMs) throw new Error("HANDSHAKE_TIMEOUT");
    await new Promise<void>((r) => setTimeout(r, 20));
  }
}

async function fetchAuthInfo(): Promise<AuthInfo> {
  const r = await fetch(`${location.origin}/api/auth/me`, { credentials: "include" });
  if (!r.ok) {
    return { ok: false };
  }
  const j = (await r.json()) as {
    ok: boolean;
    guest?: boolean;
    userId?: number;
    nick?: string;
    displayName?: string;
    email?: string;
    nickRequired?: boolean;
    error?: string;
  };
  if (j.ok && j.guest) {
    return { ok: true, guest: true };
  }
  if (j.ok && j.guest === false && typeof j.userId === "number") {
    return {
      ok: true,
      guest: false,
      userId: j.userId,
      nick: j.nick ?? "",
      displayName: j.displayName ?? "",
      email: j.email ?? "",
      nickRequired: Boolean(j.nickRequired),
    };
  }
  return { ok: false, error: j.error };
}

function canStartFromAuth(m: AuthInfo | null): boolean {
  if (m === null) {
    return false;
  }
  if (!m.ok) {
    return false;
  }
  if (m.guest) {
    return true;
  }
  return m.userId > 0 && !m.nickRequired;
}

/** Установлен ли публичный ник у текущей сессии (только для залогиненных). */
function hasNickSet(m: AuthInfo | null): boolean {
  return m !== null && m.ok && !m.guest && !m.nickRequired && m.nick !== "";
}

/** Текст ошибки от сервера (http.Error, reason): фиксированные русские
 *  фразы есть в словаре, остальное показываем как пришло. */
function srvText(s: string): string {
  return t(s.trim());
}

function showGuestCard(msg: string, warn: boolean): void {
  authProfileCard.classList.add("hidden");
  authGuestCard.classList.remove("hidden");
  setText(authStatus, msg);
  authStatus.classList.remove("warn", "ok");
  if (warn) authStatus.classList.add("warn");
}

function showProfileCard(name: string, email: string): void {
  authGuestCard.classList.add("hidden");
  authProfileCard.classList.remove("hidden");
  profileName.textContent = name;
  // Email скрыт по умолчанию — публичен только ник.
  // Для совместимости с существующей разметкой оставляем строку,
  // но показываем только если email реально есть и владелец смотрит на себя.
  profileEmail.textContent = email;
  profileEmail.style.display = email ? "" : "none";
  const initial = (name || email || "?").trim().charAt(0).toUpperCase() || "?";
  profileAvatar.textContent = initial;
}

function applyAuthToUI(): void {
  if (authInfo === null) {
    authGuestCard.classList.add("hidden");
    authProfileCard.classList.add("hidden");
    mainMenu.classList.add("hidden");
    playBtn.disabled = true;
    menuDeathmatch.disabled = true;
    menuTeam.disabled = true;
    return;
  }
  if (authInfo.ok && !authInfo.guest) {
    const who = authInfo.nick || authInfo.displayName || t("Игрок #{id}", { id: authInfo.userId });
    // Email показываем внутри своей карточки (owner видит свой email).
    showProfileCard(who, authInfo.email || "");
    // Nick dialog обязателен при первом входе: закрывает доступ к меню.
    if (authInfo.nickRequired) {
      mainMenu.classList.add("hidden");
      openNickDialog(/*mandatory*/ true);
    } else {
      mainMenu.classList.remove("hidden");
      playBtn.disabled = false;
      menuDeathmatch.disabled = false;
      menuTeam.disabled = false;
    }
  } else if (authInfo.ok && authInfo.guest) {
    showGuestCard("Гостевой режим. Войдите, чтобы сохранить ник и играть в DM.", false);
    mainMenu.classList.remove("hidden");
    playBtn.disabled = false;
    // DM и команды недоступны гостю.
    menuDeathmatch.disabled = true;
    menuTeam.disabled = true;
  } else {
    showGuestCard("Войдите, чтобы начать игру.", true);
    mainMenu.classList.add("hidden");
    playBtn.disabled = true;
    menuDeathmatch.disabled = true;
    menuTeam.disabled = true;
  }
  // characters: шаг «Персонаж» после входа (до «Начать»), один раз за загрузку.
  onAuthAppliedForCharacters(!mainMenu.classList.contains("hidden"));
  updateAmbientMusic();
}

/** Обновить сессию (после OAuth, выхода, смены гостя). */
async function refreshAuth(): Promise<void> {
  authInfo = await fetchAuthInfo();
  if (authInfo && authInfo.ok && !authInfo.guest) trackAuthOnce("session");
  applyAuthToUI();
  // Бейдж приглашений в команду (не блокируем основной поток аутентификации).
  void refreshTeamBadge();
  // После каждого refresh проверяем pending-invite:
  //  - авторизован + ник выбран → сразу Join-card;
  //  - не авторизован (или гость) → приглашение на карточке логина:
  //    «создайте аккаунт, чтобы присоединиться». Токен переживает
  //    OAuth-redirect в sessionStorage, поэтому после логина (и, при
  //    необходимости, выбора ника) refreshAuth() дойдёт до Join-card сам.
  //  - авторизован, но без ника → ничего: applyAuthToUI уже открыл
  //    обязательный nick-диалог; после сохранения ника снова попадём сюда.
  if (pendingInviteToken) {
    if (hasNickSet(authInfo)) {
      await showJoinPrivateCard(pendingInviteToken);
    } else if (!authInfo.ok || authInfo.guest) {
      await showInviteLoginPrompt(pendingInviteToken);
    }
  }
}

/**
 * Неавторизованный (или гость) открыл invite-ссылку: показываем на карточке
 * логина, куда его зовут, и просим войти/создать аккаунт. Preview комнаты
 * доступен без сессии (/api/rooms/by-token отдаёт его по токену).
 */
async function showInviteLoginPrompt(token: string): Promise<void> {
  let details = "";
  let roomGone = false;
  try {
    const resp = await fetch(
      `${location.origin}/api/rooms/by-token?token=${encodeURIComponent(token)}`,
      { credentials: "include" });
    if (resp.ok) {
      const j = await resp.json() as { sceneId: string; players: number; maxPlayers: number; ownerNick: string };
      details = (j.ownerNick ? t(" игрока {nick}", { nick: j.ownerNick }) : "") +
        ` (${j.sceneId}, ${j.players}/${j.maxPlayers})`;
    } else if (resp.status === 404) {
      roomGone = true;
    }
  } catch { /* сеть — покажем общий текст без деталей */ }
  if (roomGone) {
    // Комната уже закрылась — не тащим протухший токен через логин.
    sessionStorage.removeItem("tipaduma.pendingInviteToken");
    pendingInviteToken = null;
    setText(authStatus,
      "Ссылка на комнату устарела: комната уже закрыта. " +
      "Войдите — и создайте свою или найдите комнату в списке публичных.");
  } else {
    authStatus.textContent =
      t("Вас пригласили в Deathmatch-комнату{details}. ", { details }) +
      t("Войдите или создайте аккаунт, чтобы присоединиться.");
  }
  authStatus.classList.remove("warn", "ok");
  authGuestCard.classList.remove("hidden");
}

void (async () => {
  // Поднимаем pending-invite токен из URL / sessionStorage ДО refreshAuth,
  // чтобы post-auth poll увидел его.
  setupPrivateInviteOnBoot();
  await refreshAuth();
})();

// ─── UI helpers для новых карточек ──────────────────────────────────────

/** Список всех "кластеров" UI — один виден за раз. */
type UICard =
  | "menu"
  | "nick"
  | "spSettings"
  | "gameSettings"
  | "dmMenu"
  | "dmCreate"
  | "dmList"
  | "dmInvite"
  | "joinPrivate"
  | "profileView"
  | "leaderboard"
  | "team";

function showCard(card: UICard | null): void {
  if (card !== null) hideCharacterStep(); // characters: карточка «Персонаж» живёт вне списка
  const cards: Array<[UICard, HTMLElement]> = [
    ["menu", mainMenu],
    ["nick", nickCard],
    ["spSettings", spSettingsCard],
    ["gameSettings", gameSettingsCard],
    ["dmMenu", dmMenuCard],
    ["dmCreate", dmCreateCard],
    ["dmList", dmListCard],
    ["dmInvite", dmInviteCard],
    ["joinPrivate", joinPrivateCard],
    ["profileView", profileViewCard],
    ["leaderboard", leaderboardCard],
    ["team", teamCard],
  ];
  for (const [id, el] of cards) {
    if (id === card) el.classList.remove("hidden");
    else el.classList.add("hidden");
  }
}

// characters: шаг «Персонаж» (карусель + апгрейды) — client/src/characters/menu.ts.
initCharacterMenu({
  hideOtherCards: () => showCard(null),
  showMainMenu: () => showCard("menu"),
  inGame: () => gameplayLive && !!transport?.isOpen(),
});

// ─── Nick dialog ────────────────────────────────────────────────────────
/** Открыть nick-dialog. mandatory = true: кнопка Cancel отключена (первый вход). */
function openNickDialog(mandatory: boolean): void {
  showCard("nick");
  nickInput.value = authInfo && authInfo.ok && !authInfo.guest ? authInfo.nick : "";
  nickStatus.textContent = "";
  nickStatus.classList.remove("warn", "ok");
  nickSave.disabled = true;
  nickCancel.style.visibility = mandatory ? "hidden" : "";
  setTimeout(() => nickInput.focus(), 50);
}

function closeNickDialog(): void {
  showCard("menu");
}

let nickCheckTimer: number | null = null;
nickInput.addEventListener("input", () => {
  if (nickCheckTimer !== null) clearTimeout(nickCheckTimer);
  const val = nickInput.value.trim();
  nickStatus.classList.remove("warn", "ok");
  if (val.length < 3 || val.length > 16 || !/^[A-Za-z0-9_\-.]+$/.test(val)) {
    nickStatus.textContent = t("3..16 символов, латиница / цифры / _-.");
    nickStatus.classList.add("warn");
    nickSave.disabled = true;
    return;
  }
  nickStatus.textContent = t("Проверяем…");
  nickSave.disabled = true;
  nickCheckTimer = window.setTimeout(async () => {
    nickCheckTimer = null;
    try {
      const resp = await fetch(
        `${location.origin}/api/auth/nick/available?nick=${encodeURIComponent(val)}`,
        { credentials: "include" });
      if (!resp.ok) {
        nickStatus.textContent = t("Ошибка проверки");
        nickStatus.classList.add("warn");
        return;
      }
      const j = await resp.json() as { available: boolean; reason?: string };
      if (j.available) {
        nickStatus.textContent = t("Свободен");
        nickStatus.classList.add("ok");
        nickSave.disabled = false;
      } else {
        nickStatus.textContent = srvText(j.reason || "уже занят");
        nickStatus.classList.add("warn");
      }
    } catch {
      nickStatus.textContent = t("Ошибка сети");
      nickStatus.classList.add("warn");
    }
  }, 300);
});

nickSave.addEventListener("click", async () => {
  const val = nickInput.value.trim();
  nickSave.disabled = true;
  try {
    const resp = await fetch(`${location.origin}/api/auth/nick`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nick: val }),
    });
    if (!resp.ok) {
      const txt = await resp.text();
      nickStatus.textContent = srvText(txt) || t("Ошибка");
      nickStatus.classList.add("warn");
      nickSave.disabled = false;
      return;
    }
    await refreshAuth();
    // Если ждём invite и ник теперь есть — refreshAuth уже открыл
    // Join-card; не перекрываем её главным меню.
    if (!(pendingInviteToken && hasNickSet(authInfo))) {
      showCard("menu");
    }
  } catch {
    nickStatus.textContent = t("Ошибка сети");
    nickStatus.classList.add("warn");
    nickSave.disabled = false;
  }
});

nickCancel.addEventListener("click", () => closeNickDialog());
profileNickEdit.addEventListener("click", () => openNickDialog(false));

// ─── SP settings ────────────────────────────────────────────────────────
spNpcRange.addEventListener("input", () => {
  spNpcValue.textContent = spNpcRange.value;
});
spCancel.addEventListener("click", () => showCard("menu"));
spStart.addEventListener("click", async () => {
  hideAddToHomeScreenTip();
  // Клик — настоящий жест, только из него браузер разрешает fullscreen.
  if (touchUi && canRequestFullscreen() && !isStandalone()) void enterFullscreen();
  pendingRoom = {
    mode: "single",
    sceneId: spScene.value,
    npcCount: parseInt(spNpcRange.value, 10) || 0,
    // «Начать» = всегда СВЕЖАЯ сцена (RecreateSolo). Иначе залогиненный
    // игрок возвращался в свою старую SP-комнату с прошлыми разрушениями
    // (EnsureSolo переиспользует room). Для «продолжить» есть отдельная
    // кнопка «Вернуться в игру».
    restart: true,
  };
  showCard(null);
  await start();
});

// story: «Книга заклинаний» — своя сцена (особняк), без солдат, всегда с
// начала. Кнопка недоступна, пока идёт загрузка (повторный клик не стартует
// второй забег); снимаем блокировку в finally.
spStory?.addEventListener("click", async () => {
  if (spStory.disabled) return;
  spStory.disabled = true;
  try {
    hideAddToHomeScreenTip();
    if (touchUi && canRequestFullscreen() && !isStandalone()) void enterFullscreen();
    pendingRoom = { mode: "single", sceneId: STORY_SCENE_ID, npcCount: 0, restart: true, story: true };
    showCard(null);
    await start();
  } finally {
    spStory.disabled = false;
  }
});

// ─── DM menu ────────────────────────────────────────────────────────────
menuDeathmatch.addEventListener("click", () => {
  if (!hasNickSet(authInfo)) {
    openNickDialog(true);
    return;
  }
  showCard("dmMenu");
});
dmBack.addEventListener("click", () => showCard("menu"));

// SP triggers settings modal (раньше сразу start()).
playBtn.addEventListener("click", () => {
  showCard("spSettings");
});

// Profile menu button
menuProfile.addEventListener("click", () => {
  if (!hasNickSet(authInfo)) { openNickDialog(true); return; }
  showProfileView((authInfo as Extract<AuthInfo, { guest: false }>).nick);
});

menuGameSettings.addEventListener("click", () => {
  syncGameSettingsSliders();
  showCard("gameSettings");
});
gsBack.addEventListener("click", () => showCard("menu"));

// ─── DM create ──────────────────────────────────────────────────────────
dmCreateOpen.addEventListener("click", () => {
  dmCreateStatus.textContent = "";
  dmCreateStatus.classList.remove("warn", "ok");
  showCard("dmCreate");
});
dmCreateBack.addEventListener("click", () => showCard("dmMenu"));
dmMaxPlayers.addEventListener("input", () => {
  dmMaxPlayersValue.textContent = dmMaxPlayers.value;
});
dmCreateSubmit.addEventListener("click", async () => {
  dmCreateStatus.textContent = t("Создаём…");
  dmCreateStatus.classList.remove("warn", "ok");
  dmCreateSubmit.disabled = true;
  try {
    const kind = dmPrivateToggle.checked ? "dm_private" : "dm_public";
    const resp = await fetch(`${location.origin}/api/rooms`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind,
        sceneId: dmScene.value,
        maxPlayers: parseInt(dmMaxPlayers.value, 10),
        mode: dmMode.value,
        roundSeconds: parseInt(dmRoundTime.value, 10) || 600,
        rounds: parseInt(dmRounds.value, 10) || 3,
      }),
    });
    if (!resp.ok) {
      const txt = await resp.text();
      dmCreateStatus.textContent = srvText(txt) || `HTTP ${resp.status}`;
      dmCreateStatus.classList.add("warn");
      dmCreateSubmit.disabled = false;
      return;
    }
    const j = await resp.json() as {
      id: string; kind: string; sceneId: string; maxPlayers: number;
      joinToken?: string; inviteUrl?: string;
    };
    if (j.inviteUrl) {
      // Экран со ссылкой показываем для ОБОИХ типов комнат: публичную
      // тоже можно шерить по ссылке (плюс она видна в списке публичных).
      inviteUrlInput.value = j.inviteUrl;
      (inviteStart as HTMLButtonElement).dataset.roomId = j.id;
      // sceneId обязателен для pendingRoom: без него start() не загрузит
      // terrain-heightmap ДО спавна, и предикшн роняет игрока под террейн.
      (inviteStart as HTMLButtonElement).dataset.sceneId = j.sceneId;
      if (kind === "dm_private") {
        dmInviteTitle.textContent = t("Приватная комната создана");
        dmInviteSubtitle.textContent =
          t("Отправьте ссылку друзьям. Попасть в комнату можно только по ней.");
      } else {
        dmInviteTitle.textContent = t("Публичная комната создана");
        dmInviteSubtitle.textContent = t(
          "Отправьте ссылку друзьям — или они найдут комнату в списке публичных. " +
          "Если у друга нет аккаунта, ссылка сначала предложит войти.");
      }
      showCard("dmInvite");
    } else {
      // Fallback (старый сервер без inviteUrl для public): сразу в игру.
      pendingRoom = {
        mode: "dm_join",
        roomId: j.id,
        sceneId: j.sceneId,
        maxPlayers: j.maxPlayers,
      };
      showCard(null);
      await start();
    }
  } catch (e) {
    dmCreateStatus.textContent = String(e);
    dmCreateStatus.classList.add("warn");
  } finally {
    dmCreateSubmit.disabled = false;
  }
});

// ─── DM invite (private) ────────────────────────────────────────────────
inviteCopyBtn.addEventListener("click", async () => {
  try { await navigator.clipboard.writeText(inviteUrlInput.value); } catch { /* ignore */ }
  inviteCopyBtn.textContent = t("Скопировано");
  setTimeout(() => { inviteCopyBtn.textContent = t("Копировать"); }, 1500);
});
inviteBack.addEventListener("click", () => showCard("dmMenu"));
inviteStart.addEventListener("click", async () => {
  const btn = inviteStart as HTMLButtonElement;
  const id = btn.dataset.roomId;
  if (!id) return;
  // sceneId критичен: без него terrain-heightmap не загрузится до спавна
  // и игрок «проваливается под землю» (предикшн без пола под террейном).
  pendingRoom = { mode: "dm_join", roomId: id, sceneId: btn.dataset.sceneId };
  showCard(null);
  await start();
});

// ─── DM public list ─────────────────────────────────────────────────────
let dmListPollTimer: number | null = null;

dmJoinOpen.addEventListener("click", async () => {
  showCard("dmList");
  await refreshDmList();
  if (dmListPollTimer !== null) clearInterval(dmListPollTimer);
  dmListPollTimer = window.setInterval(refreshDmList, 3000);
});
dmListBack.addEventListener("click", () => {
  if (dmListPollTimer !== null) { clearInterval(dmListPollTimer); dmListPollTimer = null; }
  showCard("dmMenu");
});
dmListRefresh.addEventListener("click", () => { void refreshDmList(); });

async function refreshDmList(): Promise<void> {
  try {
    const resp = await fetch(`${location.origin}/api/rooms`, { credentials: "include" });
    if (!resp.ok) {
      dmListStatus.textContent = `HTTP ${resp.status}`;
      return;
    }
    const j = await resp.json() as { public: Array<{ id: string; sceneId: string; players: number; maxPlayers: number; ownerNick: string; mode?: string; rounds?: number; roundSeconds?: number }> };
    dmListRows.innerHTML = "";
    if (!j.public.length) {
      dmListStatus.textContent = t("Публичных комнат с местами пока нет.");
      return;
    }
    dmListStatus.textContent = t("{n} комнат(ы)", { n: j.public.length });
    for (const r of j.public) {
      const row = document.createElement("div");
      row.className = "roomRow";
      const info = document.createElement("div");
      info.className = "roomRow__info";
      const name = document.createElement("div");
      name.className = "name";
      name.textContent = t("Комната {name}", { name: r.ownerNick || r.id.slice(0, 6) });
      const meta = document.createElement("div");
      meta.className = "meta";
      const modeTxt = r.mode === "tdm" ? "TDM" : "FFA";
      const roundsTxt = r.rounds ? ` · ${r.rounds}×${t("{m}мин", { m: Math.round((r.roundSeconds || 600) / 60) })}` : "";
      meta.textContent = `${r.sceneId} · ${modeTxt}${roundsTxt} · ${r.players}/${r.maxPlayers}`;
      info.appendChild(name); info.appendChild(meta);
      const btn = document.createElement("button");
      btn.textContent = t("Войти");
      btn.addEventListener("click", async () => {
        if (dmListPollTimer !== null) { clearInterval(dmListPollTimer); dmListPollTimer = null; }
        pendingRoom = { mode: "dm_join", roomId: r.id, sceneId: r.sceneId, maxPlayers: r.maxPlayers };
        showCard(null);
        await start();
      });
      row.appendChild(info); row.appendChild(btn);
      dmListRows.appendChild(row);
    }
  } catch (e) {
    dmListStatus.textContent = String(e);
  }
}

// ─── Private invite link (?room=<token>) ────────────────────────────────
function setupPrivateInviteOnBoot(): void {
  const params = new URLSearchParams(location.search);
  const tok = params.get("room");
  if (tok) {
    sessionStorage.setItem("tipaduma.pendingInviteToken", tok);
    // Сохраняем в sessionStorage и очищаем query, чтобы токен не светился
    // в истории браузера после показа Join-card.
    const url = new URL(location.href);
    url.searchParams.delete("room");
    history.replaceState({}, "", url.toString());
  }
  pendingInviteToken = sessionStorage.getItem("tipaduma.pendingInviteToken");
}

async function showJoinPrivateCard(token: string): Promise<void> {
  showCard("joinPrivate");
  joinPrivateInfo.textContent = t("Загружаем информацию о комнате…");
  joinPrivateOk.disabled = true;
  try {
    const resp = await fetch(
      `${location.origin}/api/rooms/by-token?token=${encodeURIComponent(token)}`,
      { credentials: "include" });
    if (!resp.ok) {
      joinPrivateInfo.textContent = t("Не удалось найти комнату по этой ссылке.");
      return;
    }
    const j = await resp.json() as { id: string; sceneId: string; players: number; maxPlayers: number; ownerNick: string };
    joinPrivateInfo.textContent =
      t("Сцена: {scene}, игроков: {n}/{max}", { scene: j.sceneId, n: j.players, max: j.maxPlayers }) +
      (j.ownerNick ? t(", хост: {nick}", { nick: j.ownerNick }) : "");
    joinPrivateOk.disabled = j.players >= j.maxPlayers;
    (joinPrivateOk as HTMLButtonElement).dataset.token = token;
    (joinPrivateOk as HTMLButtonElement).dataset.sceneId = j.sceneId;
  } catch {
    joinPrivateInfo.textContent = t("Ошибка сети при загрузке комнаты.");
  }
}

joinPrivateOk.addEventListener("click", async () => {
  const btn = joinPrivateOk as HTMLButtonElement;
  const tok = btn.dataset.token || "";
  sessionStorage.removeItem("tipaduma.pendingInviteToken");
  pendingInviteToken = null;
  // sceneId — чтобы terrain-heightmap загрузился до спавна (см. inviteStart).
  pendingRoom = { mode: "dm_by_token", roomId: tok, sceneId: btn.dataset.sceneId };
  showCard(null);
  await start();
});
joinPrivateCancel.addEventListener("click", () => {
  sessionStorage.removeItem("tipaduma.pendingInviteToken");
  pendingInviteToken = null;
  showCard("menu");
});

// ─── Profile view ───────────────────────────────────────────────────────
async function showProfileView(nick: string): Promise<void> {
  showCard("profileView");
  profileViewTitle.textContent = nick;
  profileViewBody.innerHTML = t("Загружаем…");
  try {
    const resp = await fetch(`${location.origin}/api/profile/${encodeURIComponent(nick)}`);
    if (!resp.ok) {
      profileViewBody.textContent = t("Не удалось загрузить профиль ({status}).", { status: resp.status });
      return;
    }
    const j = await resp.json() as {
      nick: string;
      stats: { kills: number; deaths: number; hits: number; matchesPlayed: number; lastMatchAt: number; rating?: number; wins?: number };
      teamName?: string;
      teamTag?: string;
      matches: Array<{ id: number; roomKind: string; mode?: string; sceneId: string; startedAt: number; endedAt: number; players: Array<{ nickSnapshot: string; kills: number; deaths: number; hits: number }> }>;
    };
    const s = j.stats;
    const kd = s.deaths > 0 ? (s.kills / s.deaths).toFixed(2) : "—";
    const teamLine = j.teamTag
      ? `<div style="color:#7a7; margin-bottom:6px;">${t("Команда:")} <b>[${j.teamTag}] ${j.teamName ?? ""}</b></div>` : "";
    const lines = [
      `<div style="color:#fc3; font-size:16px; margin-bottom:4px;">${t("Рейтинг:")} <b>${Math.round(s.rating ?? 1000)}</b>` +
        ` · ${t("Побед:")} <b>${s.wins ?? 0}</b></div>`,
      teamLine,
      `<div style="color:#ddd; font-size:14px; margin-bottom:8px;">${t("Матчей:")} ${s.matchesPlayed}</div>`,
      `<div>${t("Фраги:")} <b>${s.kills}</b> · ${t("Смерти:")} <b>${s.deaths}</b> · K/D: <b>${kd}</b> · ${t("Попаданий:")} <b>${s.hits}</b></div>`,
    ];
    if (!j.matches || j.matches.length === 0) {
      lines.push(`<div style="color:#888; margin-top:8px;">${t("Матчей пока нет.")}</div>`);
    } else {
      lines.push(`<div style="color:#888; margin-top:12px;">${t("Последние матчи:")}</div>`);
      for (const m of j.matches) {
        const when = new Date((m.endedAt || m.startedAt) * 1000).toLocaleString();
        const me = (m.players || []).find((p) => p.nickSnapshot.toLowerCase() === nick.toLowerCase());
        lines.push(`<div class="roomRow" style="margin-top:6px;">
          <div class="roomRow__info">
            <div class="name">${m.roomKind} · ${m.sceneId}</div>
            <div class="meta">${when} · ${me ? `K/D/H = ${me.kills}/${me.deaths}/${me.hits}` : ""}</div>
          </div>
        </div>`);
      }
    }
    profileViewBody.innerHTML = lines.join("");
  } catch {
    profileViewBody.textContent = t("Ошибка сети.");
  }
}
profileViewBack.addEventListener("click", () => showCard("menu"));

// ─── Рейтинг (leaderboard) ──────────────────────────────────────────────
async function showLeaderboard(): Promise<void> {
  showCard("leaderboard");
  leaderboardBody.innerHTML = t("Загружаем…");
  try {
    const resp = await fetch(`${location.origin}/api/leaderboard?limit=100`, { credentials: "include" });
    if (!resp.ok) {
      leaderboardBody.textContent = t("Не удалось загрузить рейтинг ({status}).", { status: resp.status });
      return;
    }
    const j = await resp.json() as {
      players: Array<{ nick: string; rating: number; kills: number; deaths: number; wins: number; matchesPlayed: number; teamTag?: string }>;
    };
    if (!j.players.length) {
      leaderboardBody.innerHTML =
        `<div style="color:#888; text-align:center">${t("Пока пусто — сыграйте матч в Deathmatch, и рейтинг появится.")}</div>`;
      return;
    }
    const myNick = authInfo && authInfo.ok && !authInfo.guest ? authInfo.nick.toLowerCase() : "";
    const rows = j.players.map((p, i) => {
      const kd = p.deaths > 0 ? (p.kills / p.deaths).toFixed(2) : String(p.kills);
      const tag = p.teamTag ? `<span class="tag">[${escapeHtml(p.teamTag)}]</span> ` : "";
      const me = myNick && p.nick.toLowerCase() === myNick ? " class=\"me\"" : "";
      return `<tr${me}><td>${i + 1}</td><td>${tag}${escapeHtml(p.nick)}</td>` +
        `<td>${Math.round(p.rating)}</td><td>${kd}</td><td>${p.wins}</td><td>${p.matchesPlayed}</td></tr>`;
    }).join("");
    leaderboardBody.innerHTML =
      `<table class="lbTable"><thead><tr>` +
      `<th>#</th><th>${t("Игрок")}</th><th>${t("Рейтинг")}</th><th>K/D</th><th>${t("Победы")}</th><th>${t("Матчи")}</th>` +
      `</tr></thead><tbody>${rows}</tbody></table>`;
  } catch {
    leaderboardBody.textContent = t("Ошибка сети.");
  }
}
menuLeaderboard.addEventListener("click", () => { void showLeaderboard(); });
leaderboardBack.addEventListener("click", () => showCard("menu"));
leaderboardRefresh.addEventListener("click", () => { void showLeaderboard(); });

// ─── Команда (клан) ─────────────────────────────────────────────────────
type TeamMemberT = { userId: number; nick: string; role: string; joinedAt: number; rating: number };
type TeamT = { id: number; name: string; tag: string; ownerUserId: number; createdAt: number };

function setTeamStatus(msg: string, warn: boolean): void {
  teamStatus.textContent = msg;
  teamStatus.classList.remove("warn", "ok");
  if (msg) teamStatus.classList.add(warn ? "warn" : "ok");
}

/** Бейдж «(N)» на кнопке меню — входящие приглашения. */
async function refreshTeamBadge(): Promise<void> {
  menuTeamBadge.textContent = "";
  if (!hasNickSet(authInfo)) return;
  try {
    const resp = await fetch(`${location.origin}/api/team/my`, { credentials: "include" });
    if (!resp.ok) return;
    const j = await resp.json() as { team: TeamT | null; invitesIncoming: number };
    if (j.team) {
      menuTeamSub.textContent = `[${j.team.tag}] ${j.team.name}`;
    } else {
      setText(menuTeamSub, "клан и приглашения");
    }
    if (j.invitesIncoming > 0) menuTeamBadge.textContent = `(+${j.invitesIncoming})`;
  } catch { /* сеть — бейдж не критичен */ }
}

async function teamPost(path: string, body: unknown): Promise<{ ok: boolean; text: string }> {
  try {
    const resp = await fetch(`${location.origin}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
    const text = await resp.text();
    return { ok: resp.ok, text };
  } catch (e) {
    return { ok: false, text: String(e) };
  }
}

async function showTeamCard(): Promise<void> {
  showCard("team");
  setTeamStatus("", false);
  teamCreateSection.classList.add("hidden");
  teamViewSection.classList.add("hidden");
  teamInvitesSection.classList.add("hidden");
  teamMembersRows.innerHTML = "";
  teamInvitesRows.innerHTML = "";
  try {
    const [myResp, invResp] = await Promise.all([
      fetch(`${location.origin}/api/team/my`, { credentials: "include" }),
      fetch(`${location.origin}/api/team/invites`, { credentials: "include" }),
    ]);
    if (!myResp.ok) {
      setTeamStatus(t("Не удалось загрузить команду ({status}).", { status: myResp.status }), true);
      return;
    }
    const my = await myResp.json() as { team: TeamT | null; members?: TeamMemberT[]; myRole?: string };
    const inv = invResp.ok
      ? (await invResp.json() as { invites: Array<{ id: number; teamName: string; teamTag: string; inviterNick: string }> })
      : { invites: [] };

    if (my.team) {
      // Состою в команде: состав + приглашение по нику.
      teamViewSection.classList.remove("hidden");
      const tm = my.team;
      teamViewHeader.innerHTML =
        `<span style="color:#ddd; font-size:15px; font-weight:700">[${escapeHtml(tm.tag)}] ${escapeHtml(tm.name)}</span>` +
        `<br><span style="color:#888">${t("участников: {n} из 16", { n: my.members?.length ?? 0 })}</span>`;
      const isOwner = my.myRole === "owner";
      teamLeaveBtn.textContent = isOwner && (my.members?.length ?? 0) === 1
        ? t("Распустить команду") : t("Покинуть команду");
      for (const m of my.members ?? []) {
        const row = document.createElement("div");
        row.className = "teamMemberRow";
        const nick = document.createElement("div");
        nick.className = "nick";
        nick.textContent = m.nick;
        const role = document.createElement("div");
        role.className = "role";
        role.textContent = m.role === "owner" ? t("★ владелец") : "";
        const rating = document.createElement("div");
        rating.className = "rating";
        rating.textContent = `${Math.round(m.rating)}`;
        row.appendChild(nick);
        row.appendChild(role);
        row.appendChild(rating);
        const myUserId = authInfo && authInfo.ok && !authInfo.guest ? authInfo.userId : 0;
        if (isOwner && m.userId !== myUserId) {
          const kick = document.createElement("button");
          kick.textContent = t("Кик");
          kick.addEventListener("click", async () => {
            const r = await teamPost("/api/team/kick", { userId: m.userId });
            setTeamStatus(r.ok ? t("{nick} исключён", { nick: m.nick }) : srvText(r.text), !r.ok);
            if (r.ok) await showTeamCard();
          });
          row.appendChild(kick);
        }
        teamMembersRows.appendChild(row);
      }
    } else {
      // Не в команде: форма создания.
      teamCreateSection.classList.remove("hidden");
    }

    // Входящие приглашения (видны и когда в команде — вдруг решит сменить).
    if (inv.invites.length > 0) {
      teamInvitesSection.classList.remove("hidden");
      for (const i of inv.invites) {
        const row = document.createElement("div");
        row.className = "teamMemberRow";
        const info = document.createElement("div");
        info.className = "nick";
        info.innerHTML = `<b>[${escapeHtml(i.teamTag)}] ${escapeHtml(i.teamName)}</b>` +
          `<br><span style="color:#888; font-size:11px">${t("зовёт {nick}", { nick: escapeHtml(i.inviterNick) })}</span>`;
        const actions = document.createElement("div");
        actions.className = "inviteActions";
        const acc = document.createElement("button");
        acc.className = "acc";
        acc.textContent = t("Принять");
        acc.addEventListener("click", async () => {
          const r = await teamPost("/api/team/invite/accept", { id: i.id });
          setTeamStatus(r.ok ? t("Вы в команде!") : srvText(r.text), !r.ok);
          await showTeamCard();
          void refreshTeamBadge();
        });
        const dec = document.createElement("button");
        dec.className = "dec";
        dec.textContent = t("Отклонить");
        dec.addEventListener("click", async () => {
          await teamPost("/api/team/invite/decline", { id: i.id });
          await showTeamCard();
          void refreshTeamBadge();
        });
        actions.appendChild(acc);
        actions.appendChild(dec);
        row.appendChild(info);
        row.appendChild(actions);
        teamInvitesRows.appendChild(row);
      }
    }
  } catch {
    setTeamStatus(t("Ошибка сети."), true);
  }
}

menuTeam.addEventListener("click", () => {
  if (!hasNickSet(authInfo)) { openNickDialog(true); return; }
  void showTeamCard();
});
teamBack.addEventListener("click", () => showCard("menu"));

teamCreateBtn.addEventListener("click", async () => {
  const name = teamNameInput.value.trim();
  const tag = teamTagInput.value.trim().toUpperCase();
  if (name.length < 3 || name.length > 24) {
    setTeamStatus(t("Название: 3-24 символа."), true);
    return;
  }
  if (!/^[A-Za-z0-9]{2,5}$/.test(tag)) {
    setTeamStatus(t("Тег: 2-5 символов, латиница/цифры."), true);
    return;
  }
  teamCreateBtn.disabled = true;
  const r = await teamPost("/api/team/create", { name, tag });
  teamCreateBtn.disabled = false;
  setTeamStatus(r.ok ? t("Команда создана!") : srvText(r.text), !r.ok);
  if (r.ok) {
    await showTeamCard();
    void refreshTeamBadge();
  }
});

teamInviteBtn.addEventListener("click", async () => {
  const nick = teamInviteNick.value.trim();
  if (!nick) {
    setTeamStatus(t("Введите ник игрока."), true);
    return;
  }
  teamInviteBtn.disabled = true;
  const r = await teamPost("/api/team/invite", { nick });
  teamInviteBtn.disabled = false;
  setTeamStatus(r.ok ? t("Приглашение отправлено: {nick}", { nick }) : srvText(r.text), !r.ok);
  if (r.ok) teamInviteNick.value = "";
});

teamLeaveBtn.addEventListener("click", async () => {
  if (!confirm(t("Точно покинуть команду?"))) return;
  const r = await teamPost("/api/team/leave", {});
  setTeamStatus(r.ok ? t("Вы вышли из команды.") : srvText(r.text), !r.ok);
  await showTeamCard();
  void refreshTeamBadge();
});

let map: GridMap = buildArena();

// Лампы под потолком в открытых местах арены. Цвет — тёплый "лампа накаливания".
// Высота 2.7м (ниже потолка 3м). Range 6м — каждая накрывает свой "район".
const WARM = (i: number): [number, number, number] => [1.0, 0.85 - i * 0.05, 0.55];
const lamps: PointLight[] = [
  // Вдоль коридора по строке 6 (passable) и строке 9
  { x: 4.0,  y: 6.5, z: 2.7, range: 6, color: WARM(0) },
  { x: 12.0, y: 6.5, z: 2.7, range: 6, color: WARM(1) },
  { x: 4.0,  y: 9.5, z: 2.7, range: 6, color: WARM(2) },
  { x: 12.0, y: 9.5, z: 2.7, range: 6, color: WARM(3) },
  // Поперечные коридоры строки 1 и 14
  { x: 8.5,  y: 1.5,  z: 2.7, range: 6, color: WARM(0) },
  { x: 8.5,  y: 14.5, z: 2.7, range: 6, color: WARM(1) },
  // Декоративная подсветка центра арены — голубой акцент
  { x: 8.0,  y: 5.0,  z: 2.5, range: 4, color: [0.4, 0.7, 1.0] },
  // Фиолетовая подсветка возле колонны
  { x: 11.5, y: 7.5,  z: 2.5, range: 3.5, color: [0.7, 0.3, 1.0] },
];

/** Лампы из level.json (Hello v17); пусто = использовать `lamps` арены. */
const sceneStaticLights: PointLight[] = [];

const renderer = new Renderer(canvas);

/** «Яркость» из настроек (1 = по умолчанию), хранится в этом браузере. */
const LS_BRIGHTNESS = "tipaduma.brightness";
let brightness01 = 1;
try {
  const v = parseFloat(localStorage.getItem(LS_BRIGHTNESS) ?? "");
  if (!Number.isNaN(v)) brightness01 = Math.max(0.5, Math.min(2, v));
} catch { /* ignore */ }
renderer.setBrightness(brightness01);
renderer.uploadMap(map);
// ─── PerfGovernor: авто-тиры качества по фактическому frame time ─────────────
// 0 = full; 1 = без dust-пассов и lux (главный вершинный бюджет GPU);
// 2 = mobile-lite (лайт-частицы, без видео-оружия); 3 = lite + 280p.
// Тир запоминается в localStorage — повторный запуск сразу стартует с
// рабочего профиля. Отключить авто: localStorage tipaduma.autoGfx="0";
// форс: ?gfx=high (full) / ?gfx=lite. Пиксель-арт-вайб (360p + posterize)
// остаётся во всех тирах — kill-switch прежний (tipaduma.pixelArt="0").
const GFX_TIER_MAX = 3;
let gfxTier = 0;
let gfxGovernorEnabled = true;
{
  const q = new URLSearchParams(location.search);
  const g = q.get("gfx");
  let saved = NaN;
  try { saved = parseInt(localStorage.getItem("tipaduma.gfxTier") ?? "", 10); } catch { /* */ }
  if (g === "high" || g === "desktop") { gfxTier = 0; gfxGovernorEnabled = false; }
  else if (g === "lite") { gfxTier = 2; gfxGovernorEnabled = false; }
  else if (Number.isFinite(saved)) gfxTier = Math.min(Math.max(saved, 0), GFX_TIER_MAX);
  // «Дом медленный» не должен забываться: если в прошлой сессии гувернор
  // спускался до N, стартуем сразу с N — иначе каждый заход начинается с
  // 15-40 с жёстких тормозов у дома, пока тир доезжает.
  try {
    const floor = parseInt(localStorage.getItem("tipaduma.gfxTierFloor") ?? "", 10);
    if (Number.isFinite(floor) && gfxGovernorEnabled) {
      gfxTier = Math.min(Math.max(gfxTier, Math.min(floor, GFX_TIER_MAX)), GFX_TIER_MAX);
    }
  } catch { /* */ }
  try { if (localStorage.getItem("tipaduma.autoGfx") === "0") gfxGovernorEnabled = false; } catch { /* */ }
}

function applyGfxTier(t: number): void {
  gfxTier = Math.min(Math.max(t, 0), GFX_TIER_MAX);
  const lite = mobileLiteBase || gfxTier >= 2;
  mobileLiteGraphics = lite;
  // «Точечный стиль» (пыль на стенах + растворение обломков) — только tier 0.
  renderer.setDustStyle(gfxTier < 1 && !lite);
  renderer.setLuxEffectsEnabled(gfxTier < 1 && !lite);
  renderer.setMobileLiteParticles(lite);
  // POM — 8 итераций raymarch'а с зависимыми выборками текстуры на пиксель.
  // Раньше гувернор его НЕ трогал ни на одном тире, хотя мобильный профиль
  // глушит первым делом. Именно этим Mac Safari отличался от айфона, где
  // тормозов нет (см. mobileLiteBase-блок ниже).
  renderer.setPomLayers(gfxTier >= 1 || lite ? 0 : userPomLayers);
  // Дистанционный LOD света: на tier>=1 дальше 12 м считаем 4 лампы вместо 8
  // (mesh.frag + voxel.frag читают один и тот же uLightLodDist).
  renderer.setLightLodDistance(gfxTier >= 1 || lite ? 12 : 26);
  fpWeaponPixelComposite = gfxTier === 0 && !lite;
  // Видео-оружие НЕ прячем в динамическом lite: на десктопе «пропавшее оружие»
  // читается как баг. Класс mobile-lite-gfx (скрытие видео) ставит только
  // статический мобильный блок по mobileLiteBase.
  if (!mobileLiteBase) ensureWeaponVideoSources();
  // Пиксель-арт вайб: ~360p + pixelated-апскейл + постеризация. Низкое
  // разрешение заодно РАЗГРУЖАЕТ GPU (заполнение ÷10+), поэтому включён
  // и на мобильных; tier 3 опускает до 280p.
  if (localStorage.getItem("tipaduma.pixelArt") !== "0") {
    renderer.setPixelArtMode(true, gfxTier >= 3 ? 280 : 360);
  }
  try { localStorage.setItem("tipaduma.gfxTier", String(gfxTier)); } catch { /* */ }
}

/** Порог понижения качества. Было 30 мс (реакция только ниже 33 fps) —
 *  машина, стабильно выдающая 45 fps с рывками, не понижалась никогда. */
const GOV_DOWNGRADE_MS = 20;
const GOV_WINDOW = 90; // кадров на окно (~1.5 с при 60 fps)
/** При жёстких тормозах 90 кадров растягиваются на десятки секунд — окно
 *  закрывается и по времени: ≥20 сэмплов и ≥2.5 с. */
const GOV_WINDOW_MIN = 20;
const GOV_WINDOW_MS = 2500;
let govWindowStart = 0;
const govSamples: number[] = [];
let govHoldUntil = 0;
let govGoodWindows = 0;
let govUpgradeBlockedUntil = 0;
let govLastUpgradeAt = 0;
let govUpgradesLocked = false;

/** Вызывается из frame() с фактическим dt кадра. Медиана окна > 30 мс —
 *  понижаем тир; стабильно у vsync (p50 < 17.2, p95 < 21) — осторожно
 *  повышаем. Флап-защита: апгрейд, за которым в 20 с следует даунгрейд,
 *  блокирует дальнейшие апгрейды до конца сессии. */
function perfGovernorSample(dtMs: number): void {
  if (!gfxGovernorEnabled) return;
  // Окно без фокуса/скрытое: Safari троттлит rAF фоновых окон до единиц fps —
  // такие сэмплы роняли тир на ровном месте.
  if (!gameplayLive || document.visibilityState !== "visible" || !document.hasFocus()) {
    govSamples.length = 0;
    return;
  }
  const now = performance.now();
  if (now < govHoldUntil) return;
  if (govSamples.length === 0) govWindowStart = now;
  govSamples.push(dtMs);
  const windowFull =
    govSamples.length >= GOV_WINDOW ||
    (govSamples.length >= GOV_WINDOW_MIN && now - govWindowStart >= GOV_WINDOW_MS);
  if (!windowFull) return;
  const sorted = govSamples.slice().sort((a, b) => a - b);
  const p50 = sorted[sorted.length >> 1];
  const p95 = sorted[Math.floor(sorted.length * 0.95)];
  govSamples.length = 0;
  if (p50 > GOV_DOWNGRADE_MS && gfxTier < GFX_TIER_MAX) {
    if (now - govLastUpgradeAt < 20000) govUpgradesLocked = true;
    // Жёсткие тормоза (<17 fps) — прыгаем сразу на два тира и держим паузу
    // короче: лестница 0→3 по одному тиру занимала 15-40 с страданий.
    const severe = p50 > GOV_DOWNGRADE_MS * 3;
    applyGfxTier(gfxTier + (severe ? 2 : 1));
    try { localStorage.setItem("tipaduma.gfxTierFloor", String(gfxTier)); } catch { /* */ }
    govHoldUntil = now + (severe ? 2000 : 4000);
    govUpgradeBlockedUntil = now + 60000;
    govGoodWindows = 0;
    console.log(`[gfx] p50 ${p50.toFixed(1)}ms — качество вниз, tier ${gfxTier}`);
  } else if (p50 < 17.2 && p95 < 21 && gfxTier > 0 && !govUpgradesLocked && now > govUpgradeBlockedUntil) {
    govGoodWindows++;
    if (govGoodWindows >= 4) {
      applyGfxTier(gfxTier - 1);
      govLastUpgradeAt = now;
      govHoldUntil = now + 4000;
      govGoodWindows = 0;
      console.log(`[gfx] кадры стабильные — качество вверх, tier ${gfxTier}`);
    }
  } else {
    govGoodWindows = 0;
  }
}

// NB: боевой вызов applyGfxTier(gfxTier) — ниже, ПОСЛЕ loadGraphicsSettings()
// и статического mobile-lite-блока, чтобы не конфликтовать с ними порядком.
renderer.setParticleAtlasEnabled(true);

/** Мемо-кэш GLB-мешей по URL: префетч моделей параллельно с текстурами/чанками
 *  + RESTART не перекачивает и не перепарсивает те же модели (мгновенный
 *  повторный старт, нет повторной GPU-загрузки). Ошибки не кэшируем. */
const meshByUrl = new Map<string, Promise<LoadedMesh>>();
function loadMeshCached(url: string): Promise<LoadedMesh> {
  let p = meshByUrl.get(url);
  if (!p) {
    p = renderer.loadMeshFromUrl(url);
    p.catch(() => meshByUrl.delete(url));
    meshByUrl.set(url, p);
  }
  return p;
}

const particleSim = new ParticleSimulation();
particleSim.registerEmitter(presetFireEmitter(), { x: 8.0, y: 8.0, z: 0.35 });
particleSim.registerEmitter(presetSmokeEmitter(), { x: 8.0, y: 8.0, z: 0.35 });
renderer.setParticleSimulation(particleSim);

/** Над ступнями mesh-инстанса — грудь/верх торса для FX при снятии гладкой оболочки. */
const SHELL_STRIP_BURST_OFFSET_Z = 1.08;

/** Вспышка у дула как PointLight (воксели + меши), только при включённой постобработке. */
const MUZZLE_FLASH_MS = 105;
const MAX_MUZZLE_FLASHES = 6;
type MuzzleFlashLight = { x: number; y: number; z: number; born: number };
const muzzleFlashes: MuzzleFlashLight[] = [];
const _lightsFrameScratch: PointLight[] = [];

function muzzleFlashStrength(bornMs: number, nowMs: number): number {
  const u = (nowMs - bornMs) / MUZZLE_FLASH_MS;
  if (u >= 1) return 0;
  const t = 1 - u;
  return t * t;
}

function pushMuzzleFlashLight(x: number, y: number, z: number): void {
  if (mobileLiteGraphics) return;
  if (!renderer.getPostProcessEnabled()) return;
  muzzleFlashes.push({ x, y, z, born: performance.now() });
  while (muzzleFlashes.length > MAX_MUZZLE_FLASHES) {
    muzzleFlashes.shift();
  }
}

function buildFrameLights(nowMs: number): PointLight[] {
  _lightsFrameScratch.length = 0;
  if (sceneStaticLights.length > 0) {
    for (const L of sceneStaticLights) {
      _lightsFrameScratch.push(L);
    }
  } else if (currentSceneId === "arena1") {
    for (const L of lamps) {
      _lightsFrameScratch.push(L);
    }
  } else {
    // Level-pack без light_* в Blender: не подмешиваем аренные лампы (XY не совпадают со сценой).
    const cs = map.cellSize ?? 1;
    const ox = map.originX ?? 0;
    const oy = map.originY ?? 0;
    const cx = ox + (map.width * cs) * 0.5;
    const cy = oy + (map.height * cs) * 0.5;
    _lightsFrameScratch.push({
      x: cx,
      y: cy,
      z: 12,
      range: 120,
      color: [0.95, 0.93, 0.88],
    });
  }
  let w = 0;
  for (let i = 0; i < muzzleFlashes.length; i++) {
    const f = muzzleFlashes[i];
    const s = muzzleFlashStrength(f.born, nowMs);
    if (s <= 0.012) continue;
    muzzleFlashes[w++] = f;
    _lightsFrameScratch.push({
      x: f.x,
      y: f.y,
      z: f.z,
      range: 2.4 + s * 3.2,
      color: [2.0 * s + 0.14, 1.35 * s + 0.09, 0.38 * s + 0.03],
    });
  }
  muzzleFlashes.length = w;
  weapons.appendLights(_lightsFrameScratch); // weapons: вспышки, взрывы, ореолы
  artifacts.appendLights(_lightsFrameScratch); // artifacts
  story.appendLights(_lightsFrameScratch); // story
  return _lightsFrameScratch;
}

/** Левый debug-HUD (fps, chunks, mem…): по умолчанию скрыт — переключение Ctrl+L. */
let gameDebugHudVisible = false;

/** Оверлей F10: замер кадра / тиков; включает GPU-разбивку рендера как `?debugPerf=1`. */
let profilerOverlayVisible = false;

/** Разбивка времени рендера; также включается на время открытого профайлера (F10). */
const DEBUG_PERF = new URLSearchParams(location.search).get("debugPerf") === "1";
function syncRendererDebugPerf(): void {
  renderer.setDebugPerf(DEBUG_PERF || profilerOverlayVisible);
}
syncRendererDebugPerf();

/** F9 / ?colliders=1 — 2D debug overlay поверх WebGL: mesh_collision + voxel wall columns. */
let colliderDebugVisible = new URLSearchParams(location.search).get("colliders") === "1";
let colliderDebugMesh: MeshCollisionEntry[] | null = null;
const colliderCanvas = document.createElement("canvas");
colliderCanvas.id = "collider-debug-overlay";
Object.assign(colliderCanvas.style, {
  position: "fixed",
  inset: "0",
  width: "100vw",
  height: "100vh",
  pointerEvents: "none",
  zIndex: "2147483000",
  display: "none",
});
document.body.appendChild(colliderCanvas);
const colliderCtx = colliderCanvas.getContext("2d");

function syncColliderCanvasSize(): void {
  const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
  const w = Math.max(1, Math.floor(window.innerWidth * dpr));
  const h = Math.max(1, Math.floor(window.innerHeight * dpr));
  if (colliderCanvas.width !== w || colliderCanvas.height !== h) {
    colliderCanvas.width = w;
    colliderCanvas.height = h;
  }
  colliderCanvas.style.display = colliderDebugVisible ? "block" : "none";
}

function drawColliderSegment(
  ctx: CanvasRenderingContext2D,
  a: [number, number, number],
  b: [number, number, number],
): void {
  const pa = renderer.projectToScreen(a[0], a[1], a[2]);
  const pb = renderer.projectToScreen(b[0], b[1], b[2]);
  if (!pa || !pb) return;
  ctx.moveTo(pa.px, pa.py);
  ctx.lineTo(pb.px, pb.py);
}

function drawColliderAABB(
  ctx: CanvasRenderingContext2D,
  min: [number, number, number],
  max: [number, number, number],
  color: string,
  lineWidth = 1.2,
): void {
  const [x0, y0, z0] = min;
  const [x1, y1, z1] = max;
  const p: [number, number, number][] = [
    [x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0],
    [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1],
  ];
  ctx.beginPath();
  drawColliderSegment(ctx, p[0], p[1]);
  drawColliderSegment(ctx, p[1], p[2]);
  drawColliderSegment(ctx, p[2], p[3]);
  drawColliderSegment(ctx, p[3], p[0]);
  drawColliderSegment(ctx, p[4], p[5]);
  drawColliderSegment(ctx, p[5], p[6]);
  drawColliderSegment(ctx, p[6], p[7]);
  drawColliderSegment(ctx, p[7], p[4]);
  drawColliderSegment(ctx, p[0], p[4]);
  drawColliderSegment(ctx, p[1], p[5]);
  drawColliderSegment(ctx, p[2], p[6]);
  drawColliderSegment(ctx, p[3], p[7]);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

function meshColliderColor(role: string): string {
  if (role === "wall" || role === "window") return "rgba(255,60,45,0.95)";
  if (role === "door") return "rgba(255,210,40,0.95)";
  if (role === "floor") return "rgba(70,255,110,0.75)";
  if (role === "stair") return "rgba(90,170,255,0.85)";
  return "rgba(255,255,255,0.55)";
}

function debugCircleOverlapsAABB(
  cx: number, cy: number,
  x0: number, y0: number, x1: number, y1: number,
): boolean {
  let px = cx;
  if (px < x0) px = x0;
  else if (px > x1) px = x1;
  let py = cy;
  if (py < y0) py = y0;
  else if (py > y1) py = y1;
  const dx = cx - px;
  const dy = cy - py;
  return dx * dx + dy * dy <= PLAYER_RADIUS * PLAYER_RADIUS + 1e-5;
}

function renderColliderDebugOverlay(
  cam: { x: number; y: number; eyeZ?: number },
  playerX: number,
  playerY: number,
  playerFeetZ: number,
  playerColliderH: number,
): void {
  if (!colliderDebugVisible || !colliderCtx) return;
  syncColliderCanvasSize();
  const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
  const ctx = colliderCtx;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
  ctx.font = "12px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textBaseline = "top";
  const maxDist2 = 38 * 38;

  let meshDrawn = 0;
  const mesh = colliderDebugMesh ?? [];
  for (const b of mesh) {
    const cx = (b.min[0] + b.max[0]) * 0.5;
    const cy = (b.min[1] + b.max[1]) * 0.5;
    const dx = cx - cam.x;
    const dy = cy - cam.y;
    if (dx * dx + dy * dy > maxDist2) continue;
    drawColliderAABB(ctx, b.min, b.max, meshColliderColor(b.role), b.role === "wall" ? 1.4 : 1.1);
    meshDrawn++;
  }

  const playerHeadZ = playerFeetZ + playerColliderH;
  let voxelCols = 0;
  let voxelBlockingNow = 0;
  for (const ch of chunks.all()) {
    if (ch.role !== ChunkRole.Wall && ch.role !== ChunkRole.Window) continue;
    ensureColumnMaps(ch);
    if (!ch.columnTopZ || !ch.columnBottomZ) continue;
    const sx = ch.sx;
    const sy = ch.sy;
    const cs = ch.cubeSize;
    for (let by = 0; by < sy; by++) {
      for (let bx = 0; bx < sx; bx++) {
        const idx = bx + by * sx;
        const z1 = ch.columnTopZ[idx];
        const z0 = ch.columnBottomZ[idx];
        if (z1 < -1e19 || z0 > 1e19 || !(z0 < z1)) continue;
        const x0 = ch.ox + bx * cs;
        const y0 = ch.oy + by * cs;
        const cx = x0 + cs * 0.5;
        const cy = y0 + cs * 0.5;
        const dx = cx - cam.x;
        const dy = cy - cam.y;
        if (dx * dx + dy * dy > 18 * 18) continue;
        const overlapsZ = z1 > playerFeetZ && z0 < playerHeadZ;
        const overlapsPlayer = overlapsZ && debugCircleOverlapsAABB(playerX, playerY, x0, y0, x0 + cs, y0 + cs);
        drawColliderAABB(
          ctx,
          [x0, y0, z0],
          [x0 + cs, y0 + cs, z1],
          overlapsPlayer ? "rgba(255,135,0,1)" : overlapsZ ? "rgba(210,70,255,0.85)" : "rgba(130,70,255,0.28)",
          overlapsPlayer ? 2.2 : 0.8,
        );
        voxelCols++;
        if (overlapsPlayer) voxelBlockingNow++;
      }
    }
  }

  ctx.fillStyle = "rgba(0,0,0,0.58)";
  ctx.fillRect(12, 12, 430, 116);
  ctx.fillStyle = "white";
  ctx.fillText("Collider debug (F9):", 22, 20);
  ctx.fillStyle = "rgba(255,60,45,1)";
  ctx.fillText(`red mesh wall/window AABB: ${meshDrawn}`, 22, 38);
  ctx.fillStyle = "rgba(210,70,255,1)";
  ctx.fillText(`purple voxel wall columns: ${voxelCols}`, 22, 56);
  ctx.fillStyle = "rgba(255,135,0,1)";
  ctx.fillText(`orange overlaps player capsule now: ${voxelBlockingNow}`, 22, 74);
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.fillText(`yellow doors, green floors, blue stairs`, 22, 92);
  ctx.fillText(`body ${playerX.toFixed(2)}, ${playerY.toFixed(2)}, feet=${playerFeetZ.toFixed(2)} head=${playerHeadZ.toFixed(2)}`, 22, 110);
}

const LS_POM_LAYERS = "tipaduma.pomLayers";
const LS_RENDER_SCALE = "tipaduma.renderScale";
const LS_POST_PROCESS = "tipaduma.postProcess";
const LS_BLOOM = "tipaduma.bloom";
/** Выбор пользователя по POM. Гувернор глушит POM на низких тирах, но при
 *  возврате на tier 0 надо восстановить именно то, что выбрал человек. */
let userPomLayers: PomLayerOption = 8;
/** tier 0 — оружие композитится в пиксель-арт канвас (drawImage из видео
 *  каждый кадр). Это самая дорогая DOM-статья и в Safari она особенно дорога
 *  (HEVC-с-альфой → канвас идёт без быстрого GPU-блита). На тирах ≥1
 *  композит выключаем: оружие остаётся видимым, но его рисует нативный
 *  компоновщик браузера, а не мы каждый кадр. */
let fpWeaponPixelComposite = true;
function loadGraphicsSettings(): void {
  try {
    const p = localStorage.getItem(LS_POM_LAYERS);
    if (p === "0" || p === "4" || p === "8") {
      userPomLayers = parseInt(p, 10) as PomLayerOption;
      renderer.setPomLayers(userPomLayers);
    }
    const r = localStorage.getItem(LS_RENDER_SCALE);
    if (r != null) {
      const f = parseFloat(r);
      if (Number.isFinite(f)) renderer.setRenderScale(f);
    }
    const pp = localStorage.getItem(LS_POST_PROCESS);
    if (pp === "1") renderer.setPostProcessEnabled(true);
    else if (pp === "0") renderer.setPostProcessEnabled(false);
    const bl = localStorage.getItem(LS_BLOOM);
    if (bl === "0") renderer.setBloomUiEnabled(false);
    else if (bl === "1") renderer.setBloomUiEnabled(true);
  } catch {
    /* private mode / no storage */
  }
}
loadGraphicsSettings();
// Статический lite-блок — ТОЛЬКО для настоящих мобильных (mobileLiteBase):
// он необратим (renderScale-кап, выпил <source>, залоченные чекбоксы).
// Динамический lite слабых десктопов делает applyGfxTier ниже.
if (mobileLiteBase) {
  document.body.classList.add("mobile-lite-gfx");
  renderer.setPostProcessEnabled(false);
  renderer.setPomLayers(0);
  renderer.setMaxDevicePixelRatio(1);
  const cap = 0.58;
  if (renderer.getRenderScale() > cap) renderer.setRenderScale(cap);
  renderer.setLightLodDistance(Math.min(renderer.getLightLodDistance(), 12));
  renderer.setPomMaxDistance(0);
  for (const el of [_fpWeaponIdleVideoEl, _fpWeaponWalkVideoEl, _fpFireVideoEl]) {
    if (!el) continue;
    try {
      el.pause();
      el.removeAttribute("src");
      for (const s of el.querySelectorAll("source")) s.remove();
      el.load();
    } catch { /* */ }
  }
  gsPostProcess.checked = false;
  gsPostProcess.disabled = true;
  gsPostProcess.title = t("На мобильном отключено для скорости");
  gsBloom.checked = false;
  gsBloom.disabled = true;
  gsBloom.title = t("На мобильном отключено для скорости");
}

// Применяем стартовый тир качества (сохранённый с прошлой сессии или 0).
if (NO_WEAPON_VIDEO) unloadWeaponVideos();
applyGfxTier(gfxTier);

// Поднимаем DPR-пересчёт только когда пользователь реально ресайзит окно —
// иначе resize() каждый кадр вызывал synchronous layout через canvas.clientWidth.
window.addEventListener("resize", () => renderer.markResize(), { passive: true });

const input = new Input(canvas);
// weapons: оружие (ввод, инвентарь, HUD, вид от 1-го лица, эффекты).
const weapons = new WeaponsClient({
  renderer,
  particles: particleSim,
  input,
  myId: () => myId,
  soundVolume: () => soundVolume01,
  lite: () => mobileLiteGraphics,
  thirdPerson: () => effectiveThirdPerson(),
  inGame: () => transport !== null && myId >= 0 && gameplayLive && gameInputActive(),
  controlActive: () => localPlayerControlActive(),
  localState: () => ({
    x: renderLerpX, y: renderLerpY, z: renderLerpZ + 1.6,
    speed: Math.hypot(predicted.vx, predicted.vy), grounded: predicted.grounded,
  }),
  playerInstances: () => playerInstances,
  pickups: () => pickups,
  npcInstances: () => {
    weaponsNpcInst.clear();
    for (const [id, n] of npcs) weaponsNpcInst.set(id, n.inst);
    return weaponsNpcInst;
  },
  fpVideo: FP_VIDEO_WEAPON,
  onRemoteFire: (kind, id) => {
    if (kind === 1) {
      const npc = npcs.get(id);
      if (npc) { npc.triggerShoot(); npcLastShotAt.set(id, performance.now()); }
    } else {
      remoteShootUntil.set(id, performance.now() + REMOTE_SHOOT_HOLD_MS);
    }
  },
});
const weaponsNpcInst = new Map<number, MeshInstance>();
// artifacts: артефакты из разрушенного — модели, свечение, дуга, подсказки.
const artifacts = new ArtifactsClient({
  renderer,
  particles: particleSim,
  myId: () => myId,
  guest: () => !(authInfo && authInfo.ok && !authInfo.guest),
  soundVolume: () => soundVolume01,
  lite: () => mobileLiteGraphics,
  listener: () => ({ x: renderLerpX, y: renderLerpY, z: renderLerpZ + 1.6 }),
  pickups: () => pickups,
});
// story: сюжетный режим «Книга заклинаний» — существа-видео, этапы, HUD.
const STORY_SCENE_ID = "alone_in_the_dark";
const story = new StoryClient({
  renderer,
  gl: renderer.getGL(),
  player: () => (myId >= 0 ? { x: renderLerpX, y: renderLerpY, z: renderLerpZ, yaw: renderLerpYaw } : null),
  myDeaths: () => scores.get(myId)?.deaths ?? 0,
  soundVolume: () => soundVolume01,
  onAgain: () => {
    pendingRoom = { mode: "single", sceneId: STORY_SCENE_ID, npcCount: 0, restart: true, story: true };
    void start();
  },
  onMenu: () => {
    if (transport) onRoomClosed(100);
  },
});
let touchCtl: TouchControlsHandle | null = null;
if (touchUi) {
  document.body.classList.add("touch-ui");
  input.setTouchMode(true);
  touchCtl = initTouchControls(input, touchControlsEl);
}
const prediction = new PredictionBuffer();
const snapBuf = new SnapshotBuffer();
const chunks = new ChunkRegistry();
const fallingGroups = new FallingGroupRegistry();
/** Throttle smoke trail for FallingGroupUpdate (groupId → last emit sec). */
const fallingSmokeTrailLast = new Map<number, number>();
const damagedTiles = new Set<number>();
const killfeed: { text: string; until: number }[] = [];

// FPS / частота кадров: считаем за последнюю 1 секунду
let fpsFrameCount = 0;
let fpsLastSample = performance.now();
let fpsValue = 0;
/** Сглаженный dt для экспоненты XY-камеры — при скачках frame time kCam не «прыгает». */
let smoothDtSecForCam = 1 / 60;

// weapons: темп/патроны спуска — src/weapons (state.ts, catalog.ts).
/** Макс. патроны винтовки (сервер game.MaxWeaponAmmo). */
const MAX_WEAPON_AMMO = 50;
let myId = -1;
let yawInitialized = false;
let nextSeq = 1;
let lastInputDt = TICK_DT;
// z = ступни игрока над полом арены (0 при спавне, >0 в прыжке или на платформе).
// vz/grounded поддерживаются локальным предсказанием, сверяются с сервером.
// prevButtons нужны для edge-trigger прыжка (как на сервере).
let predicted = {
  x: 2.5, y: 2.5, z: 0, vx: 0, vy: 0, vz: 0, grounded: true, crouching: false,
  stairContactPrev: false,
  yaw: 0, pitch: 0,
};
let prevButtons = 0;
// Сглаженная Z глаза камеры (в локальной системе над ногами). Лерпит между
// 1.6 (стоя) и 1.0 (присед) — плавный приседание камеры, не скачок.
let eyeZSmoothed = 1.6;
let lastDrawTime = performance.now();

/** Текущий FP view bob по Z — обновляется в `draw()`, используется `getShotRay` / `updateCrosshair`. */
let fpViewBobZ = 0;
/** Фаза шага (рад); крутится только на земле при движении. */
let fpBobPhase = 0;
let fpBobPrevLerpX = 2.5;
let fpBobPrevLerpY = 2.5;
let fpBobPrevTime = performance.now();
/** Сглаженная горизонтальная скорость для bob (без шума от d(pos)/dt по кадрам). */
let fpBobSpeedSmooth = 0;

/** Позиция для рендера (камера/меш/прицел): интерполяция между шагами физики (Gaffer). */
let renderLerpX = 2.5;
let renderLerpY = 2.5;
let renderLerpZ = 0;
/** Те же границы, что в `Input.sample()` — рендер-lerp не выводит pitch за clamp. */
const RENDER_PITCH_LO = -1.2;
const RENDER_PITCH_HI = 1.2;
let renderLerpYaw = 0;
let renderLerpPitch = 0;
/** Текущая «скорость» visual-Z для smooth-damp 2nd-order фильтра. */
let renderLerpZVel = 0;

/** Smooth-damp (2nd-order critically damped, Game Programming Gems / Unity).
 *  Возвращает [newPos, newVel].
 *
 *  Преимущество над 1st-order exp-lerp: при мгновенном δ = target-current
 *  velocity ramps up плавно из 0 (нет «толчка» в первом кадре). Использует
 *  переданный `vel` (state) — caller должен сохранять между кадрами.
 *  smoothTime — желаемое время «settle» (приблизительно). maxSpeed —
 *  страховочный cap скорости (м/с). */
function smoothDampFloat(
  current: number,
  target: number,
  vel: number,
  smoothTime: number,
  dt: number,
  maxSpeed: number,
): [number, number] {
  smoothTime = Math.max(0.0001, smoothTime);
  const omega = 2 / smoothTime;
  const x = omega * dt;
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  let change = current - target;
  const originalTo = target;
  const maxChange = maxSpeed * smoothTime;
  if (change > maxChange) change = maxChange;
  else if (change < -maxChange) change = -maxChange;
  const adjustedTarget = current - change;
  const temp = (vel + omega * change) * dt;
  const newVel = (vel - omega * temp) * exp;
  let output = adjustedTarget + (change + temp) * exp;
  if (originalTo - current > 0 === output > originalTo) {
    output = originalTo;
    return [output, (output - originalTo) / dt];
  }
  return [output, newVel];
}

/** Шаг позиции за последний учтённый фикс-тик (predicted−ix). Для детекта смены направления стрейфа. */
let prevChordDx = 0;
let prevChordDy = 0;

/** Полный сброс визуальных интерполяторов и bob-состояния. Используется
 *  только для НАСТОЯЩИХ телепортов (init/respawn/portal). */
function syncRenderInterpFromPredicted(): void {
  renderLerpX = predicted.x;
  renderLerpY = predicted.y;
  renderLerpZ = predicted.z;
  renderLerpYaw = predicted.yaw;
  renderLerpPitch = predicted.pitch;
  prevChordDx = 0;
  prevChordDy = 0;
  smoothDtSecForCam = 1 / 60;
  fpBobPrevLerpX = renderLerpX;
  fpBobPrevLerpY = renderLerpY;
  fpBobPhase = 0;
  fpBobSpeedSmooth = 0;
  fpViewBobZ = 0;
}

/** Мягкая пересинхронизация для hard-snap reconciliation. Resync только
 *  XY/yaw/pitch (где snap имеет смысл — телепорт после большого dist2),
 *  но НЕ Z, и НЕ bob-состояние.
 *
 *  Reconciliation snap часто срабатывает на лестнице из-за server-client
 *  RTT-desync (~50мс): client предсказывает drop предсказательно
 *  (predicted.z=6.36), server-snapshot ещё рапортует прежнюю позицию
 *  (server.z=6.88), |dz|>0.5 → snap. Но post-replay predicted.z тот же
 *  (физика детерминирована), и реального телепорта нет. Полный сброс
 *  renderLerpZ дёргает камеру; сброс bob-фазы вызывает «заикание» bob'а.
 *  Эта мягкая версия оставляет визуал плавным. */
function syncRenderInterpAfterReconcile(): void {
  renderLerpX = predicted.x;
  renderLerpY = predicted.y;
  // renderLerpZ, bob-state — оставляем как есть.
  renderLerpYaw = predicted.yaw;
  renderLerpPitch = predicted.pitch;
  prevChordDx = 0;
  prevChordDy = 0;
}

/** Подтяжка к авторитету сервера: накапливаем в snapshot, размазываем по fixedTick (без рывка @20Hz). */
let pendingReconcileX = 0;
let pendingReconcileY = 0;
let pendingReconcileZ = 0;
const RECON_DRAIN = 0.32;
const RECON_XY_DEAD2 = 1e-6;
const RECON_Z_DEAD = 0.0008;
const RECON_SOFT_XY = 0.2;
const RECON_SOFT_Z = 0.16;
let netStatus = "offline";
const scores: Map<number, { score: number; hp: number; deaths: number; team: number }> = new Map();
const nicknames: Map<number, string> = new Map();

// ── v22: состояние матча (из Hello + Snapshot) ──────────────────────────
let matchMode: number = MatchMode.FFA;
let matchRoundsTotal = 0;   // 0 = SP (матч-HUD скрыт)
const matchState = {
  phase: MatchPhase.Idle as number,
  roundIdx: 0,
  secsLeft: 0,
  teamAWins: 0,
  teamBWins: 0,
  teamAKills: 0,
  teamBKills: 0,
};
/** Транзиентный баннер (старт/итог раунда) поверх фазового текста. */
let matchBannerTransient = "";
let matchBannerTransientUntil = 0;

function myTeam(): number {
  return scores.get(myId)?.team ?? Team.None;
}

function teamLabel(team: number): string {
  return team === Team.A ? t("КРАСНЫЕ") : team === Team.B ? t("СИНИЕ") : "";
}

function resetMatchState(): void {
  matchMode = MatchMode.FFA;
  matchRoundsTotal = 0;
  matchState.phase = MatchPhase.Idle;
  matchState.roundIdx = 0;
  matchState.secsLeft = 0;
  matchState.teamAWins = 0;
  matchState.teamBWins = 0;
  matchState.teamAKills = 0;
  matchState.teamBKills = 0;
  matchBannerTransient = "";
  matchBannerTransientUntil = 0;
  matchHudEl.classList.add("hidden");
  matchBannerEl.classList.add("hidden");
  matchEndEl.classList.add("hidden");
}
let myNick = "guest";
/** ID текущей комнаты (пустая строка для SP до первого Hello). */
let currentRoomId = "";
/** sceneId/roomKind из Hello для UI. */
let currentSceneId = "arena1";
let currentRoomKind = 0;
let currentMaxPlayers = 1;

/** Оверлей меню поверх идущей сессии — пауза локальной симуляции и ввода. */
function gameMenuPaused(): boolean {
  return transport !== null && myId >= 0 && gameplayLive && !overlay.classList.contains("hidden");
}

/** Ввод в игру (клавиатура/мышь → fixedTick) только без меню. */
function gameInputActive(): boolean {
  return transport !== null && myId >= 0 && gameplayLive && overlay.classList.contains("hidden");
}

// ── Pickups (v8) ─────────────────────────────────────────────────────
// Каждый pickup в сцене — объект с координатами, типом и state (active/taken).
// Сервер шлёт список PickupInit один раз после Hello; дальше state приходит
// в Snapshot.pickups и при самом подборе — EvPickup.
const pickups = new Map<number, PickupInit>();
// Local inventory: medkit HP (сервер authoritative по снапшоту; EvPickup — дельта).
const localInventory = { hp: 100 };
/** Предсказанные патроны для UI/звука; каждый снапшот сверяется с activeAmmo. */
let predictedAmmoRounds = MAX_WEAPON_AMMO;
/** GLB для pickup'ов на сцене (ammo / medkit); weapon — масштаб ammo.glb. */
let pickupAmmoMesh: LoadedMesh | null = null;
let pickupMedkitMesh: LoadedMesh | null = null;
const pickupItemInst = new Map<number, MeshInstance>();
/** Ключ визуала (ammo / medkit / weapon) — смена типа пересоздаёт инстанс. */
const pickupItemVisualTag = new Map<number, string>();

// Mesh-инстансы игроков (солдат). Mesh шарится между всеми (one LoadedMesh —
// many MeshInstance). state/матрицы уникальны на инстанс.
let soldierMesh: LoadedMesh | null = null;
/** Внутренний «мясной» меш (по умолчанию soldier_animated_vox.glb): шейдерная битмаска + кости. */
let soldierVoxMesh: LoadedMesh | null = null;
/** Центроиды сабмешей только для `?realBody=1` (прототип per-mesh кубов). */
let soldierRealVoxCentroids: Float32Array | null = null;
/** Куб → ячейка bodyVox в референсной позе — только для `?realBody=1`. */
let soldierRealVoxStableCells: Int32Array | null = null;
/** Прототип: отдельный draw на каждый GLB-куб — только с `?realBody=1`. */
let useRealVoxelBody = false;
let soldierScale = 1.0;
let soldierFloorOffset = 0.0;
// yaw-offset для меша солдата. Новый soldier_animated.glb ориентирован так,
// что forward = mesh +Z (узкая ось грудь-спина), а наш uModel ожидает
// forward = mesh +X. Доворачиваем +90° вокруг вертикальной оси, чтобы
// солдат смотрел туда же, куда и игрок (по yaw).
const SOLDIER_YAW_OFFSET = Math.PI / 2;
// Корректировка аим-yaw при стрельбе: anim firingRifle держит ствол ~40°
// левее естественного forward'а корпуса. Доворачиваем Spine вправо чтобы
// оружие совпало с прицелом. Знак подбирается экспериментально (если
// корпус крутит не туда — поменять знак). Радианы.
//
// ВАЖНО: с включённым rotation-only Hips overlay (meshLoader.ts:
// upperBodyRotationOnlyNodes) компенсация ПРИ ПОЛНОМ overlay weight
// больше не нужна — Hips принимает позу shoot-клипа, корпус и оружие
// уже направлены корректно. Но в фазе fade-in (weight 0..1) Hips ещё
// смешан с base-Hips, и часть compensation нужна для плавного подхода
// к "aim forward". Поэтому применяем **инвертированную** зависимость:
// при w=1 → 0, при w=0 → -40° (= старое поведение, когда overlay ещё
// не активен и Hips полностью из base).
const SOLDIER_SHOOT_AIM_YAW = -40 * Math.PI / 180;
/** Joint-индексы (в skin.joints), которые в FP-виде локального игрока
 *  НЕ должны рендериться (их bone matrix обнуляется → вершины с этими
 *  весами схлопываются в origin меша). Здесь — всё кроме поддеревьев
 *  `LeftShoulder` и `RightShoulder`, т.е. видны только руки (плечи →
 *  предплечья → кисти). Оружие — отдельный non-skinned sub-mesh через
 *  `weapone_bone`, оно не зависит от скининга и рендерится как есть. */
let fpHiddenJoints: number[] = [];
/** Имена костей, поддеревья которых ОСТАЮТСЯ видимыми в FP.
 *  Берём ForeArm (предплечье + кисть + пальцы), т.к. плечо + верхняя
 *  часть руки разделяют веса с торсом/шеей — при коллапсе parent'ов
 *  шов около плеча "сжимался" и давал обрывки геометрии. Предплечье
 *  ниже в иерархии → вершины на нём почти полностью весят на этом
 *  joint'e, без артефактов. */
const FP_ARM_ROOT_BONE_NAMES = [
  "mixamorig:LeftForeArm",
  "mixamorig:RightForeArm",
];
const playerInstances: Map<number, MeshInstance> = new Map();
/** Не-voxel статика сцены (`static_meshes.glb`), если сервер отдал manifest. */
let levelStaticMeshInst: MeshInstance | null = null;
/** Точки взаимодействия из Hello (v20); подсказка у дверей особняка. */
let interactHotspotsHello: HelloInteractHotspot[] = [];
let mansionDoorGhostClearTimer: number | null = null;

const MANSION_DOOR_CLIP = "door_manison_open";
const MANSION_DOOR_CLOSE_MS = (90 / 60) * 1000;

// ─── Межкомнатные двери level-pack'а (v21) ─────────────────────────────
// Список строится из нод static_meshes.glb детерминированно (сортировка по
// базе имени) — тем же правилом, что сервер строит его из mesh_collision.
// Событие EvInteriorDoor несёт индекс в этом списке.
interface InteriorDoorEntry {
  base: string;
  nodeFilter: Set<number>;
  double: boolean;
}
let interiorDoorRegistry: InteriorDoorEntry[] = [];

/** door_<база>_<leaf|leafl|leafr> (регистр не важен) → база; прочее → null. */
function interiorDoorLeafBase(nodeName: string): string | null {
  const n = nodeName.toLowerCase();
  if (!n.startsWith("door_")) return null;
  for (const sfx of ["_leafl", "_leafr", "_leaf"]) {
    if (n.endsWith(sfx)) {
      const base = n.slice(0, -sfx.length);
      // Парадная пара — свой runtime (applyMansionDoorClient).
      if (base.includes("manison") || base.includes("mansion")) return null;
      return base;
    }
  }
  return null;
}

function buildInteriorDoorRegistry(mesh: { nodes: { name: string }[] }): InteriorDoorEntry[] {
  // Порядок и состав — СТРОГО из mesh_collision (getInteriorDoorBases —
  // тот же список, что на сервере). Ноды glb только наполняют nodeFilter:
  // дверь без ноды-полотна в glb остаётся в списке (индексы не сдвигаются),
  // просто не анимируется визуально.
  const entries: InteriorDoorEntry[] = getInteriorDoorBases().map((base) => ({
    base,
    nodeFilter: new Set<number>(),
    double: false,
  }));
  const byBase = new Map(entries.map((e) => [e.base, e]));
  for (let i = 0; i < mesh.nodes.length; i++) {
    const base = interiorDoorLeafBase(mesh.nodes[i].name ?? "");
    if (!base) continue;
    const e = byBase.get(base);
    if (!e) continue; // нода без mesh_collision-полотна — сервер про неё не знает
    e.nodeFilter.add(i);
    const n = (mesh.nodes[i].name ?? "").toLowerCase();
    if (n.endsWith("_leafl") || n.endsWith("_leafr")) e.double = true;
  }
  return entries;
}

function applyInteriorDoorClient(doorIdx: number, phase: number): void {
  const inst = levelStaticMeshInst;
  if (interiorDoorRegistry.length === 0 && inst) {
    // Страховка от гонки загрузки (см. setMeshCollision в fetchAndLoadChunks).
    interiorDoorRegistry = buildInteriorDoorRegistry(inst.mesh);
  }
  const entry = interiorDoorRegistry[doorIdx];
  if (!entry || !inst) return;
  const clips = phase === 0
    ? (entry.double ? ["DblOpenL", "DblOpenR"] : ["DoorOpen"])
    : (entry.double ? ["DblCloseL", "DblCloseR"] : ["DoorClose"]);
  inst.doorLayers.set(entry.base, {
    clips,
    time: 0,
    speed: 1,
    holdLast: phase === 0,
    autoRemove: phase !== 0,
    nodeFilter: entry.nodeFilter,
  });
  if (phase === 0) {
    setInteriorDoorGhostBase(entry.base, true);
  } else {
    // Коллизия возвращается по завершении close-анимации (зеркало сервера).
    window.setTimeout(() => setInteriorDoorGhostBase(entry.base, false), MANSION_DOOR_CLOSE_MS);
  }
}
/** Sceneid, для которой `levelStaticMeshInst` загружен (чтобы fire-and-forget
 *  `loadLevelStaticMesh` от Hello не перезагружал уже готовый GLB). */
let levelStaticMeshSceneId: string | null = null;
/** Последний viewed hp игрока — чтобы в следующем снапшоте detect'ить
 *  "hp упал → попали → флинч". Используется и для remote, и для локального. */
const playerLastHp: Map<number, number> = new Map();
/** performance.now() последнего hit-event'а. Нужно для того же taimeout'а
 *  hurt-overlay'я что и у NPC: очередь → retrigger, отпустили → stop. */
const playerLastHitAt: Map<number, number> = new Map();
/** Максимальный HP игрока в протоколе = 100 (`Player.Hp: uint8`). */
const PLAYER_MAX_HP = 100;

/** Локальный игрок без HP (снапшот authoritative). */
function localPlayerDead(): boolean {
  if (myId < 0) return false;
  const me = scores.get(myId);
  return (me?.hp ?? PLAYER_MAX_HP) === 0;
}

/** Движение/огонь/отправка input на сервер — только живой и без паузы-меню. */
function localPlayerControlActive(): boolean {
  return gameInputActive() && !localPlayerDead();
}

// Для расчёта скорости (speed-driven анимация): храним предыдущую позицию.
// Включаем z для расчёта вертикальной скорости (vz) у remote-игроков —
// у локального vz берётся напрямую из `predicted.vz`.
const prevPlayerPos: Map<number, { x: number; y: number; z: number; t: number }> = new Map();
// Сглаженная скорость per player (экспоненциальный фильтр с tau≈150мс).
// Без сглаживания render-rate (60-144Гц) выше fixed-tick (60Гц), и на
// "пустых" рендер-кадрах position-delta=0 → speed=0 → idle, на "тиковых"
// кадрах огромный скачок → run. Это вызывает дрожание baseAnim'а.
const playerSpeedSmoothed: Map<number, number> = new Map();
// Remote-игроки: stop fire spam'ить upperLayer; держим upper-layer 250мс
// после каждого EvType.Shot события.
const remoteShootUntil: Map<number, number> = new Map();
/** Per-remote smooth-damp Z velocity state (для visual Z lerp моделей). */
const remoteZVel: Map<number, number> = new Map();
const REMOTE_SHOOT_HOLD_MS = 250;
// Прыжок — двухфазная one-shot анимация: сначала играется "jump" (отрыв от
// земли), затем "jumpDown" (приземление), после чего управление возвращается
// обычному speed-driven селектору. Триггерится по edge-событию Space у локального
// игрока. Remote-sync пока нет (Phase 2: добавить EvJump в протокол).
type JumpPhase = "up" | "down";
interface JumpState { phase: JumpPhase }
const jumpState: Map<number, JumpState> = new Map();
// Стоп-транзиция — one-shot "runToStop" при резком переходе с бега/ходьбы
// в покой. Смягчает визуальную задержку в state machine (сглаженная скорость
// падает до TH_WALK_DOWN не мгновенно), отыгрывает торможение и затем
// возвращает управление speed-driven селектору (который увидит idle).
const stopTransition: Set<number> = new Set();
// Override анимаций hot-keys (для ручной отладки 1..5) — null = state machine.
let baseAnimOverride: string | null = null;
let upperAnimOverride: boolean | null = null;
// Камера: false = first-person, true = third-person.
let thirdPerson = false;

/** Камера chase (TP): по V или принудительно после смерти. */
function effectiveThirdPerson(): boolean {
  return thirdPerson || localPlayerDead();
}

// ─── NPC (серверные боты) ──────────────────────────────────────────────
// Состояние NPC теперь authoritative на сервере. Клиент хранит Map<id, Npc>,
// где Npc — визуальная обёртка вокруг MeshInstance. Позиция/yaw/hp/state
// применяются из Snapshot.npcs; смерть и flinch — по дельтам.
const npcs: Map<number, Npc> = new Map();
/** performance.now() последнего выстрела NPC (для авто-снятия shoot overlay). */
const npcLastShotAt: Map<number, number> = new Map();
const NPC_SHOOT_OVERLAY_MS = 400;

// ─── Аудио: эффекты, музыка, сохранённые уровни ─────────────────────────
const LS_AUDIO_SOUND = "tipaduma.audio.sound";
const LS_AUDIO_MUSIC = "tipaduma.audio.music";

let soundVolume01 = 1;
let musicVolume01 = 0.7;

function loadAudioSettings(): void {
  try {
    const s = localStorage.getItem(LS_AUDIO_SOUND);
    const m = localStorage.getItem(LS_AUDIO_MUSIC);
    if (s !== null) {
      const v = parseFloat(s);
      if (!Number.isNaN(v)) soundVolume01 = Math.max(0, Math.min(1, v));
    }
    if (m !== null) {
      const v = parseFloat(m);
      if (!Number.isNaN(v)) musicVolume01 = Math.max(0, Math.min(1, v));
    }
  } catch { /* ignore */ }
}

function saveAudioSettings(): void {
  try {
    localStorage.setItem(LS_AUDIO_SOUND, String(soundVolume01));
    localStorage.setItem(LS_AUDIO_MUSIC, String(musicVolume01));
  } catch { /* ignore */ }
}

loadAudioSettings();

/** Эффекты переведены на Web Audio (engine/sfx.ts): на HTMLAudioElement
 *  каждый seek/громкость/скорость дёргает медиа-конвейер, что в Safari
 *  синхронно тормозит кадр. Пути к файлам оставлены прежними. */
const SFX_FIRE = "/sounds/fire.mp3";
const SFX_OUT_OF_AMMO = "/sounds/outOfAmmo.wav";
const SFX_RUN = "/sounds/run.mp3";



const FOOTSTEP_VOL_LOCAL = 0.42;
const runFootstepLoop = new SfxLoop(SFX_RUN);

/** Меню / пауза — Ashen Gate; матч — Obsidian Gate.
 *  preload=metadata: полные треки (8 и 4.5 мин) с preload=auto качались целиком
 *  при открытии страницы и отъедали канал у моделей/текстур; play() и так
 *  стримит с места. */
const menuBgMusic = new Audio("/music/ashen-gate.mp3");
menuBgMusic.preload = "metadata";
menuBgMusic.loop = true;

const gameBgMusic = new Audio("/music/obsidian-gate.mp3");
gameBgMusic.preload = "metadata";
gameBgMusic.loop = true;

function applySfxLevels(): void { /* громкость задаётся на каждый playOneShot */ }

function applyMusicGain(): void {
  menuBgMusic.volume = musicVolume01;
  gameBgMusic.volume = musicVolume01;
}

/** Синхронизировать ползунки с переменными (открытие карточки / загрузка). */
function syncGameSettingsSliders(): void {
  gsSoundRange.value = String(Math.round(soundVolume01 * 100));
  gsSoundValue.textContent = gsSoundRange.value;
  gsMusicRange.value = String(Math.round(musicVolume01 * 100));
  gsMusicValue.textContent = gsMusicRange.value;
  gsBrightRange.value = String(Math.round(brightness01 * 100));
  gsBrightValue.textContent = gsBrightRange.value;
  gsPostProcess.checked = renderer.getPostProcessEnabled();
  gsBloom.checked = renderer.getBloomUiEnabled();
}

applySfxLevels();
applyMusicGain();

/** Сброс игрового трека при выходе из матча. */
function stopGameMusicHard(): void {
  try {
    gameBgMusic.pause();
    gameBgMusic.currentTime = 0;
  } catch { /* ignore */ }
}

/** Меню открыто → Ashen; матч (оверлей скрыт) → Obsidian. */
function updateAmbientMusic(): void {
  applyMusicGain();
  // Заставка играет свою музыку — меню и матч молчат, пока она на экране.
  if (introBlocksMusic()) {
    try { menuBgMusic.pause(); } catch { /* ignore */ }
    try { gameBgMusic.pause(); } catch { /* ignore */ }
    return;
  }
  if (musicVolume01 < 0.001) {
    try { menuBgMusic.pause(); } catch { /* ignore */ }
    try { gameBgMusic.pause(); } catch { /* ignore */ }
    return;
  }

  const menuOpen = !overlay.classList.contains("hidden");
  const inGame =
    transport !== null && myId >= 0 && gameplayLive;

  if (menuOpen) {
    try { gameBgMusic.pause(); } catch { /* ignore */ }
    void menuBgMusic.play().catch(() => { /* autoplay / decode */ });
    return;
  }

  if (inGame) {
    try { menuBgMusic.pause(); } catch { /* ignore */ }
    void gameBgMusic.play().catch(() => { /* autoplay / decode */ });
    return;
  }

  try { menuBgMusic.pause(); } catch { /* ignore */ }
  try { gameBgMusic.pause(); } catch { /* ignore */ }
}

/** Жёсткий стоп — рестарт игры / вне сессии. */
function killFireSound(): void { /* one-shot'ы сами завершаются */ }


const NPC_SHOT_MAX_DIST = 25;
const NPC_SHOT_FULL_DIST = 4;
const NPC_SHOT_VOL_FULL = 0.55;





function onNpcShotSound(_npcId: number, ox: number, oy: number, _oz: number): void {
  const dx = ox - predicted.x;
  const dy = oy - predicted.y;
  const dist = Math.hypot(dx, dy);
  if (dist > NPC_SHOT_MAX_DIST) {
    return;
  }
  const t = Math.max(0, Math.min(1,
    (NPC_SHOT_MAX_DIST - dist) / (NPC_SHOT_MAX_DIST - NPC_SHOT_FULL_DIST)));
  const volDist = NPC_SHOT_VOL_FULL * t * soundVolume01;
  if (volDist < 0.02) {
    return;
  }
  playOneShot(SFX_FIRE, volDist);
}

function stopNpcFireForRemovedInstance(_npcId?: number): void { /* one-shot'ы сами завершаются */ }

function killNpcShotSounds(): void { /* one-shot'ы сами завершаются */ }

/** Базовые анимации с шагами (как MOVE_ANIMS + crouchWalk). */
const FOOTSTEP_BASE_ANIMS = new Set([
  "run", "walk", "runLeft", "runRight", "runForwardLeft", "runForwardRight",
  "crouchWalk",
]);
const FOOTSTEP_MAX_DIST = 22;
const FOOTSTEP_FULL_DIST = 2.8;
const FOOTSTEP_VOL_WORLD_FULL = 0.38;

/** Плавное нарастание / более медленное затухание при остановке ходьбы (сек). */
const FOOTSTEP_FADE_IN_TAU = 0.038;
const FOOTSTEP_FADE_OUT_TAU = 0.072;

let localFootstepAmp = 0;
const remoteFootstepLoops = new Map<number, SfxLoop>();
const remoteFootstepAmp = new Map<number, number>();
const npcFootstepLoops = new Map<number, SfxLoop>();
const npcFootstepAmp = new Map<number, number>();

function smoothFootstepAmp(current: number, target: number, dtSec: number): number {
  const tau = target > current ? FOOTSTEP_FADE_IN_TAU : FOOTSTEP_FADE_OUT_TAU;
  const alpha = 1 - Math.exp(-dtSec / tau);
  return current + (target - current) * alpha;
}

/** Лёгкая модуляция громкости и playbackRate от скорости. */
function footstepSpeedGain(speed: number): number {
  return clamp(speed / 3.4, 0.52, 1.12);
}

function footstepPlaybackRate(speed: number): number {
  return clamp(0.88 + speed * 0.048, 0.82, 1.42);
}

function footstepVolFromDist(ox: number, oy: number): number {
  const dist = Math.hypot(ox, oy);
  if (dist > FOOTSTEP_MAX_DIST) return 0;
  const edge = Math.max(1e-6, FOOTSTEP_MAX_DIST - FOOTSTEP_FULL_DIST);
  const u = Math.max(0, Math.min(1, (FOOTSTEP_MAX_DIST - dist) / edge));
  return FOOTSTEP_VOL_WORLD_FULL * u * soundVolume01;
}

function updateLocalFootstepLoop(active: boolean, speed: number, dtSec: number): void {
  if (NO_FOOTSTEP_AUDIO) return;
  const peak = FOOTSTEP_VOL_LOCAL * soundVolume01 * (active ? footstepSpeedGain(speed) : 0);
  localFootstepAmp = smoothFootstepAmp(localFootstepAmp, peak, dtSec);
  if (localFootstepAmp <= 0.008) localFootstepAmp = 0;
  runFootstepLoop.set(localFootstepAmp, footstepPlaybackRate(speed));
}

function updateWorldFootstepLoop(
  loops: Map<number, SfxLoop>,
  amps: Map<number, number>,
  id: number,
  active: boolean,
  speed: number,
  dx: number,
  dy: number,
  dtSec: number,
): void {
  if (NO_FOOTSTEP_AUDIO) return;
  const distVol = footstepVolFromDist(dx, dy);
  const peak = active ? distVol * footstepSpeedGain(speed) : 0;
  let amp = amps.get(id) ?? 0;
  amp = smoothFootstepAmp(amp, peak, dtSec);
  amps.set(id, amp);

  if (peak < 0.001 && amp < 0.008) {
    loops.get(id)?.stop();
    loops.delete(id);
    amps.delete(id);
    return;
  }

  let a = loops.get(id);
  if (!a) { a = new SfxLoop(SFX_RUN); loops.set(id, a); }
  a.set(amp, footstepPlaybackRate(speed));
}

function updatePlayerFootstepSound(
  id: number,
  inst: MeshInstance,
  speed: number,
  vz: number,
  inJump: boolean,
  dtSec: number,
): void {
  if (!gameplayLive || transport === null || myId < 0) return;
  if (id === myId && gameMenuPaused()) {
    updateLocalFootstepLoop(false, 0, dtSec);
    return;
  }
  const movingAnim = FOOTSTEP_BASE_ANIMS.has(inst.state.baseAnim);
  const grounded = id === myId ? predicted.grounded : Math.abs(vz) < 0.22;
  const active = movingAnim && !inJump && grounded;

  if (id === myId) {
    updateLocalFootstepLoop(active, speed, dtSec);
    return;
  }
  updateWorldFootstepLoop(
    remoteFootstepLoops, remoteFootstepAmp, id,
    active, speed, inst.x - predicted.x, inst.y - predicted.y, dtSec,
  );
}

function updateNpcFootstepSound(npcId: number, npc: Npc, dtSec: number): void {
  if (!gameplayLive || transport === null || myId < 0) return;
  const ba = npc.inst.state.baseAnim;
  const speed = npc.footstepHorizSpeed();
  const active =
    npc.state === NPC_STATE_ALIVE &&
    (ba === "run" || ba === "walk") &&
    speed >= 0.35;
  updateWorldFootstepLoop(
    npcFootstepLoops, npcFootstepAmp, npcId,
    active, speed, npc.inst.x - predicted.x, npc.inst.y - predicted.y, dtSec,
  );
}

function releaseRemotePlayerFootstep(id: number): void {
  remoteFootstepLoops.get(id)?.stop();
  remoteFootstepLoops.delete(id);
  remoteFootstepAmp.delete(id);
}

function releaseNpcFootstep(id: number): void {
  npcFootstepLoops.get(id)?.stop();
  npcFootstepLoops.delete(id);
  npcFootstepAmp.delete(id);
}

function hardStopAllFootstepLoops(): void {
  runFootstepLoop.stop();
  localFootstepAmp = 0;
  for (const a of remoteFootstepLoops.values()) a.stop();
  remoteFootstepLoops.clear();
  remoteFootstepAmp.clear();
  for (const a of npcFootstepLoops.values()) a.stop();
  npcFootstepLoops.clear();
  npcFootstepAmp.clear();
}

gsSoundRange.addEventListener("input", () => {
  soundVolume01 = Math.max(0, Math.min(1, parseInt(gsSoundRange.value, 10) / 100));
  gsSoundValue.textContent = gsSoundRange.value;
  saveAudioSettings();
  applySfxLevels();
});

gsBrightRange.addEventListener("input", () => {
  brightness01 = Math.max(0.5, Math.min(2, parseInt(gsBrightRange.value, 10) / 100));
  gsBrightValue.textContent = gsBrightRange.value;
  renderer.setBrightness(brightness01);
  try { localStorage.setItem(LS_BRIGHTNESS, String(brightness01)); } catch { /* ignore */ }
});

gsMusicRange.addEventListener("input", () => {
  musicVolume01 = Math.max(0, Math.min(1, parseInt(gsMusicRange.value, 10) / 100));
  gsMusicValue.textContent = gsMusicRange.value;
  saveAudioSettings();
  applyMusicGain();
  updateAmbientMusic();
});

function applyPostProcessFromCheckbox(): void {
  if (mobileLiteGraphics) {
    gsPostProcess.checked = false;
    renderer.setPostProcessEnabled(false);
    return;
  }
  renderer.setPostProcessEnabled(gsPostProcess.checked);
  try {
    localStorage.setItem(LS_POST_PROCESS, gsPostProcess.checked ? "1" : "0");
  } catch { /* ignore */ }
}

function applyBloomFromCheckbox(): void {
  if (mobileLiteGraphics) {
    gsBloom.checked = false;
    renderer.setBloomUiEnabled(false);
    return;
  }
  renderer.setBloomUiEnabled(gsBloom.checked);
  try {
    localStorage.setItem(LS_BLOOM, gsBloom.checked ? "1" : "0");
  } catch { /* ignore */ }
}
gsPostProcess.addEventListener("change", applyPostProcessFromCheckbox);
/** iOS / часть WebKit: для checkbox иногда надёжнее input. */
gsPostProcess.addEventListener("input", applyPostProcessFromCheckbox);

gsBloom.addEventListener("change", applyBloomFromCheckbox);
gsBloom.addEventListener("input", applyBloomFromCheckbox);

syncGameSettingsSliders();

const writer = new Writer(64);
/** Один Reader на все WS-сообщения — иначе десятки аллокаций/сек на длинной сессии. */
const netReader = new Reader(new Uint8Array(0));

function updateTouchGameActive(): void {
  if (!touchUi) return;
  const inGame = transport !== null && myId >= 0 && gameplayLive;
  const overlayHidden = overlay.classList.contains("hidden");
  const show = inGame && overlayHidden;
  document.body.classList.toggle("touch-game-active", show);
  touchControlsEl.setAttribute("aria-hidden", show ? "false" : "true");
  if (!show) {
    input.clearTouchState();
    touchCtl?.reset(); // спрятать джойстик, отпустить удержания
  }
}

function resetState(): void {
  myId = -1;
  yawInitialized = false;
  nextSeq = 1;
  // Сброс tick accumulator: при повторном входе накопленные мс вызывали
  // пачку fixedTick() сразу, персонаж «телепортировался», input замерзал.
  acc = 0;
  last = performance.now();
  predicted.x = 2.5;
  predicted.y = 2.5;
  predicted.z = 0;
  predicted.vx = 0;
  predicted.vy = 0;
  predicted.vz = 0;
  predicted.grounded = true;
  predicted.crouching = false;
  predicted.yaw = 0;
  predicted.pitch = 0;
  prevButtons = 0;
  eyeZSmoothed = 1.6;
  syncRenderInterpFromPredicted();
  pendingReconcileX = 0;
  pendingReconcileY = 0;
  pendingReconcileZ = 0;
  scores.clear();
  nicknames.clear();
  killfeed.length = 0;
  chunks.clear();
  levelStaticMeshInst = null;
  levelStaticMeshSceneId = null;
  renderer.hideStaticVoxels = false;
  interactHotspotsHello.length = 0;
  setMansionDoorGhostChunks(null);
  setMansionDoorMeshGhost(false);
  setMeshCollision(null);
  if (mansionDoorGhostClearTimer !== null) {
    clearTimeout(mansionDoorGhostClearTimer);
    mansionDoorGhostClearTimer = null;
  }
  sceneStaticLights.length = 0;
  fallingGroups.clear();
  damagedTiles.clear();
  snapBuf.clear();
  prediction.clear();
  renderer.clearMeshInstances();
  pickupItemInst.clear();
  pickupItemVisualTag.clear();
  playerInstances.clear();
  playerLastHp.clear();
  playerLastHitAt.clear();
  // NPC mesh-инстансы уничтожены clearMeshInstances(); Map сбрасываем,
  // новые объекты появятся на первом snapshot'е от сервера.
  npcs.clear();
  npcLastShotAt.clear();
  npcHpBarEl.classList.add("hidden");
  damageFlashEl.classList.remove("flash");
  damageFlashEl.classList.remove("active");
  damageFlashActive = false;
  if (damageFlashTimer !== null) { clearTimeout(damageFlashTimer); damageFlashTimer = null; }
  if (damageFlashHideTimer !== null) { clearTimeout(damageFlashHideTimer); damageFlashHideTimer = null; }
  // Сброс HUD-кэшей чтобы при рестарте DOM гарантированно обновился.
  lastHpRendered = -1;
  lastAmmoHudKey = "";
  lastHudUpdateMs = 0;
  lastScoreboardHash = "";
  lastCrosshairPx = -1; lastCrosshairPy = -1; lastCrosshairHidden = false;
  lastNpcHpBarHidden = true; lastNpcHpBarPx = -1; lastNpcHpBarPy = -1; lastNpcHpBarPct = -1;
  lastKillfeedHtml = "";
  resetMatchState();
  prevPlayerPos.clear();
  playerSpeedSmoothed.clear();
  remoteShootUntil.clear();
  remoteZVel.clear();
  jumpState.clear();
  stopTransition.clear();
  baseAnimOverride = null;
  upperAnimOverride = null;
  // Страховка: one-shot выстрел не должен «висеть» между сессиями.
  killFireSound();
  killNpcShotSounds();
  muzzleFlashes.length = 0;
  hardStopAllFootstepLoops();
  stopGameMusicHard();
  localInventory.hp = 100;
  predictedAmmoRounds = MAX_WEAPON_AMMO;
  weapons.reset(); // weapons
  artifacts.reset(); // artifacts
  story.reset(); // story
  if (_fpFireVideoEl) {
    try {
      _fpFireVideoEl.pause();
      _fpFireVideoEl.currentTime = 0;
    } catch { /* */ }
    _fpFireVideoEl.classList.add("hidden");
  }
  fpWeaponSuppressedForMuzzleFlash = false;
  fpWeaponOverlayWalk = false;
  fpWeaponWalkBlend = 0;
  for (const el of [_fpWeaponIdleVideoEl, _fpWeaponWalkVideoEl]) {
    if (!el) continue;
    try {
      el.pause();
      el.currentTime = 0;
    } catch { /* */ }
    el.style.opacity = "";
    el.classList.add("hidden");
  }
  gameplayLive = false;
  pauseResumeRow.classList.add("hidden");
  updateTouchGameActive();
}

let texturesReady = false;
async function start(): Promise<void> {
  // Защита от двойного клика / повторного вызова пока предыдущий start ещё идёт.
  if (startLock) return;
  startLock = true;

  // Убиваем polling DM-списка если вдруг остался.
  if (dmListPollTimer !== null) { clearInterval(dmListPollTimer); dmListPollTimer = null; }

  // Закрываем предыдущую сессию, если есть.
  if (transport) {
    transport.close();
    transport = null;
  }
  resetState();

  await refreshAuth();
  if (!canStartFromAuth(authInfo)) {
    applyAuthToUI();
    startLock = false;
    return;
  }
  if (authInfo && authInfo.ok && !authInfo.guest) {
    myNick = authInfo.nick || authInfo.displayName || `p${authInfo.userId}`;
  } else {
    myNick = `player-${Math.floor(Math.random() * 9000 + 1000)}`;
  }
  // Дефолт: SP на arena1 с 3 NPC если pendingRoom не задан (backward
  // compatibility, кнопка "RESTART" тогда тоже работает).
  const settings: RoomSettings = pendingRoom ?? {
    mode: "single",
    sceneId: "arena1",
    npcCount: 3,
  };

  // Префетч тяжёлых моделей солдата ПАРАЛЛЕЛЬНО с аудио/текстурами/чанками —
  // раньше они качались строго последовательно в хвосте загрузки.
  /** `realBody=1` — эксперимент: отдельный примитив на куб (soldier_animated_real_vox.glb). По умолчанию — псевдо-воксель через шейдер. */
  const wantRealVoxelCubes =
    new URLSearchParams(location.search).get("realBody") === "1" ||
    localStorage.getItem("realBody") === "1";
  void loadMeshCached("/models/soldier.glb").catch(() => {});
  void loadMeshCached(
    wantRealVoxelCubes ? "/models/soldier_animated_real_vox.glb" : "/models/soldier_animated_vox.glb",
  ).catch(() => {});
  // characters: модель выбранного персонажа — тоже заранее и параллельно.
  resetCharacterSession();
  if (getSelectedCharId() !== 0) void ensureCharacterVisual(renderer, loadMeshCached, getSelectedCharId());

  // Разлочиваем HTMLAudioElement под autoplay policy: браузер требует
  // user-gesture перед первым .play(). `start()` вызывается из клика по
  // PLAY-кнопке — тут "прогреваем" аудио (mute/play/pause), после этого
  // обычные playFireSound() во время игры не будут блокироваться.
  // Прогреваем выстрел и оба музыкальных трека (autoplay).
  const warmUp = async (a: HTMLAudioElement): Promise<void> => {
    const v = a.volume;
    a.volume = 0;
    try {
      await a.play();
      a.pause();
      a.currentTime = 0;
    } catch { /* autoplay blocked */ }
    a.volume = v;
  };
  // Все прогревы параллельно: каждый await последовательно ждал буферизацию.
  await Promise.all([
    loadSfx(SFX_FIRE),
    loadSfx(SFX_OUT_OF_AMMO),
    loadSfx(SFX_RUN),
    warmUp(menuBgMusic),
    warmUp(gameBgMusic),
  ]);
  applySfxLevels();
  applyMusicGain();

  setLoader(true, "Loading textures…", 0);

  // Текстуры грузим один раз за сессию страницы: атласы тяжёлые (canvas + GL),
  // повторная загрузка при каждом RESTART крашит GPU-память.
  if (!texturesReady) {
    // Материальная palette: 9 диффуз + 9 depth → два 9×512 атласа на GPU.
    const diffuseNames = PALETTE_MATERIAL_ORDER;
    const depthNames = diffuseNames.map((n) => `${n}_depth`);
    setLoader(true, "Loading textures… (palette)", 0.1);
    const paletteDiffuse = await buildMaterialAtlas(diffuseNames, "jpg");
    setLoader(true, "Loading textures… (depth)", 0.25);
    const paletteDepth = await buildMaterialAtlas(depthNames, "png");
    renderer.uploadMaterialAtlases(paletteDiffuse, paletteDepth);
    // Object diffuse (chair/soldier)
    for (const n of ["chair_diffuse", "soldier_diffuse"]) {
      try { await loadTexture(n, "jpg"); } catch (err) {
        console.warn(`[textures] ${n}.jpg not loaded:`, err);
      }
    }
    renderer.uploadObjectTextures();
    texturesReady = true;
  }
  setLoader(true, "Loading textures…", 0.4);

  // Сначала чанки HTTP, потом WS: `restart=1` пересоздаёт SP-комнату и мир; если
  // после этого открыть /api/ws без restart, EnsureSolo подключится к уже свежей
  // комнате. Раньше были WS→HTTP и двойной restart (ломало attach) или HTTP без
  // restart (гонка с битым миром из старой комнаты).
  setLoader(true, "Loading world…", 0.5);
  try {
    await fetchAndLoadChunks(settings);
  } catch (e) {
    console.warn("chunks load:", e);
  }

  // Сначала меш солдата, потом WS: пока игрок не в комнате, NPC на сервере
  // не выбирают цель; плюс syncPlayerInstances не создаёт инстанс с null inner.
  setLoader(true, "Loading soldier model…", 0.55);
  try {
    // Нельзя выставлять soldierMesh до готовности soldier_vox: между двумя await
    // кадр syncPlayerInstances мог создать инстанс с innerMesh=null → навсегда гладкий меш.
    const mainMesh = await loadMeshCached("/models/soldier.glb");
    let voxMesh: LoadedMesh | null = null;
    useRealVoxelBody = false;
    soldierRealVoxCentroids = null;
    soldierRealVoxStableCells = null;
    const tryLoadInner = async (url: string): Promise<LoadedMesh | null> => {
      try {
        const m = await loadMeshCached(url);
        if (!canSkinInnerToOuterPose(mainMesh, m)) {
          console.warn(`[soldier] ${url}: joints mismatch outer soldier.glb`);
          return null;
        }
        return m;
      } catch {
        return null;
      }
    };
    try {
      if (wantRealVoxelCubes) {
        voxMesh = await tryLoadInner("/models/soldier_animated_real_vox.glb");
        if (voxMesh) {
          useRealVoxelBody = true;
          soldierRealVoxCentroids = precomputeInnerSubmeshCentroids(voxMesh);
          console.log("[soldier] real-voxel prototype: per-mesh cubes (?realBody=1)");
        }
      }
      if (!voxMesh) {
        voxMesh = await tryLoadInner("/models/soldier_animated_vox.glb");
      }
      if (!voxMesh) {
        voxMesh = await tryLoadInner("/models/soldier_vox.glb");
      }
      if (voxMesh && !useRealVoxelBody) {
        console.log("[soldier] pseudo-voxel inner OK (shader bitmap + limb bones)");
      }
    } catch (e) {
      console.warn("[soldier] inner body mesh load failed:", e);
      voxMesh = null;
    }
    soldierMesh = mainMesh;
    soldierVoxMesh = voxMesh;
    initBodyVoxLimbDetach(soldierVoxMesh ?? mainMesh);
    if (!soldierVoxMesh) {
      console.warn(
        "[soldier] Внутреннее тело OFF → только outer soldier.glb. " +
          "Проверьте /models/soldier_animated_vox.glb или soldier_vox.glb и совпадение скелета с soldier.glb.",
      );
    }
    // ВАЖНО: считаем bbox по РЕАЛЬНОЙ позе скининга (idle@t=0), а не по
    // bind/T-позе. У Mixamo idle ноги стоят на Y≈0, а в bind на Y≈-0.95 —
    // если использовать bind-bbox, `floorOffset` сдвинет солдата на ~0.9м
    // вверх, и он будет висеть в воздухе вместо того чтобы стоять на полу.
    const probe = renderer.createMeshInstance({
      mesh: mainMesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle",
    });
    const liveBbox = renderer.computeSkinnedBboxAtCurrentPose(probe);
    const meshHeight = liveBbox.max[1] - liveBbox.min[1];
    const desiredHeight = 1.8;
    soldierScale = meshHeight > 0 ? desiredHeight / meshHeight : 1.0;
    const FLOOR_TOP_Z = 0.0625;
    soldierFloorOffset = -liveBbox.min[1] * soldierScale + FLOOR_TOP_Z;
    soldierRealVoxStableCells = null;
    if (useRealVoxelBody && soldierVoxMesh && soldierRealVoxCentroids) {
      soldierRealVoxStableCells = buildRealVoxelStableCellMap(
        soldierVoxMesh,
        soldierRealVoxCentroids,
        {
          x: 0,
          y: 0,
          z: soldierFloorOffset,
          yaw: 0,
          yawOffset: SOLDIER_YAW_OFFSET,
          scale: soldierScale,
        },
      );
      let rvMapped = 0;
      let rvOutside = 0;
      for (let i = 0; i < soldierRealVoxStableCells.length; i++) {
        if (soldierRealVoxStableCells[i]! < 0) rvOutside++;
        else rvMapped++;
      }
      console.log(
        `[soldier] realVox cell map: ${rvMapped} submeshes → grid, ${rvOutside} si→−1 (outside 10×10×24 body grid — скрытие/отрыв не работают для них)`,
      );
    }
    if (mainMesh.skin) {
      // FP viewmodel: оставляем ВИДИМЫМИ только поддеревья плеч (руки),
      // всё остальное (голова, шея, торс, ноги) обнуляем.
      const visibleArmJoints = findJointSubtreeIndices(
        mainMesh.nodes, mainMesh.skin.joints, FP_ARM_ROOT_BONE_NAMES,
      );
      const keep = new Set(visibleArmJoints);
      fpHiddenJoints = [];
      for (let j = 0; j < mainMesh.skin.joints.length; j++) {
        if (!keep.has(j)) fpHiddenJoints.push(j);
      }
      // void-ссылка чтобы IDE не ругалась на неиспользованный импорт,
      // который всё ещё нужен кому-то в будущем (legacy head+neck список).
      void FP_HIDDEN_BONE_NAMES;
    }
    // characters: солдат — персонаж 0 (его масштаб/оффсет уже посчитаны выше).
    registerCharacterVisual({
      charId: 0, mesh: mainMesh, inner: soldierVoxMesh, scale: soldierScale, floorOffset: soldierFloorOffset,
    });
    console.log(
      `[soldier] mesh loaded (scale=${soldierScale.toFixed(3)}, ` +
      `floorOffset=${soldierFloorOffset.toFixed(3)}, ` +
      `liveBbox Y=[${liveBbox.min[1].toFixed(3)}, ${liveBbox.max[1].toFixed(3)}], ` +
      `fpHiddenJoints=${fpHiddenJoints.length})`,
    );
  } catch (err) {
    console.warn("[soldier] mesh load failed:", err);
    soldierMesh = null;
    soldierVoxMesh = null;
    soldierRealVoxCentroids = null;
    soldierRealVoxStableCells = null;
    useRealVoxelBody = false;
  }
  if (!soldierMesh) {
    showStartFailure(START_ERR_MODEL);
    startLock = false;
    return;
  }
  // NPC'ы спавнятся сервером (видны через Snapshot.npcs) — см. syncNpcs.
  void NPC_MAX_HP;
  // characters: свой персонаж — до входа в комнату (иначе первые кадры солдат).
  if (getSelectedCharId() !== 0) {
    setLoader(true, "Loading character…", 0.78);
    await ensureCharacterVisual(renderer, loadMeshCached, getSelectedCharId());
  }

  setLoader(true, "Connecting to server…", 0.82);
  netStatus = "connecting...";
  const wsScheme = location.protocol === "https:" ? "wss:" : "ws:";
  const wsParams = new URLSearchParams();
  if (settings.mode === "single") {
    if (settings.sceneId) wsParams.set("scene", settings.sceneId);
    if (settings.npcCount !== undefined) wsParams.set("npc", String(settings.npcCount));
    if (settings.story) wsParams.set("mode", "story"); // story
  } else if (settings.roomId) {
    wsParams.set("room", settings.roomId);
  }
  appendCharacterWsParams(wsParams, !(authInfo && authInfo.ok && !authInfo.guest)); // characters
  const wsUrl = `${wsScheme}//${location.host}/api/ws${wsParams.toString() ? "?" + wsParams.toString() : ""}`;
  currentRoomId = settings.roomId ?? "";
  transport = new WebSocketTransport(wsUrl, {
    onOpen: () => { netStatus = "connected"; },
    onClose: (r) => { netStatus = `closed: ${r}`; },
    onMessage: handleMessage,
  });

  try {
    await transport.connect(myNick);
  } catch (e) {
    console.warn("connection issue:", e);
    transport?.close();
    transport = null;
    showStartFailure(START_ERR_WS);
    startLock = false;
    return;
  }
  if (!transport.isOpen()) {
    transport?.close();
    transport = null;
    showStartFailure(START_ERR_WS);
    startLock = false;
    return;
  }

  setLoader(true, t("Синхронизация с сервером…"), 0.88);
  try {
    await waitForMyId(8000);
  } catch (e) {
    console.warn("handshake wait:", e);
    const msg =
      e instanceof Error && e.message === "WS_CLOSED"
        ? START_ERR_WS_CLOSED
        : START_ERR_HANDSHAKE;
    transport?.close();
    transport = null;
    showStartFailure(msg);
    startLock = false;
    return;
  }

  setLoader(true, "Ready", 1.0);
  track("game_start", { mode: settings.mode === "single" ? (settings.story ? "story" : "single") : "dm" });
  // Короткая пауза чтобы юзер увидел "Ready"
  await new Promise((r) => setTimeout(r, 150));
  setLoader(false);
  overlay.classList.add("hidden");
  if (!touchUi) input.requestLock();
  // Два rAF: первый кадр после снятия лоадера уходит на композитинг, второй —
  // ближе к моменту, когда пользователь реально видит кадр сцены.
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { resolve(); });
    });
  });
  gameplayLive = true;
  updateTouchGameActive();
  updateAmbientMusic();
  playLabel.textContent = "RESTART";
  playSub.textContent = "single player";
  pendingRoom = null;
  startLock = false;
}

/** Тянем все чанки одним HTTP-запросом и парсим в той же логике что и
 *  WebRTC-приём. Гораздо стабильнее WebRTC reliable channel для bulk static
 *  init: HTTP/TCP не страдает от SCTP таймаутов на больших payload'ах. */
async function loadLevelStaticMesh(sceneId: string | undefined): Promise<void> {
  if (!sceneId || sceneId === "arena1") {
    renderer.hideStaticVoxels = false;
    setTerrainHeightmap(null);
    levelStaticMeshSceneId = null;
    return;
  }
  // Идемпотентность: Hello вызывает `void loadLevelStaticMesh(h.sceneId)`
  // как fire-and-forget; fetchAndLoadChunks делает то же самое через `await`.
  // Без этой проверки Hello (приходит ПОСЛЕ chunks-loader'а) перезагружает
  // тот же 49-MiB GLB второй раз и временно показывает voxel-walls без
  // текстур пока reload не завершится.
  if (levelStaticMeshSceneId === sceneId && levelStaticMeshInst !== null) {
    renderer.hideStaticVoxels = true;
    return;
  }
  if (levelStaticMeshInst) {
    renderer.removeMeshInstance(levelStaticMeshInst);
    levelStaticMeshInst = null;
  }
  levelStaticMeshSceneId = null;
  renderer.hideStaticVoxels = false;
  setTerrainHeightmap(null);
  try {
    const manUrl = `${location.origin}/api/level-manifest?scene=${encodeURIComponent(sceneId)}`;
    const mr = await fetch(manUrl, { credentials: "include" });
    if (!mr.ok) return;
    const j = (await mr.json()) as {
      static_meshes?: string;
      terrain_collider?: string;
    };
    if (j.terrain_collider && typeof j.terrain_collider === "string") {
      try {
        const hm = await fetchTerrainHeightmap(location.origin, sceneId, j.terrain_collider);
        if (hm) {
          setTerrainHeightmap(hm);
          console.log(`[terrain] heightmap loaded: ${hm.w}×${hm.h} cells, ${hm.cellSize}m`);
        }
      } catch (e) {
        console.warn("[terrain] heightmap fetch failed:", e);
      }
    }
    const file = j.static_meshes;
    if (!file || typeof file !== "string") return;
    const assetUrl =
      `${location.origin}/api/level-asset?scene=${encodeURIComponent(sceneId)}` +
      `&file=${encodeURIComponent(file)}`;
    setLoader(true, "Loading static geometry…", 0.92);
    const mesh = await loadMeshCached(assetUrl);
    const doorClip = mesh.animations.get(MANSION_DOOR_CLIP);
    // Дверной клип: держим кадр 0 (закрыто) и baseSpeed=0 — иначе при «idle» в GLB
    // или лупящемся idle дверь может крутиться до взаимодействия.
    levelStaticMeshInst = renderer.addMeshInstance(
      renderer.createMeshInstance({
        mesh,
        x: 0,
        y: 0,
        z: 0,
        yaw: 0,
        scale: 1,
        initialAnim: doorClip ? MANSION_DOOR_CLIP : "idle",
      }),
    );
    if (doorClip && levelStaticMeshInst) {
      levelStaticMeshInst.state.baseTime = 0;
      levelStaticMeshInst.state.baseSpeed = 0;
    }
    interiorDoorRegistry = buildInteriorDoorRegistry(mesh);
    renderer.hideStaticVoxels = true;
    levelStaticMeshSceneId = sceneId;
  } catch (e) {
    console.warn("[level] static mesh:", e);
    levelStaticMeshSceneId = null;
    renderer.hideStaticVoxels = false;
  }
}

async function fetchAndLoadChunks(settings: RoomSettings): Promise<void> {
  const t0 = performance.now();
  // Статик-меш уровня (крупный GLB) качаем ПАРАЛЛЕЛЬНО с чанками и
  // левелпак-текстурами: ему нужен только sceneId. Раньше он ждал весь
  // пайплайн и добавлял свой вес строго в хвост загрузки.
  const staticMeshPromise = loadLevelStaticMesh(settings.sceneId ?? currentSceneId);
  setLoader(true, "Downloading chunks…", 0.62);
  const params = new URLSearchParams();
  if (settings.mode === "single") {
    if (settings.sceneId) params.set("scene", settings.sceneId);
    if (settings.npcCount !== undefined) params.set("npc", String(settings.npcCount));
    if (settings.restart) params.set("restart", "1");
    if (settings.story) params.set("mode", "story"); // story
  } else if (settings.roomId) {
    params.set("room", settings.roomId);
  } else if (currentRoomId) {
    params.set("room", currentRoomId);
  }
  const url = `${location.origin}/api/chunks${params.toString() ? "?" + params.toString() : ""}`;
  const resp = await fetch(url, { credentials: "include" });
  if (!resp.ok) throw new Error(`/api/chunks: HTTP ${resp.status}`);
  const ab = await resp.arrayBuffer();
  const r = new Reader(new Uint8Array(ab));
  const numChunks = r.u32();
  console.log(`[net] /api/chunks: ${ab.byteLength}B, ${numChunks} chunks (${(performance.now() - t0).toFixed(0)}ms)`);

  // Декомпрессия каждого чанка асинхронна (gzip alive/uvs/faceUVs).
  // Запускаем ВСЕ в параллель и ждём all.
  const promises: Promise<void>[] = [];
  for (let i = 0; i < numChunks; i++) {
    const t = r.u8();
    if (t !== MsgType.ChunkInit) {
      console.error(`[net] expected ChunkInit at #${i}, got msgType=${t}`);
      break;
    }
    const raw = readChunkInit(r);
    promises.push(
      decompressChunkInit(raw).then((init) => {
        chunks.upsert(init);
        if (init.faceUVs) {
          let aliveCount = 0;
          for (let j = 0; j < init.alive.length; j++) {
            let v = init.alive[j];
            while (v) { aliveCount++; v &= v - 1; }
          }
          renderer.uploadFaceUVs(init.chunkId, init.faceUVs, aliveCount);
        }
      }),
    );
    setLoader(true, `Loading world… (${i + 1}/${numChunks})`,
      0.62 + ((i + 1) / numChunks) * 0.33);
  }
  await Promise.all(promises);
  const levelpackTex = new Set<string>();
  const levelpackNormals = new Set<string>();
  for (const ch of chunks.all()) {
    if (ch.textureName.startsWith("levelpacks/")) levelpackTex.add(ch.textureName);
    if (ch.normalTexture && ch.normalTexture.startsWith("levelpacks/")) {
      levelpackNormals.add(ch.normalTexture);
    }
  }
  const allLevelpackTex = new Set<string>([...levelpackTex, ...levelpackNormals]);
  if (allLevelpackTex.size > 0) {
    setLoader(true, `Loading textures… (level-pack ×${allLevelpackTex.size})`, 0.95);
    let done = 0;
    await Promise.all(
      Array.from(allLevelpackTex).map(async (name) => {
        try {
          const ext = /\.jpe?g$/i.test(name) ? "jpg" : "png";
          await loadTexture(name, ext);
        } catch (err) {
          console.warn(`[textures] level-pack ${name}:`, err);
        } finally {
          done++;
          setLoader(true, `Loading textures… (${done}/${allLevelpackTex.size})`, 0.95);
        }
      }),
    );
    renderer.uploadLevelpackObjectTextures(levelpackTex);
    if (levelpackNormals.size > 0) {
      renderer.uploadLevelpackNormalTextures(levelpackNormals);
    }
  }
  setMeshCollision(null);
  colliderDebugMesh = null;
  let levelGridManifest: LevelGridManifestBounds | undefined;
  if (settings.sceneId && settings.sceneId !== "arena1") {
    try {
      const mr = await fetch(
        `${location.origin}/api/level-manifest?scene=${encodeURIComponent(settings.sceneId)}`,
        { credentials: "include" },
      );
      if (mr.ok) {
        const j = (await mr.json()) as {
          bounds?: [[number, number, number], [number, number, number]];
          grid_cell_size?: number;
          mesh_collision?: MeshCollisionEntry[];
        };
        const mc = Array.isArray(j.mesh_collision) ? j.mesh_collision : null;
        setMeshCollision(mc);
        // static_meshes.glb грузится ПАРАЛЛЕЛЬНО с манифестом: если он успел
        // раньше, реестр дверей построился по пустому списку баз (гонка —
        // «двери перестали открываться»). Перестраиваем по факту обеих загрузок.
        if (levelStaticMeshInst) {
          interiorDoorRegistry = buildInteriorDoorRegistry(levelStaticMeshInst.mesh);
        }
        colliderDebugMesh = mc;
        if (j.bounds && j.bounds.length === 2) {
          levelGridManifest = {
            bounds: j.bounds,
            gridCellSize:
              typeof j.grid_cell_size === "number" && j.grid_cell_size > 0 ? j.grid_cell_size : 1,
            meshCollision: mc ?? undefined,
          };
        }
      }
    } catch {
      /* fallback: hull чанков в pickClientGrid */
    }
  }
  map = pickClientGrid(settings.sceneId, chunks, levelGridManifest);
  // VoxVisual чанки (vox_*-меши) часто имеют крупный cube_size (например
  // vox25_wall_garden = 0.25 м), и геометрия лабиринта внутри 1м-tile'а
  // GridMap'а не bin-aligned. Fast-path в playerCollidesWalls считает любую
  // wall-tile полностью непроходимой 1×1м-AABB → проходы шириной < 1м
  // блокируются виртуальной стеной. Помечаем их tiles damaged, чтобы
  // moveAndSlideVoxel сразу шёл в slow-path (точная voxel-проверка).
  let voxVisualTiles = 0;
  for (const ch of chunks.all()) {
    if (!ch.voxVisual) continue;
    const cs = ch.cubeSize;
    const x0 = ch.ox, y0 = ch.oy;
    const x1 = ch.ox + ch.sx * cs;
    const y1 = ch.oy + ch.sy * cs;
    const cellSize = map.cellSize ?? 1;
    const txMin = Math.floor((x0 - (map.originX ?? 0)) / cellSize);
    const tyMin = Math.floor((y0 - (map.originY ?? 0)) / cellSize);
    const txMax = Math.floor((x1 - 1e-3 - (map.originX ?? 0)) / cellSize);
    const tyMax = Math.floor((y1 - 1e-3 - (map.originY ?? 0)) / cellSize);
    for (let ty = tyMin; ty <= tyMax; ty++) {
      if (ty < 0 || ty >= map.height) continue;
      for (let tx = txMin; tx <= txMax; tx++) {
        if (tx < 0 || tx >= map.width) continue;
        const idx = tx + ty * map.width;
        if (!damagedTiles.has(idx)) {
          damagedTiles.add(idx);
          voxVisualTiles++;
        }
      }
    }
  }
  console.log(
    `[net] all ${numChunks} chunks loaded (${(performance.now() - t0).toFixed(0)}ms total); ` +
    `grid ${map.width}×${map.height} sceneId=${settings.sceneId ?? "?"}; ` +
    `voxVisual tiles forced to slow-path: ${voxVisualTiles}`,
  );
  await staticMeshPromise;
}

function showOverlay(): void {
  overlay.classList.remove("hidden");
  const canResume = transport !== null && myId >= 0 && gameplayLive;
  pauseResumeRow.classList.toggle("hidden", !canResume);
  if (canResume) showCard("menu");
  updateAmbientMusic();
  updateTouchGameActive();
  if (transport) {
    playLabel.textContent = "RESTART";
    playSub.textContent = "single player";
  }
  void refreshAuth();
}

resumeGameBtn.addEventListener("click", () => {
  if (!gameMenuPaused()) return;
  overlay.classList.add("hidden");
  showCard(null);
  if (!touchUi) input.requestLock();
  updateTouchGameActive();
  updateAmbientMusic();
});

/** Меню паузы на touch: нет ESC / pointer lock — явная кнопка. */
touchPauseBtn?.addEventListener("click", (ev) => {
  ev.preventDefault();
  ev.stopPropagation();
  showOverlay();
});

authLogout.addEventListener("click", async () => {
  try {
    await fetch(`${location.origin}/api/auth/logout`, { method: "POST", credentials: "include" });
  } catch {
    /* */
  }
  await refreshAuth();
});

authYandex.addEventListener("click", () => {
  location.assign(`${location.origin}/api/auth/yandex`);
});

authGoogle.addEventListener("click", () => {
  location.assign(`${location.origin}/api/auth/google`);
});

authEmailSend.addEventListener("click", async () => {
  const email = authEmail.value.trim();
  if (!email) {
    authStatus.textContent = t("Введите e-mail");
    authStatus.classList.add("warn");
    return;
  }
  authStatus.classList.remove("warn");
  const r = await fetch(`${location.origin}/api/auth/email/start`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, lang: getLang() }), // lang: язык письма (старый сервер поле игнорирует)
  });
  if (r.ok) {
    authStatus.textContent = t("Ссылка отправлена. Проверьте почту (ссылка действует 24ч).");
    authStatus.classList.remove("warn");
    authStatus.classList.add("ok");
  } else {
    const body = await r.text();
    authStatus.textContent = t("Не удалось отправить письмо ({status}): {text}", { status: r.status, text: body.slice(0, 200) });
    authStatus.classList.add("warn");
  }
});

authEmail.addEventListener("keydown", (e) => {
  if (e.key === "Enter") authEmailSend.click();
});

// (старый playBtn→start() удалён: теперь кнопка PLAY открывает SP settings,
// а уже оттуда `spStart` вызывает start(). См. showCard("spSettings").)

// ESC выводит pointerlock и показывает overlay с кнопкой RESTART.
// Также R на клавиатуре — мгновенный рестарт без необходимости лезть мышкой.
document.addEventListener("pointerlockchange", () => {
  // На touch-UI pointer lock не используется — событие не означает «игрок вышел из игры».
  if (touchUi) return;
  if (document.pointerLockElement !== canvas && transport) {
    showOverlay();
  }
});

window.addEventListener("keydown", (ev) => {
  if (ev.code === "KeyR" && document.pointerLockElement === canvas) {
    pendingRoom = story.isActive()
      ? { mode: "single", sceneId: STORY_SCENE_ID, npcCount: 0, restart: true, story: true } // story
      : {
        mode: "single",
        sceneId: currentSceneId,
        npcCount: parseInt(spNpcRange.value, 10) || 0,
        restart: true,
      };
    void start();
    return;
  }
  // Camera toggle FP↔TP — работает всегда, даже без pointer-lock (для отладки)
  if (ev.code === "KeyV") {
    if (localPlayerDead()) return;
    thirdPerson = !thirdPerson;
    console.log(`[camera] ${thirdPerson ? "third-person" : "first-person"}`);
    return;
  }
  if (ev.code === "F10") {
    ev.preventDefault();
    toggleProfilerOverlay();
    return;
  }
  if (ev.code === "F9") {
    ev.preventDefault();
    colliderDebugVisible = !colliderDebugVisible;
    syncColliderCanvasSize();
    console.log(`[debug] colliders ${colliderDebugVisible ? "ON" : "OFF"}`);
    return;
  }
  if (ev.code === "KeyL" && ev.ctrlKey) {
    const t = ev.target;
    if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) {
      return;
    }
    ev.preventDefault();
    gameDebugHudVisible = !gameDebugHudVisible;
    hud.classList.toggle("hidden", !gameDebugHudVisible);
    hud.setAttribute("aria-hidden", gameDebugHudVisible ? "false" : "true");
    lastHudUpdateMs = 0;
    return;
  }
  // POM: 8 → 4 → 0 → 8; render scale: 100% → 90% → 85% → 75% → 100%
  if (ev.code === "F6") {
    ev.preventDefault();
    const order: PomLayerOption[] = [8, 4, 0];
    const cur = renderer.getPomLayers();
    const i = order.indexOf(cur);
    const next = order[(i < 0 ? 0 : i + 1) % order.length];
    renderer.setPomLayers(next);
    try {
      localStorage.setItem(LS_POM_LAYERS, String(next));
    } catch { /* ignore */ }
    console.log(`[gfx] POM layers = ${next}`);
    return;
  }
  if (ev.code === "F7") {
    ev.preventDefault();
    const scales = [1, 0.9, 0.85, 0.75];
    const cur = renderer.getRenderScale();
    let idx = scales.findIndex((s) => Math.abs(s - cur) < 0.01);
    if (idx < 0) idx = 0;
    const next = scales[(idx + 1) % scales.length];
    renderer.setRenderScale(next);
    try {
      localStorage.setItem(LS_RENDER_SCALE, String(next));
    } catch { /* ignore */ }
    console.log(`[gfx] render scale = ${(next * 100).toFixed(0)}%`);
    return;
  }
  // Debug overrides анимаций (Ctrl+1..5, Ctrl+0 = сброс). На голых цифрах
  // жить нельзя: случайное нажатие «2» ставило override "walk", которого у
  // солдата нет, и модель падала в bind-позу (Т-поза) до сброса.
  if (!ev.ctrlKey) return;
  switch (ev.code) {
    case "Digit1": baseAnimOverride = "idle"; console.log("[anim] override base=idle"); break;
    case "Digit2": baseAnimOverride = "walk"; console.log("[anim] override base=walk"); break;
    case "Digit3": baseAnimOverride = "run"; console.log("[anim] override base=run"); break;
    case "Digit4":
      upperAnimOverride = upperAnimOverride === true ? null : true;
      console.log(`[anim] override upper=${upperAnimOverride === true ? "shoot" : "off"}`);
      break;
    case "Digit5": baseAnimOverride = "die"; console.log("[anim] override base=die"); break;
    case "Digit0":
      baseAnimOverride = null;
      upperAnimOverride = null;
      console.log("[anim] overrides cleared");
      break;
  }
});

/** Импульс «действие» для тач-интерфейса: сервер ловит фронт BtnUse
 *  (usePressed && !useWas), поэтому бит надо подержать несколько тиков
 *  по 16.7 мс и отпустить — мгновенный set/clear в одном кадре сервер
 *  может не увидеть. */
const TOUCH_USE_PULSE_MS = 120;
let touchUsePulseTimer: number | null = null;
function pulseTouchUse(): void {
  input.setTouchButton(Btn.Use, true);
  if (touchUsePulseTimer !== null) clearTimeout(touchUsePulseTimer);
  touchUsePulseTimer = window.setTimeout(() => {
    touchUsePulseTimer = null;
    input.setTouchButton(Btn.Use, false);
  }, TOUCH_USE_PULSE_MS);
}

/** Клиентская часть двери особняка: анимация GLB + ghost-коллизия по chunk id из Hello. */
function applyMansionDoorClient(phase: number): void {
  const spot = interactHotspotsHello.find((s) => s.kind === HelloInteractKindMansionDoor);
  if (!spot) return;
  const inst = levelStaticMeshInst;
  const clip = inst?.mesh.animations.get(MANSION_DOOR_CLIP);
  if (phase === 0) {
    if (mansionDoorGhostClearTimer !== null) {
      clearTimeout(mansionDoorGhostClearTimer);
      mansionDoorGhostClearTimer = null;
    }
    if (spot.chunkA !== 0 || spot.chunkB !== 0) {
      setMansionDoorMeshGhost(false);
      setMansionDoorGhostChunks(new Set([spot.chunkA, spot.chunkB]));
    } else {
      setMansionDoorGhostChunks(null);
      setMansionDoorMeshGhost(true);
    }
    if (inst && clip) {
      setBaseAnim(inst.state, MANSION_DOOR_CLIP, 0);
      inst.state.baseTime = 0;
      inst.state.baseSpeed = 1;
    }
    return;
  }
  if (phase === 1) {
    if (inst && clip) {
      setBaseAnim(inst.state, MANSION_DOOR_CLIP, 0);
      inst.state.baseTime = clip.duration;
      inst.state.baseSpeed = -1;
    }
    if (mansionDoorGhostClearTimer !== null) clearTimeout(mansionDoorGhostClearTimer);
    mansionDoorGhostClearTimer = window.setTimeout(() => {
      mansionDoorGhostClearTimer = null;
      setMansionDoorGhostChunks(null);
      setMansionDoorMeshGhost(false);
      if (inst && clip) {
        setBaseAnim(inst.state, MANSION_DOOR_CLIP, 0);
        inst.state.baseTime = 0;
        inst.state.baseSpeed = 0;
      }
    }, MANSION_DOOR_CLOSE_MS);
  }
}

function updateInteractHint(): void {
  const el = document.getElementById("interact-hint");
  if (!el) return;
  if (!gameplayLive || !gameInputActive() || myId < 0) {
    el.classList.add("hidden");
    el.setAttribute("aria-hidden", "true");
    return;
  }
  const spot = interactHotspotsHello.find((s) => s.kind === HelloInteractKindMansionDoor);
  const nearMesh =
    feetNearMansionDoorInteract(predicted.x, predicted.y, predicted.z) ||
    feetNearInteriorDoorInteract(predicted.x, predicted.y, predicted.z);
  let near = nearMesh;
  if (!near && spot) {
    const dx = predicted.x - spot.x;
    const dy = predicted.y - spot.y;
    const dz = predicted.z - spot.z;
    // Fallback для воксельных полотен без mesh_collision: центр Z из Hello может быть
    // между ступнями и верхом двери — допуск по Z шире, чем старый цилиндр от центра.
    near =
      dx * dx + dy * dy <= spot.radius * spot.radius &&
      Math.abs(dz) < Math.max(50, spot.radius * 2.4);
  }
  if (near) {
    el.textContent = touchUi ? t("Открыть дверь") : t("[E] Открыть дверь");
    el.classList.remove("hidden");
    el.setAttribute("aria-hidden", "false");
  } else {
    el.classList.add("hidden");
    el.setAttribute("aria-hidden", "true");
  }
}

/** Единый dispatch всех server→client messages. WebSocket = один канал, msgType
 *  определяет что делать. Никаких reliable/unreliable разделений. */
function handleMessage(data: Uint8Array): void {
  if (data.byteLength < 1) return;
  netReader.reset(data);
  const r = netReader;
  const msgType = r.u8();
  switch (msgType) {
    case MsgType.Hello: {
      const h = readHello(r);
      if (myId < 0) myId = h.yourId;
      currentSceneId = h.sceneId || currentSceneId;
      currentRoomKind = h.roomKind;
      currentMaxPlayers = h.maxPlayers || currentMaxPlayers;
      renderer.setSceneFog({
        color: h.fogColor,
        density: h.fogDensity,
        height: h.fogHeight,
        heightFalloff: h.fogHeightFalloff,
      });
      renderer.setSkyboxUrl(h.skyboxUrl);
      sceneStaticLights.length = 0;
      for (const L of h.lights ?? []) {
        sceneStaticLights.push({
          x: L.x,
          y: L.y,
          z: L.z,
          range: L.range,
          color: [L.r / 255, L.g / 255, L.b / 255],
        });
      }
      interactHotspotsHello = h.interactHotspots ?? [];
      // v22: параметры матча (DM). roundsTotal=0 → SP, матч-HUD скрыт.
      matchMode = h.matchMode;
      matchRoundsTotal = h.roundsTotal;
      applyHelloCharacter(h); // characters: персонаж + множитель скорости для prediction
      story.onHello(h); // story: сюжетная комната или нет
      void loadLevelStaticMesh(h.sceneId);
      // Если сервер сообщил нам о room'е (Hello теперь содержит sceneId/kind),
      // обновим лейбл кнопки в меню после уведомления.
      if (currentRoomKind === 0) {
        playLabel.textContent = "RESTART";
        playSub.textContent = h.storyMode === 1 ? t("книга заклинаний") : "single player"; // story
      } else if (currentRoomKind === 1) {
        playLabel.textContent = "RESTART";
        playSub.textContent = `deathmatch (public, max ${currentMaxPlayers})`;
      } else if (currentRoomKind === 2) {
        playLabel.textContent = "RESTART";
        playSub.textContent = `deathmatch (private, max ${currentMaxPlayers})`;
      }
      updateTouchGameActive();
      break;
    }
    case MsgType.Snapshot: {
      const snap = readSnapshot(r);
      if (myId < 0) {
        myId = snap.yourId;
        updateTouchGameActive();
      }
      snapBuf.push(snap, performance.now());
      // characters: кто каким персонажем играет; гостю — серебро за фраги в SP.
      noteSnapshotCharacters(snap.players, myId, !(authInfo && authInfo.ok && !authInfo.guest));
      // Grace-period reconnect: старая сессия могла иметь lastAckedInput >> nextSeq
      // новой сессии. ack(15000) при nextSeq=1 стирал бы все новые инпуты (1 ≤ 15000)
      // → replay с пустым буфером → предсказание не работало → персонаж «застывал».
      // Исправление: если ack от сервера ≥ нашего следующего seq, синхронизируем
      // nextSeq вперёд чтобы новые инпуты имели seq > lastAckedInput.
      if (snap.lastAckedInput > 0 && snap.lastAckedInput >= nextSeq) {
        nextSeq = snap.lastAckedInput + 1;
        prediction.clear();
        pendingReconcileX = 0;
        pendingReconcileY = 0;
        pendingReconcileZ = 0;
        syncRenderInterpFromPredicted();
      }
      prediction.ack(snap.lastAckedInput);

      const me = snap.players.find((p) => p.id === myId);
      if (me) {
        if (!yawInitialized) {
          // Подхватываем стартовый yaw со спавн-точки сервера (один раз)
          predicted.yaw = me.yaw;
          input.setYaw(me.yaw);
          yawInitialized = true;
        }
        const dx = me.x - predicted.x;
        const dy = me.y - predicted.y;
        const dz = me.z - predicted.z;
        const dist2 = dx * dx + dy * dy;
        // Порог расхождения Z больше чем XY: вертикальный jitter между
        // 60Hz (server) и 60Hz (client) при RTT ≈ 100мс даёт до 0.3м
        // дельты в пиковой точке прыжка, это нормально.
        const snapXY = dist2 > 0.36;
        const snapZ = Math.abs(dz) > 0.5;
        if (snapXY || snapZ) {
          pendingReconcileX = 0;
          pendingReconcileY = 0;
          pendingReconcileZ = 0;
          predicted.x = me.x;
          predicted.y = me.y;
          predicted.z = me.z;
          // vz/grounded/crouching с сервера не приходят — восстанавливаем из replay'а.
          const replayed = prediction.replay(predicted, map, chunks, damagedTiles);
          predicted.x = replayed.x;
          predicted.y = replayed.y;
          predicted.z = replayed.z;
          predicted.vx = replayed.vx;
          predicted.vy = replayed.vy;
          predicted.vz = replayed.vz;
          predicted.grounded = replayed.grounded;
          predicted.crouching = replayed.crouching;
          predicted.stairContactPrev = replayed.stairContactPrev;
          // Мягкая пересинхронизация — оставляем visual Z и bob-состояние.
          // Reconciliation snap на лестнице бенeфит: server отстаёт на ~50мс,
          // дёрганья renderLerpZ или bob phase обнуления выглядят как «бамп».
          // Post-replay predicted.z тот же, реального телепорта нет.
          syncRenderInterpAfterReconcile();
        } else {
          // Мягкая ошибка: копим дельту и размазываем по fixedTick — без импульса каждые ~50мс.
          if (dist2 >= RECON_XY_DEAD2 || Math.abs(dz) >= RECON_Z_DEAD) {
            pendingReconcileX += dx * RECON_SOFT_XY;
            pendingReconcileY += dy * RECON_SOFT_XY;
            // Z-reconcile НЕ применяем когда игрок на лестнице:
            // server-snapshot отстаёт на ~50мс (RTT), и при ходьбе по
            // лестнице client-predicted.z уже опередил server.z на 5-15см
            // (server в snapshot'е ещё на предыдущей высоте). Soft-reconcile
            // тянул бы predicted.z НАЗАД к server.z (вверх при спуске,
            // вниз при подъёме) — против движения, добавляя видимый «бамп».
            // На лестнице server и client синхронно следуют HF, истинного
            // расхождения нет — только лаг snapshot'а. Пропускаем.
            if (!predicted.stairContactPrev) {
              pendingReconcileZ += dz * RECON_SOFT_Z;
            }
          }
        }
      }
      for (const p of snap.players) {
        // Детектим «попали в игрока» по падению hp между snapshot'ами.
        // Снижение на любое значение (1+) → flinch. Триггерим только для
        // живых (hp > 0); смертельный хит обрабатывается через baseAnim=die
        // в updatePlayerAnims.
        const prev = playerLastHp.get(p.id);
        if (prev !== undefined && p.hp < prev && p.hp > 0) {
          playerLastHitAt.set(p.id, performance.now());
          // Для локального игрока триггерим damage-flash overlay (DOOM-style).
          if (p.id === myId) {
            triggerDamageFlash();
          }
        }
        playerLastHp.set(p.id, p.hp);
        scores.set(p.id, { score: p.score, hp: p.hp, deaths: p.deaths, team: p.team });
        if (p.id === myId) {
          predictedAmmoRounds = p.activeAmmo;
        }
      }
      weapons.onSnapshot(snap, myId); // weapons: инвентарь, оружие актёров, снаряды
      predictedAmmoRounds = weapons.activeAmmo();
      // v22: состояние матча для HUD (фаза/таймер/счёт команд).
      matchState.phase = snap.matchPhase;
      matchState.roundIdx = snap.roundIdx;
      matchState.secsLeft = snap.phaseSecsLeft;
      matchState.teamAWins = snap.teamAWins;
      matchState.teamBWins = snap.teamBWins;
      matchState.teamAKills = snap.teamAKills;
      matchState.teamBKills = snap.teamBKills;
      for (const pk of snap.pickups) {
        const ent = pickups.get(pk.id);
        if (ent) ent.state = pk.state;
      }
      syncPickupItemMeshes();
      // NPC: spawn новых / обновляем target. Движение/flinch → tickNpcs().
      syncNpcs(snap, performance.now());
      story.onSnapshot(snap); // story: этап, цель, существа
      break;
    }
    case MsgType.EventBatch: {
      handleEvents(readEventBatch(r));
      break;
    }
    case MsgType.Chat: {
      const id = r.u8();
      const txt = r.str();
      nicknames.set(id, txt);
      break;
    }
    case MsgType.PickupInit: {
      // v8: один раз после Hello — список всех pickup'ов сцены.
      for (const inst of pickupItemInst.values()) {
        renderer.removeMeshInstance(inst);
      }
      pickupItemInst.clear();
      pickupItemVisualTag.clear();
      pickups.clear();
      for (const p of readPickupInit(r)) {
        pickups.set(p.id, p);
      }
      console.log(`[pickups] loaded ${pickups.size} items from scene`);
      void (async () => {
        try {
          if (!pickupAmmoMesh) {
            pickupAmmoMesh = await renderer.loadMeshFromUrl("/models/items/ammo.glb");
          }
          if (!pickupMedkitMesh) {
            pickupMedkitMesh = await renderer.loadMeshFromUrl("/models/items/medkit.glb");
          }
        } catch (e) {
          console.warn("[pickups] item glb:", e);
        }
        syncPickupItemMeshes();
      })();
      break;
    }
    default:
      console.warn(`[net] unknown msg type=${msgType}`);
      break;
  }
}


function handleEvents(events: GameEvent[]): void {
  for (const ev of events) {
    if (story.onEvent(ev)) continue; // story: события сюжета
    artifacts.onEvent(ev, myId); // artifacts: до main — запись pickup'а ещё в списке
    if (weapons.onEvent(ev, myId)) {
      // weapons: выстрел/взмах/бросок, взрыв — эффекты и звук в src/weapons.
    } else if (ev.type === EvType.MansionDoor) {
      applyMansionDoorClient(ev.phase);
    } else if (ev.type === EvType.InteriorDoor) {
      applyInteriorDoorClient(ev.doorIdx, ev.phase);
    } else if (ev.type === EvType.BodyDamaged) {
      renderer.spawnDebris(ev.playerId, ev.hx, ev.hy, ev.hz, ev.vx, ev.vy, ev.vz, {
        size: BODY_FLESH_DEBRIS_CUBE_SIZE,
        color: BLOOD_DEBRIS_COLOR,
      });
    } else if (ev.type === EvType.NpcBodyDamaged) {
      const headshot = (ev.flags & NpcBodyDamagedFlagHeadshot) !== 0;
      const npc = npcs.get(ev.npcId);
      if (npc && headshot) {
        npc.inst.npcHeadshotDetach = true;
      }
      if (headshot) {
        const burst = 140;
        for (let i = 0; i < burst; i++) {
          const u = Math.random() * Math.PI * 2;
          const zBias = 0.18 + Math.random() * 0.72;
          const horiz = Math.sqrt(Math.max(0, 1 - zBias * zBias));
          const sp = 9 + Math.random() * 24;
          const dx = Math.cos(u) * horiz;
          const dy = Math.sin(u) * horiz;
          renderer.spawnChunkDebris(
            ev.hx + (Math.random() - 0.5) * 0.22,
            ev.hy + (Math.random() - 0.5) * 0.22,
            ev.hz + (Math.random() - 0.5) * 0.18,
            ev.vx * 0.28 + dx * sp,
            ev.vy * 0.28 + dy * sp,
            ev.vz * 0.22 + zBias * sp,
            MEAT_MESH_DEBRIS_COLOR,
            BODY_FLESH_DEBRIS_CUBE_SIZE * (0.65 + Math.random() * 0.85),
          );
        }
      } else if (ev.shatter) {
        const burst = 220;
        for (let i = 0; i < burst; i++) {
          const u = Math.random();
          const v = Math.random();
          const theta = u * Math.PI * 2;
          const phi = Math.acos(2 * v - 1);
          const sp = 7 + Math.random() * 16;
          const dx = Math.sin(phi) * Math.cos(theta);
          const dy = Math.sin(phi) * Math.sin(theta);
          const dz = Math.cos(phi);
          renderer.spawnChunkDebris(
            ev.hx + (Math.random() - 0.5) * 0.55,
            ev.hy + (Math.random() - 0.5) * 0.55,
            ev.hz + (Math.random() - 0.5) * 0.85,
            ev.vx * 0.22 + dx * sp,
            ev.vy * 0.22 + dy * sp,
            ev.vz * 0.18 + dz * sp,
            MEAT_MESH_DEBRIS_COLOR,
            BODY_FLESH_DEBRIS_CUBE_SIZE * (0.72 + Math.random() * 0.72),
          );
        }
      } else {
        const n = 88;
        for (let i = 0; i < n; i++) {
          const u = Math.random() * Math.PI * 2;
          const v = 0.25 + Math.random() * 0.75;
          const sp = 5.5 + Math.random() * 12;
          const horiz = Math.sqrt(Math.max(0, 1 - v * v));
          const dx = Math.cos(u) * horiz;
          const dy = Math.sin(u) * horiz;
          renderer.spawnChunkDebris(
            ev.hx + (Math.random() - 0.5) * 0.45,
            ev.hy + (Math.random() - 0.5) * 0.45,
            ev.hz + (Math.random() - 0.5) * 0.5,
            ev.vx * 0.32 + dx * sp,
            ev.vy * 0.32 + dy * sp,
            ev.vz * 0.28 + v * sp,
            MEAT_MESH_DEBRIS_COLOR,
            BODY_FLESH_DEBRIS_CUBE_SIZE * (0.55 + Math.random() * 0.65),
          );
        }
      }
    } else if (ev.type === EvType.Shot) {
      if (ev.surface === ShotSurf.Geometry) {
        const tact = renderer.getTimeAccSec();
        particleSim.recordWallGeometryHit(ev.hx, ev.hy, ev.hz, tact);
        const dx = ev.hx - ev.ox;
        const dy = ev.hy - ev.oy;
        const dz = ev.hz - ev.oz;
        // weapons: у дроби 9 попаданий за выстрел — искр на каждое меньше.
        const nSparks = (mobileLiteGraphics ? 4 : 8) >> (ev.weapon === 2 ? 2 : 0);
        particleSim.spawnImpactSparks(ev.hx, ev.hy, ev.hz, tact, dx, dy, dz, nSparks);
      }
      // weapons: трассер/луч/дробь по виду оружия (свой — от дула модели).
      weapons.onShot(ev, myId);
      if (ev.shooterId !== myId) {
        // Держим upper-layer "shoot" 250мс после события — для плавности
        // анимации (накладывается на текущее ходьба/бег remote'а).
        remoteShootUntil.set(ev.shooterId, performance.now() + REMOTE_SHOOT_HOLD_MS);
      }
    } else if (ev.type === EvType.Death) {
      const v = nicknames.get(ev.victimId) ?? `#${ev.victimId}`;
      const k = nicknames.get(ev.killerId) ?? `#${ev.killerId}`;
      pushKillfeed(`${k} → ${v}`);
    } else if (ev.type === EvType.Respawn) {
      if (ev.playerId === myId) {
        predicted.x = ev.x;
        predicted.y = ev.y;
        predicted.z = ev.z;
        predicted.vx = 0;
        predicted.vy = 0;
        // Сервер ставит игрока на землю (respawn или voidrescue после
        // падения за карту) — гасим вертикаль, иначе predicted продолжит
        // «падать» с накопленным vz до ближайшего reconcile.
        predicted.vz = 0;
        predicted.grounded = true;
        syncRenderInterpFromPredicted();
      }
    } else if (ev.type === EvType.FallingGroupSpawn) {
      // Сервер отделил кусок чанка как rigid body. Сэмплим цвета кубов
      // (один раз, на основе текстуры исходного чанка) и заводим группу.
      // Кубы в самом чанке уже мертвы; на клиенте chunks.upsert/break не нужен,
      // потому что server killed их и pushed them out of visibleList через
      // последующий ChunkBroken? — Нет, при FallingGroup сервер НЕ шлёт
      // ChunkBroken. Нужно вручную убрать кубы из chunks.
      const c = chunks.get(ev.chunkId);
      if (c) {
        for (const cube of ev.cubes) chunks.break(ev.chunkId, cube.cubeIdx);
      }
      fallingGroups.spawn(ev, chunks, performance.now() / 1000);
    } else if (ev.type === EvType.FallingGroupUpdate) {
      fallingGroups.update(ev, performance.now() / 1000);
      const nowS = performance.now() / 1000;
      if (fallingGroups.getCubeCount(ev.groupId) > 50) {
        const last = fallingSmokeTrailLast.get(ev.groupId) ?? 0;
        if (nowS - last >= 0.1) {
          const g = fallingGroups.get(ev.groupId);
          if (g) {
            const dt = Math.max(0, nowS - g.lastUpdateTime);
            const px = g.pos[0] + g.vel[0] * dt;
            const py = g.pos[1] + g.vel[1] * dt;
            const pz = g.pos[2] + g.vel[2] * dt;
            particleSim.spawnFallingSmokeTrail(
              g.pivot[0] + px,
              g.pivot[1] + py,
              g.pivot[2] + pz,
              renderer.getTimeAccSec(),
            );
            fallingSmokeTrailLast.set(ev.groupId, nowS);
          }
        }
      }
    } else if (ev.type === EvType.FallingGroupShatter) {
      fallingSmokeTrailLast.delete(ev.groupId);
      // Обвал секции — «падающая песчаная башня» из референса: каждый куб
      // группы рассыпается зёрнами (с дотекающими струйками), кубики-обломки
      // оставляем каждому третьему для веса.
      let shatterCubeNo = 0;
      fallingGroups.shatter(ev.groupId, performance.now() / 1000,
        (x, y, z, vx, vy, vz, color, size) => {
          const fz = floorAtColumn(chunks, x, y, 0.03, z + 0.01);
          renderer.spawnDustBurst(x, y, z, vx, vy, vz, color, fz, 16);
          if (shatterCubeNo++ % 3 === 0) {
            renderer.spawnChunkDebris(x, y, z, vx, vy, vz, color, size, { floorZ: fz });
          }
        });
    } else if (ev.type === EvType.ChunkPoseUpdate) {
      chunks.updatePose(ev.chunkId, ev.pos, ev.vel, ev.quat, ev.angVelVec,
        ev.resting, performance.now() / 1000);
    } else if (ev.type === EvType.NpcShot) {
      // NPC выстрелил: spawn трейсер, активируем shoot-overlay у NPC,
      // если попал в игрока — triggerим его flinch + (для локального)
      // запускаем damage-flash экранный overlay + звук выстрела.
      const now = performance.now();
      // weapons: не автомат — трассер/звук по виду оружия бота.
      if (!weapons.onNpcShot(ev)) {
        renderer.spawnTracer(ev.ox, ev.oy, ev.oz, ev.hx, ev.hy, ev.hz);
        pushMuzzleFlashLight(ev.ox, ev.oy, ev.oz);
        onNpcShotSound(ev.npcId, ev.ox, ev.oy, ev.oz);
      }
      const npc = npcs.get(ev.npcId);
      if (npc) {
        npc.triggerShoot();
        npcLastShotAt.set(ev.npcId, now);
      }
      if (ev.victimId !== 0) {
        const inst = playerInstances.get(ev.victimId);
        if (inst) {
          triggerHurtFlinch(inst.state, inst.mesh);
          playerLastHitAt.set(ev.victimId, now);
        }
      }
    } else if (ev.type === EvType.RoomClosed) {
      onRoomClosed(ev.reason);
    } else if (ev.type === EvType.RoundStart) {
      // v22: старт раунда — баннер + чистый килфид.
      showMatchBanner(`${t("РАУНД {n}", { n: ev.roundIdx + 1 })}<span class="sub">${t("из {total}", { total: ev.roundsTotal })}</span>`, 2500);
    } else if (ev.type === EvType.RoundEnd) {
      // v22: итог раунда.
      let txt: string;
      if (matchMode === MatchMode.TDM) {
        if (ev.winnerTeam === Team.None) {
          txt = `${t("Ничья в раунде")}<span class="sub">${ev.teamAKills}:${ev.teamBKills}</span>`;
        } else {
          const mine = myTeam() === ev.winnerTeam;
          txt = t("Раунд за {who}", { who: teamLabel(ev.winnerTeam) }) +
            `<span class="sub">${ev.teamAKills}:${ev.teamBKills}${mine ? t(" — ваша команда!") : ""}</span>`;
        }
      } else {
        if (ev.winnerPlayerId === 0) {
          txt = t("Ничья в раунде");
        } else {
          const nick = nicknames.get(ev.winnerPlayerId) ?? `#${ev.winnerPlayerId}`;
          txt = ev.winnerPlayerId === myId
            ? t("Раунд за вами!")
            : t("Раунд за {who}", { who: escapeHtml(nick) });
        }
      }
      showMatchBanner(txt, 6500);
    } else if (ev.type === EvType.MatchEnd) {
      // v22: финал матча — оверлей с таблицей. Комнату сервер закроет сам.
      console.log("[net] match end", ev);
      showMatchEndOverlay(ev);
    } else if (ev.type === EvType.Pickup) {
      // v8: отмечаем pickup как taken; если это локальный игрок —
      // обновляем HUD дельтой.
      const p = pickups.get(ev.pickupId);
      if (p) p.state = 1;
      if (ev.playerId === myId) {
        if (ev.pickupType === PickupType.Medkit) {
          localInventory.hp = maxHpOf(myId); // characters: макс. HP с апгрейдом
          const me = scores.get(myId);
          if (me) {
            me.hp = maxHpOf(myId);
            lastHpRendered = -1;
          }
        } else if (ev.pickupType === PickupType.Ammo) {
          if (predictedAmmoRounds >= 0) {
            predictedAmmoRounds = Math.min(MAX_WEAPON_AMMO, predictedAmmoRounds + ev.amount);
          }
        }
        console.log(`[pickup] ${ev.pickupType} +${ev.amount}`);
      }
      syncPickupItemMeshes();
    } else if (ev.type === EvType.PickupSpawn) {
      // weapons: трофей с убитого — новый pickup.
      pickups.set(ev.pickup.id, ev.pickup);
      syncPickupItemMeshes();
    } else if (ev.type === EvType.PickupRemove) {
      // weapons: pickup исчез (взят или истёк).
      const inst = pickupItemInst.get(ev.pickupId);
      if (inst) {
        renderer.removeMeshInstance(inst);
        pickupItemInst.delete(ev.pickupId);
        pickupItemVisualTag.delete(ev.pickupId);
      }
      pickups.delete(ev.pickupId);
    } else if (ev.type === EvType.PortalEnter) {
      // v8: просто лог — scene swap полноценно приходит в v9.
      console.log(`[portal] player ${ev.playerId} entered portal → ${ev.targetScene}:${ev.targetPortal}`);
      if (ev.playerId === myId) {
        // Лёгкий overlay (можно подключить DOM-элемент позже).
        const el = document.querySelector("#portal-overlay");
        if (el) (el as HTMLElement).textContent = t("Переход в {scene}…", { scene: ev.targetScene });
      }
    } else if (ev.type === EvType.ChunkBroken) {
      const center = chunks.break(ev.chunkId, ev.cubeIdx);
      if (center) {
        const c = chunks.get(ev.chunkId);
        if (c) {
          markTileDamaged(c, ev.cubeIdx, map, damagedTiles);
          const cubeColor = sampleChunkCubeColor(c, center[0], center[1], center[2], ev.cubeIdx);
          const tact = renderer.getTimeAccSec();
          const debrisFloor = floorAtColumn(chunks, center[0], center[1], 0.03, center[2] + 0.01);
          // ПЕСОК — главный носитель разрушения (референс: Houdini sand sim):
          // каждый воксель рассыпается плотным потоком зёрен с дотекающими
          // струйками; кубики-обломки — редкая приправа (каждый 3-й), чтобы
          // читался масштаб, но не спорил с песком.
          renderer.spawnDustBurst(center[0], center[1], center[2],
            ev.vx, ev.vy, ev.vz, cubeColor, debrisFloor, 40);
          if (ev.cubeIdx % 3 === 0) {
            const nSpark = mobileLiteGraphics ? 1 : 2;
            particleSim.spawnImpactSparks(center[0], center[1], center[2], tact, ev.vx, ev.vy, ev.vz, nSpark);
            renderer.spawnChunkDebris(center[0], center[1], center[2], ev.vx, ev.vy, ev.vz, cubeColor, c.cubeSize,
              { floorZ: debrisFloor });
          }
          if (ev.cubeIdx % 9 === 0) {
            particleSim.spawnDustPuff(center[0], center[1], center[2], tact, 0.6, mobileLiteGraphics);
          }
          // Мелкие кубики-осколки убраны: их роль полностью взял песок
          // (spawnDustBurst выше) — референсное «крошение» без кубиков.
        }
      }
    }
  }
}

function pushKillfeed(text: string): void {
  killfeed.push({ text, until: performance.now() + 4000 });
  if (killfeed.length > 6) killfeed.shift();
}

/** Сервер закрыл комнату (host ушёл / server shutdown / empty). Выходим
 *  из игры, показываем оверлей меню, ждём решения пользователя. */
function onRoomClosed(reason: number): void {
  console.log("[net] room closed, reason=" + reason);
  if (transport) {
    transport.close();
    transport = null;
  }
  resetState();
  pauseResumeRow.classList.add("hidden");
  overlay.classList.remove("hidden");
  showCard("menu");
  try { document.exitPointerLock(); } catch { /* ignore */ }
  let msg = "Комната закрыта.";
  if (reason === 1) msg = "Хост покинул комнату.";
  else if (reason === 2) msg = "Комната опустела.";
  else if (reason === 3) msg = "Сервер завершил комнату.";
  else if (reason === 4) msg = "Матч завершён.";
  else if (reason === 100) msg = "Книга заклинаний: забег окончен."; // story
  pushKillfeed(t(msg));
  playLabel.textContent = "SINGLE PLAYER";
  setText(playSub, "арена с NPC");
  updateTouchGameActive();
  updateAmbientMusic();
}

// «В меню» с финального экрана матча: не ждём авто-закрытия комнаты сервером.
matchEndExitBtn.addEventListener("click", () => {
  matchEndEl.classList.add("hidden");
  if (transport) {
    onRoomClosed(4);
  } else {
    resetMatchState();
    overlay.classList.remove("hidden");
    showCard("menu");
  }
});

/** Возвращает текущий шот-рэй игрока: (origin, dir) в мировых координатах.
 *  Дублирует логику shot origin на сервере: (x, y, EyeHeight + playerZ).
 *  Используется и для hit-detection по NPC, и для hover-теста. */
function getShotRay(): {
  ox: number; oy: number; oz: number;
  dx: number; dy: number; dz: number;
} {
  const cy = Math.cos(renderLerpYaw), sy = Math.sin(renderLerpYaw);
  const cp = Math.cos(renderLerpPitch), sp = Math.sin(renderLerpPitch);
  let ox: number;
  let oy: number;
  let oz: number;
  if (effectiveThirdPerson()) {
    const TP_BACK = 1.6;
    const TP_UP = 0.1;
    ox = renderLerpX - cy * TP_BACK;
    oy = renderLerpY - sy * TP_BACK;
    oz = eyeZSmoothed + TP_UP + renderLerpZ;
  } else {
    const FP_FORWARD = predicted.crouching ? 0.05 : 0.18;
    const FP_EYE_BUMP = predicted.crouching ? 0.3 : 0.2;
    const fcy = Math.cos(renderLerpYaw), fsy = Math.sin(renderLerpYaw);
    ox = renderLerpX + fcy * FP_FORWARD;
    oy = renderLerpY + fsy * FP_FORWARD;
    oz = eyeZSmoothed + renderLerpZ + FP_EYE_BUMP + fpViewBobZ;
  }
  return {
    ox,
    oy,
    oz,
    dx: cy * cp,
    dy: sy * cp,
    dz: sp,
  };
}

/** Применяет snapshot-state сервера ко всем NPC. Создаёт новые инстансы
 *  при первом появлении. Движение/анимации обрабатывает tickNpcs() в
 *  render loop'е. */
function syncRealVoxelHiddenFlags(): void {
  if (!useRealVoxelBody || !soldierRealVoxStableCells || !soldierRealVoxCentroids) return;
  const stable = soldierRealVoxStableCells;
  const centroids = soldierRealVoxCentroids;
  const t = renderer.getTimeAccSec();
  for (const inst of renderer.getMeshInstances()) {
    if (inst.realVoxelBody) {
      updateRealVoxelSubmeshHidden(inst, stable, centroids, t);
    }
  }
}

function syncNpcs(snap: { npcs: SnapshotNpc[] }, nowMs: number): void {
  if (!soldierMesh) return;
  const innerOpt = soldierVoxMesh ?? undefined;
  const seen = new Set<number>();
  for (const s of snap.npcs) {
    seen.add(s.id);
    let n = npcs.get(s.id);
    if (!n) {
      n = new Npc(renderer, soldierMesh, {
        x: s.x, y: s.y, feetZ: s.z, yaw: s.yaw,
        yawOffset: SOLDIER_YAW_OFFSET,
        scale: soldierScale,
        floorOffset: soldierFloorOffset,
        innerMesh: innerOpt,
        realVoxelBody: useRealVoxelBody,
      });
      npcs.set(s.id, n);
      console.log(`[npc] spawned #${s.id} at (${s.x.toFixed(1)}, ${s.y.toFixed(1)})`);
    }
    const shellStrip = n.onServerState(s.x, s.y, s.z, s.yaw, s.hp, s.state, s.hits, nowMs, s.bodyVox, s.bodyVoxShell);
    if (shellStrip) {
      particleSim.spawnShellStripBurst(
        n.inst.x,
        n.inst.y,
        n.inst.z + SHELL_STRIP_BURST_OFFSET_Z,
        renderer.getTimeAccSec(),
        mobileLiteGraphics,
      );
    }
  }
  for (const [id, n] of npcs) {
    if (!seen.has(id)) {
      stopNpcFireForRemovedInstance(id);
      renderer.removeMeshInstance(n.inst);
      npcs.delete(id);
      npcLastShotAt.delete(id);
      releaseNpcFootstep(id);
      console.log(`[npc] removed #${id}`);
    }
  }
}

/** Тикает pose NPC'ов (обновляет bone-matrices для render этого кадра)
 *  + авто-снятие shoot-overlay'я по таймеру после последнего EvNpcShot. */
function tickNpcs(dtSec: number, nowMs: number): void {
  for (const [id, n] of npcs) {
    const shotAt = npcLastShotAt.get(id);
    if (shotAt !== undefined && nowMs - shotAt > NPC_SHOOT_OVERLAY_MS) {
      n.stopShoot();
    }
    n.tick(dtSec, renderer, nowMs);
    updateNpcFootstepSound(id, n, dtSec);
    syncBodyVoxCellDebris(renderer, n.inst);
    updateInnerMeshLimbDetach(n.inst);
  }
}

/** Позиционирует/скрывает HP-бар. Показывает HP ближайшей цели под
 *  крестиком — это может быть NPC или любой remote-игрок. Вызывать ПОСЛЕ
 *  render (использует свежий viewProj через projectToScreen).
 *  Окклюзия стенами не проверяется: серверный hitscan сам знает где стена.
 *
 *  Обновления DOM — через transform: translate3d (GPU compositor layer) +
 *  dirty-check для координат/hp, чтобы не дергать layout каждый кадр. */
let lastNpcHpBarHidden = true;
let lastNpcHpBarPx = -1, lastNpcHpBarPy = -1;
let lastNpcHpBarPct = -1;
function updateNpcHpBar(): void {
  const r = getShotRay();

  type HoverTarget = {
    t: number;
    hpFrac: number;
    anchor: [number, number, number];
  };
  let best: HoverTarget | null = null;

  // NPCs.
  for (const n of npcs.values()) {
    if (n.state !== 0) continue; // мёртвому бар не нужен
    const t = n.intersectRay(r.ox, r.oy, r.oz, r.dx, r.dy, r.dz);
    if (t === null) continue;
    if (best !== null && t >= best.t) continue;
    best = { t, hpFrac: n.hpFraction(), anchor: n.headAnchor() };
  }
  // Remote-игроки (свой HP видим в HUD-overlay'е).
  for (const [id, inst] of playerInstances) {
    if (id === myId) continue;
    const hp = scores.get(id)?.hp ?? PLAYER_MAX_HP;
    if (hp <= 0) continue;
    const t = intersectCylinder(
      inst.x, inst.y, /*feetZ=*/0, ACTOR_RADIUS, ACTOR_HEIGHT,
      r.ox, r.oy, r.oz, r.dx, r.dy, r.dz,
    );
    if (t === null) continue;
    if (best !== null && t >= best.t) continue;
    best = {
      t,
      hpFrac: Math.max(0, Math.min(1, hp / maxHpOf(id))), // characters
      anchor: [inst.x, inst.y, ACTOR_HEIGHT + 0.25],
    };
  }

  if (!best) {
    if (!lastNpcHpBarHidden) {
      npcHpBarEl.classList.add("hidden");
      lastNpcHpBarHidden = true;
    }
    return;
  }

  const scr = renderer.projectToScreen(best.anchor[0], best.anchor[1], best.anchor[2]);
  if (!scr) {
    if (!lastNpcHpBarHidden) {
      npcHpBarEl.classList.add("hidden");
      lastNpcHpBarHidden = true;
    }
    return;
  }
  if (lastNpcHpBarHidden) {
    npcHpBarEl.classList.remove("hidden");
    lastNpcHpBarHidden = false;
  }
  const px = Math.round(scr.px);
  const py = Math.round(scr.py);
  if (px !== lastNpcHpBarPx || py !== lastNpcHpBarPy) {
    lastNpcHpBarPx = px;
    lastNpcHpBarPy = py;
    // -40 по X: центрирование (-50% of width=80); -6 по Y: -100% of height=6.
    npcHpBarEl.style.transform = `translate3d(${px - 40}px, ${py - 6}px, 0)`;
  }
  const pct = Math.round(best.hpFrac * 100);
  if (pct !== lastNpcHpBarPct) {
    lastNpcHpBarPct = pct;
    npcHpBarFillEl.style.width = `${pct}%`;
  }
}

function playFpFireFlash(): void {
  if (mobileLiteBase) return;
  const el = _fpFireVideoEl;
  if (!el) return;
  const show =
    !effectiveThirdPerson() && localPlayerControlActive();
  if (!show) return;
  fpWeaponSuppressedForMuzzleFlash = true;
  el.classList.remove("hidden");
  try { el.currentTime = 0; } catch { /* */ }
  void el.play().catch((e) => {
    console.warn("[fp-fire-video] play failed:", e);
    fpWeaponSuppressedForMuzzleFlash = false;
  });
}

// --- главный цикл ---
let acc = 0;
let last = performance.now();

const PROF_RING = 120;
const profFrameMs: number[] = [];
const profTicksPerFrame: number[] = [];

function ringAvgMax(a: number[]): { avg: number; max: number } {
  if (a.length === 0) return { avg: 0, max: 0 };
  let s = 0;
  let m = -Infinity;
  for (const x of a) {
    s += x;
    if (x > m) m = x;
  }
  return { avg: s / a.length, max: m };
}

function pushProfSample(ring: number[], v: number): void {
  ring.push(v);
  if (ring.length > PROF_RING) ring.shift();
}

function toggleProfilerOverlay(): void {
  profilerOverlayVisible = !profilerOverlayVisible;
  profilerEl.classList.toggle("hidden", !profilerOverlayVisible);
  profilerEl.setAttribute("aria-hidden", profilerOverlayVisible ? "false" : "true");
  syncRendererDebugPerf();
  if (profilerOverlayVisible) {
    profFrameMs.length = 0;
    profTicksPerFrame.length = 0;
  }
}

function updateProfilerOverlay(dtMs: number, ticksThisFrame: number): void {
  if (!profilerOverlayVisible) return;
  pushProfSample(profFrameMs, dtMs);
  pushProfSample(profTicksPerFrame, ticksThisFrame);
  const fa = ringAvgMax(profFrameMs);
  const ta = ringAvgMax(profTicksPerFrame);
  const perf = renderer.getPerfBreakdown();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mem = (performance as any).memory as { usedJSHeapSize?: number } | undefined;
  const memMB = mem?.usedJSHeapSize != null ? (mem.usedJSHeapSize / 1048576).toFixed(1) : "?";
  profilerEl.textContent =
    `Profiler (F10)  last ${profFrameMs.length} frames\n` +
    `frame ms  avg ${fa.avg.toFixed(2)}  max ${fa.max.toFixed(2)}  (this ${dtMs.toFixed(2)})\n` +
    `fixed ticks / frame  avg ${ta.avg.toFixed(2)}  max ${ta.max.toFixed(0)}  (this ${ticksThisFrame})\n` +
    `fps (1s): ${fpsValue.toFixed(0)}  heap ${memMB} MB\n` +
    `render ms (last frame):\n` +
    `  raycast ${perf.raycastMs.toFixed(3)}  fill ${perf.fillPrefixMs.toFixed(3)}  static ${perf.staticChunksMs.toFixed(3)}\n` +
    `  buf ${perf.bufferUploadMs.toFixed(3)}  vox ${perf.voxelDrawMs.toFixed(3)}  dyn ${perf.dynamicChunksMs.toFixed(3)}\n` +
    `  obj ${perf.objectChunksMs.toFixed(3)}  mesh ${perf.meshesMs.toFixed(3)}  post ${perf.postMs.toFixed(3)}`;
}

function frame(now: number): void {
  const dtMs = Math.min(now - last, 200); // clamp: вкладка в фоне могла дать +30 сек
  last = now;
  acc += dtMs;
  if (gameMenuPaused()) acc = 0;

  // FPS sampler — раз в секунду
  fpsFrameCount++;
  if (now - fpsLastSample >= 1000) {
    fpsValue = (fpsFrameCount * 1000) / (now - fpsLastSample);
    fpsFrameCount = 0;
    fpsLastSample = now;
  }

  // Максимум 5 тиков за кадр (≈ 83мс накопленного долга).
  // Без этого лага спираль: один тяжёлый кадр → 10+ fixedTick подряд →
  // пачка WS-сообщений → следующий кадр ещё тяжелее.
  if (acc > TICK_DT * 1000 * 5) acc = TICK_DT * 1000 * 5;

  let ticksThisFrame = 0;
  let ix = predicted.x;
  let iy = predicted.y;
  let iz = predicted.z;
  let iYaw = predicted.yaw;
  let iPitch = predicted.pitch;
  while (acc >= TICK_DT * 1000) {
    ix = predicted.x;
    iy = predicted.y;
    iz = predicted.z;
    iYaw = predicted.yaw;
    iPitch = predicted.pitch;
    acc -= TICK_DT * 1000;
    fixedTick(TICK_DT);
    ticksThisFrame++;
  }
  const tickMs = TICK_DT * 1000;
  const alpha = acc / tickMs;
  const rawX = predicted.x * alpha + ix * (1 - alpha);
  const rawY = predicted.y * alpha + iy * (1 - alpha);
  const rawZ = predicted.z * alpha + iz * (1 - alpha);

  const chordDx = predicted.x - ix;
  const chordDy = predicted.y - iy;
  const chordLen2 = chordDx * chordDx + chordDy * chordDy;
  let strafeFlip = false;
  if (chordLen2 > 1e-12) {
    const dotChord = chordDx * prevChordDx + chordDy * prevChordDy;
    strafeFlip = dotChord < -1e-9;
    prevChordDx = chordDx;
    prevChordDy = chordDy;
  }

  const dtSec = dtMs / 1000;
  // Ровный dt для камеры: при просадке FPS (~30 Гц) «сырой» dtSec сильно скачет → мерцание сглаживания.
  const dtCapCam = Math.min(dtSec, 0.085);
  smoothDtSecForCam += (dtCapCam - smoothDtSecForCam) * 0.18;
  const pendXY = Math.abs(pendingReconcileX) + Math.abs(pendingReconcileY);
  /** При резкой смене направления или подтяжке к серверу слегка сглаживаем XY камеры (мс), без доп. лага в покое. */
  const tauCam =
    strafeFlip ? 0.034 : pendXY > 2e-4 ? 0.038 : 0;
  const kCam = tauCam > 0 ? 1 - Math.exp(-smoothDtSecForCam / tauCam) : 1;
  if (kCam >= 0.999) {
    renderLerpX = rawX;
    renderLerpY = rawY;
  } else {
    renderLerpX += (rawX - renderLerpX) * kCam;
    renderLerpY += (rawY - renderLerpY) * kCam;
  }
  // Visual Z smoothing — 2nd-order critically damped (smooth-damp).
  // См. описание в client/src/engine/prediction.ts. Ключевое отличие от
  // 1st-order exp-lerp: zero initial velocity при step-входе, плавный
  // ramp-up acceleration, peak velocity 1.7 м/с вместо 7 м/с.
  //
  // При ungrounded (прыжок/падение) и при δZ > snap — мгновенный snap
  // (камера не должна отставать от тела при респавне/портале).
  const dzVis = rawZ - renderLerpZ;
  const absDzVis = Math.abs(dzVis);
  if (absDzVis > VISUAL_FEET_SMOOTH_SNAP || !predicted.grounded) {
    renderLerpZ = rawZ;
    renderLerpZVel = 0;
  } else {
    const [newZ, newVel] = smoothDampFloat(
      renderLerpZ, rawZ, renderLerpZVel,
      VISUAL_FEET_SMOOTH_TIME, dtSec, VISUAL_FEET_SMOOTH_MAX_SPEED,
    );
    renderLerpZ = newZ;
    renderLerpZVel = newVel;
  }
  renderLerpYaw = lerpAngle(iYaw, predicted.yaw, alpha);
  renderLerpPitch = clamp(lerp(iPitch, predicted.pitch, alpha), RENDER_PITCH_LO, RENDER_PITCH_HI);

  renderer.updateDebris(dtSec);
  particleSim.update(dtSec, renderer.getTimeAccSec(), {
    embersEnabled: !mobileLiteGraphics,
  });
  syncPlayerInstances(now, dtSec);
  updatePlayerAnims(now, dtSec);
  updatePickupMeshes(dtSec);
  updateLevelStaticMeshAnim(dtSec);
  // NPC-позы: применяем последний viewed server state (если был snapshot
  // в этом кадре — syncNpcs его применил) + тикаем анимацию.
  tickNpcs(dtSec, now);
  renderer.updateRealVoxelFlyForAll(dtSec);
  syncRealVoxelHiddenFlags();
  weapons.frame(dtSec, now); // weapons: оружие в руках у всех, снаряды, эффекты, HUD
  artifacts.frame(dtSec); // artifacts
  story.frame(dtSec); // story
  draw(now);
  updateHud();
  updateHpHud();
  updateAmmoHud();
  updateProfilerOverlay(dtMs, ticksThisFrame);
  perfGovernorSample(dtMs);
  requestAnimationFrame(frame);
}

const PICKUP_DRAW_AMMO_SCALE = 0.35;
const PICKUP_DRAW_MEDKIT_SCALE = 0.32;
const PICKUP_DRAW_WEAPON_SCALE = 0.48;

function pickupDrawSpec(
  type: PickupInit["type"],
): { mesh: LoadedMesh; scale: number; tag: string } | null {
  // weapons: оружие на полу (с подсветкой) рисует src/weapons/pickups.ts.
  if (weapons.isWeaponPickup(type)) return null;
  if (type === PickupType.Ammo && pickupAmmoMesh) {
    return { mesh: pickupAmmoMesh, scale: PICKUP_DRAW_AMMO_SCALE, tag: "ammo" };
  }
  if (type === PickupType.Medkit && pickupMedkitMesh) {
    return { mesh: pickupMedkitMesh, scale: PICKUP_DRAW_MEDKIT_SCALE, tag: "medkit" };
  }
  if (type === PickupType.WeaponRifle && pickupAmmoMesh) {
    return { mesh: pickupAmmoMesh, scale: PICKUP_DRAW_WEAPON_SCALE, tag: "weapon" };
  }
  return null;
}

/** GLB-инстансы pickup'ов (ammo / medkit / weapon) по state из `pickups` / snapshot. */
function syncPickupItemMeshes(): void {
  for (const [id, p] of pickups) {
    const spec = artifacts.owns(p) ? null : pickupDrawSpec(p.type); // artifacts: свои модели
    if (p.state === 0) {
      if (!spec) continue;
      const prevTag = pickupItemVisualTag.get(id);
      let inst = pickupItemInst.get(id);
      if (inst && prevTag !== spec.tag) {
        renderer.removeMeshInstance(inst);
        pickupItemInst.delete(id);
        pickupItemVisualTag.delete(id);
        inst = undefined;
      }
      if (!inst) {
        inst = renderer.addMeshInstance(renderer.createMeshInstance({
          mesh: spec.mesh,
          x: p.x, y: p.y, z: p.z, yaw: p.yaw, scale: spec.scale, initialAnim: "idle",
        }));
        pickupItemInst.set(id, inst);
        pickupItemVisualTag.set(id, spec.tag);
      } else {
        inst.x = p.x;
        inst.y = p.y;
        inst.z = p.z;
        inst.yaw = p.yaw;
        inst.scale = spec.scale;
        inst.hidden = false;
      }
    } else {
      const inst = pickupItemInst.get(id);
      if (inst) {
        renderer.removeMeshInstance(inst);
        pickupItemInst.delete(id);
        pickupItemVisualTag.delete(id);
      }
    }
  }
}

// characters: визуал игрока по персонажу из снапшота. Пока модель персонажа
// грузится — солдат; когда загрузилась, инстанс пересоздаётся (см. ниже).
const instFloorOffset = new WeakMap<MeshInstance, number>();
function playerVisualFor(id: number): CharacterVisual {
  const want = charIdOf(id, myId);
  const v = getCharacterVisual(want);
  if (v) return v;
  void ensureCharacterVisual(renderer, loadMeshCached, want);
  return getCharacterVisual(0) ?? {
    charId: 0, mesh: soldierMesh!, inner: soldierVoxMesh, scale: soldierScale, floorOffset: soldierFloorOffset,
  };
}
function createPlayerInstance(vis: CharacterVisual, x: number, y: number, yaw: number): MeshInstance {
  const inst = renderer.addMeshInstance(renderer.createMeshInstance({
    mesh: vis.mesh,
    innerMesh: vis.inner,
    realVoxelBody: useRealVoxelBody && vis.charId === 0,
    innerBodyVoxShaderCarve: false,
    x, y, z: vis.floorOffset,
    yaw, yawOffset: SOLDIER_YAW_OFFSET,
    scale: vis.scale,
    initialAnim: "idle",
  }));
  instFloorOffset.set(inst, vis.floorOffset);
  return inst;
}
/** Сменился персонаж (или догрузилась его модель) — старый инстанс убираем. */
function dropPlayerInstanceIfCharacterChanged(id: number, vis: CharacterVisual): void {
  const inst = playerInstances.get(id);
  if (inst && inst.mesh !== vis.mesh) {
    renderer.removeMeshInstance(inst);
    playerInstances.delete(id);
  }
}

/** Создаёт/удаляет MeshInstance под каждый известный playerId. */
function syncPlayerInstances(now: number, dtSec: number): void {
  if (!soldierMesh) return;
  const seen = new Set<number>();
  // Локальный игрок: даже если ещё нет в snapshot'е (myId<0), не создаём.
  // Но как только myId назначен, создаём для него инстанс по predicted.
  if (myId >= 0) {
    seen.add(myId);
    const vis = playerVisualFor(myId); // characters
    dropPlayerInstanceIfCharacterChanged(myId, vis);
    if (!playerInstances.has(myId)) {
      playerInstances.set(myId, createPlayerInstance(vis, predicted.x, predicted.y, predicted.yaw));
    }
  }
  // Remote игроки из snapshot'а
  for (const id of snapBuf.latestPlayerIds()) {
    if (id === myId) continue;
    seen.add(id);
    const vis = playerVisualFor(id); // characters
    dropPlayerInstanceIfCharacterChanged(id, vis);
    if (!playerInstances.has(id)) {
      const p = snapBuf.sample(id, now);
      const x = p?.x ?? 0, y = p?.y ?? 0, yaw = p?.yaw ?? 0;
      playerInstances.set(id, createPlayerInstance(vis, x, y, yaw));
      console.log(`[soldier] spawned player #${id} (персонаж ${vis.charId})`);
    }
  }
  // Удаляем инстансы для игроков, которых больше нет
  for (const [id, inst] of playerInstances) {
    if (!seen.has(id)) {
      renderer.removeMeshInstance(inst);
      playerInstances.delete(id);
      prevPlayerPos.delete(id);
      playerSpeedSmoothed.delete(id);
      remoteShootUntil.delete(id);
      remoteZVel.delete(id);
      jumpState.delete(id);
      stopTransition.delete(id);
      releaseRemotePlayerFootstep(id);
      console.log(`[soldier] removed player #${id}`);
    }
  }

  // Обновляем позиции/yaw + расчёт скорости каждого инстанса
  for (const [id, inst] of playerInstances) {
    let px = 0, py = 0, pyaw = 0, ppitch = 0, hp = 100;
    let pz = 0;
    if (id === myId) {
      px = renderLerpX; py = renderLerpY; pz = renderLerpZ;
      pyaw = renderLerpYaw; ppitch = renderLerpPitch;
      const me = scores.get(myId);
      if (me) hp = me.hp;
    } else {
      const p = snapBuf.sample(id, now);
      if (!p) continue;
      px = p.x; py = p.y; pz = p.z; pyaw = p.yaw; hp = p.hp;
      // Remote-игроки не передают pitch в snapshot, оставляем 0 (горизонт).
      // TODO: добавить pitch в proto если будет нужен видимый aim в TP для NPC/других.
    }
    inst.x = px;
    inst.y = py;
    // Vertical offset: ступни mesh'а = soldierFloorOffset над полом арены,
    // + player.Z добавляет высоту прыжка/платформы.
    //
    // Visual Z smoothing для remote-игроков: smooth-damp 2nd-order,
    // тот же фильтр что и для локальной камеры (см. frame()). Для локального
    // — renderLerpZ уже сглажен.
    const targetZ = (instFloorOffset.get(inst) ?? soldierFloorOffset) + pz; // characters
    if (id === myId) {
      inst.z = targetZ;
    } else {
      const dz = targetZ - inst.z;
      if (Math.abs(dz) > VISUAL_FEET_SMOOTH_SNAP) {
        inst.z = targetZ;
        const st = remoteZVel.get(id);
        if (st !== undefined) remoteZVel.set(id, 0);
      } else {
        const vel = remoteZVel.get(id) ?? 0;
        const [newZ, newVel] = smoothDampFloat(
          inst.z, targetZ, vel,
          VISUAL_FEET_SMOOTH_TIME, dtSec, VISUAL_FEET_SMOOTH_MAX_SPEED,
        );
        inst.z = newZ;
        remoteZVel.set(id, newVel);
      }
    }
    inst.yaw = pyaw;
    // Aim-pitch IK: верхний корпус наклоняется по pitch игрока, чтобы
    // оружие смотрело туда же, куда направлен взгляд (look up/down).
    inst.aimPitch = ppitch;
    // Aim-yaw IK: компенсируем запечённый поворот ствола в shoot-анимации.
    // firingRifle держит ствол ~40° левее body-forward, поэтому при активном
    // upper-layer "shoot" докручиваем корпус ВПРАВО на 40°. Сглаживаем
    // долей по weight'у — плавный заход/выход из стрельбы.
    // ВАЖНО: компенсация применяется ТОЛЬКО когда upper clip = "shoot";
    // для "idle"-overlay'я (strafe-hold) стрелка не запечена, доворот не нужен.
    const upperW = inst.state.upperLayer ? Math.abs(inst.state.upperLayer.weight) : 0;
    const upperIsShoot = inst.state.upperLayer?.clipName === "shoot";
    // С rotation-only Hips overlay корпус (Hips) slerp'ится между base-позой
    // и shoot-позой по weight w. Значит:
    //   w=0 (overlay не активен): Hips из base → НУЖНА полная компенсация
    //                              запечённого угла ствола (-40°).
    //   w=1 (полный overlay):     Hips = shoot-поза → компенсация НЕ нужна,
    //                              ствол уже направлен как в shoot-клипе.
    // Формула `(1 - w)` плавно снимает компенсацию по мере fade-in overlay'я.
    inst.aimYaw = upperIsShoot ? SOLDIER_SHOOT_AIM_YAW * (1 - upperW) : 0;
    // В FP у локального игрока: весь mesh скрыт (`hidden`), оружие только
    // как HTML-video поверх сцены. В TP — полный солдат с оружием в руке.
    const fpLocal = id === myId && !effectiveThirdPerson();
    inst.hidden = fpLocal;
    inst.fpViewOnly = false;
    inst.hiddenJoints = null;
    void hp;
    const spAlive = snapBuf.sample(id, now);
    if (spAlive) {
      inst.bodyHitCount = spAlive.hitCount;
      inst.bodyHitsPacked = packBodyHitsUniform(spAlive.hits);
      const prevHp = inst.prevHpForBodyVoxMerge;
      const respawnFullBody =
        prevHp !== undefined && prevHp <= 0 && spAlive.hp > 0;
      const shellStrip = applyServerBodyVox(inst, spAlive.bodyVox, respawnFullBody, spAlive.bodyVoxShell);
      if (shellStrip) {
        particleSim.spawnShellStripBurst(
          inst.x,
          inst.y,
          inst.z + SHELL_STRIP_BURST_OFFSET_Z,
          renderer.getTimeAccSec(),
          mobileLiteGraphics,
        );
      }
      inst.prevHpForBodyVoxMerge = spAlive.hp;
    }
    syncBodyVoxCellDebris(renderer, inst);
    updateInnerMeshLimbDetach(inst);
  }
}

/** Вращение пропов на полу: если в GLB нет клипов — крутим весь инстанс по yaw. */
const PICKUP_FALLBACK_SPIN_RPS = 1.15;

function updatePickupMeshes(dtSec: number): void {
  for (const inst of pickupItemInst.values()) {
    if (inst.mesh.animations.size === 0) {
      inst.yaw += PICKUP_FALLBACK_SPIN_RPS * dtSec;
      if (inst.yaw > Math.PI * 2) inst.yaw -= Math.PI * 2;
      else if (inst.yaw < -Math.PI * 2) inst.yaw += Math.PI * 2;
    }
    renderer.updateMeshInstancePose(inst, dtSec);
  }
}

function updateLevelStaticMeshAnim(dtSec: number): void {
  if (levelStaticMeshInst) {
    renderer.updateMeshInstancePose(levelStaticMeshInst, dtSec);
  }
}

/** Обновляет AnimState каждого инстанса (на основе скорости и Fire). */
function updatePlayerAnims(now: number, dtSec: number): void {
  // Time-constant сглаживания для скорости (сек). 150мс хватает чтобы
  // отфильтровать дрожание на границе fixedTick(60Гц)/render(144Гц), но
  // оставить мгновенным переход idle↔walk при старте/остановке.
  const SPEED_TAU = 0.15;
  const alpha = 1 - Math.exp(-dtSec / SPEED_TAU);
  // Hysteresis: разные пороги "вверх"/"вниз" чтобы на ровной дистанции
  // (например playerSpeed≈3.5) target не дёргался каждый кадр между walk/run.
  const TH_IDLE_UP   = 0.7;   // idle → walk
  const TH_WALK_DOWN = 0.3;   // walk → idle
  const TH_RUN_UP    = 4.0;   // walk → run
  const TH_RUN_DOWN  = 3.2;   // run → walk

  for (const [id, inst] of playerInstances) {
    let instSpeed = 0;
    let instVz = 0;
    let fireDown = false;
    let hp = 100;

    // Z игрока без mesh-оффсета (ноги над полом арены).
    const playerZ = inst.z - (instFloorOffset.get(inst) ?? soldierFloorOffset); // characters

    const prev = prevPlayerPos.get(id);
    const curT = now / 1000;
    if (prev) {
      const dt = Math.max(1e-3, curT - prev.t);
      instSpeed = Math.hypot(inst.x - prev.x, inst.y - prev.y) / dt;
      instVz = (playerZ - prev.z) / dt;
    }
    prevPlayerPos.set(id, { x: inst.x, y: inst.y, z: playerZ, t: curT });

    // Сглаживаем скорость per-player. Без этого render-rate jitter ломает
    // выбор анимации (см. комментарий у `playerSpeedSmoothed`).
    const prevSm = playerSpeedSmoothed.get(id) ?? 0;
    const speed = prevSm + (instSpeed - prevSm) * alpha;
    playerSpeedSmoothed.set(id, speed);

    // Vz: у локального берём напрямую из предсказания (responsive), у remote
    // считаем по дельте Z между кадрами. Сглаживания по Vz не применяем —
    // пороги достаточно широкие чтобы не ложно срабатывать на jitter.
    let vz = instVz;
    if (id === myId) vz = predicted.vz;

    if (id === myId) {
      // Локальный: upper «shoot» только по импульсу после выстрела (см. fixedTick),
      // а не пока зажата ЛКМ — иначе при полуавтомате клип залипает на удержании.
      fireDown = false;
      const until = remoteShootUntil.get(id);
      if (until !== undefined && now < until) fireDown = true;
      const me = scores.get(myId);
      if (me) hp = me.hp;
    } else {
      const p = snapBuf.sample(id, now);
      if (p) hp = p.hp;
      // Remote: если недавно был EvType.Shot — держим upper-layer
      const until = remoteShootUntil.get(id);
      if (until !== undefined && now < until) fireDown = true;
    }

    // Death — highest priority, one-shot. Прерывает всё остальное
    // (в т.ч. активный прыжок), чтобы смерть была сразу видна.
    if (hp <= 0) {
      if (inst.state.baseAnim !== "die") setBaseAnim(inst.state, "die");
      jumpState.delete(id);
      stopTransition.delete(id);
    } else {
      const animsAvail = inst.mesh.animations;
      const hasWalk = animsAvail.has("walk");
      const hasCrouchIdle = animsAvail.has("crouchIdle");
      const hasCrouchWalk = animsAvail.has("crouchWalk");
      const hasJump = animsAvail.has("jump");
      const hasJumpDown = animsAvail.has("jumpDown");
      const hasRunToStop = animsAvail.has("runToStop");
      const hasRunLeft = animsAvail.has("runLeft");
      const hasRunRight = animsAvail.has("runRight");
      const hasRunFwdLeft = animsAvail.has("runForwardLeft");
      const hasRunFwdRight = animsAvail.has("runForwardRight");
      // Если у меша нет "walk" клипа — состояние walk заменяем на run.
      const walkOrRun = hasWalk ? "walk" : "run";

      // ─── Направление движения (для стрейф/диагональ анимаций) ─────────
      // Приоритет: чистая диагональ (W+A → forwardLeft) > чистый стрейф
      // (A без W → left). Для back+strafe (S+A, S+D) отдельных клипов нет,
      // fallback на "run". Для local — прямо по кнопкам; для remote —
      // проекция велосити на forward/right игрока через его yaw.
      const STRAFE_DOMINANCE = 1.5;  // |right| > |fwd| × 1.5 → чистый стрейф
      const DIAGONAL_MIN = 0.4;      // оба компонента > MIN × speed → диагональ
      let moveAnim = "run";
      if (id === myId) {
        const fwd = ((input.buttons & Btn.Forward) ? 1 : 0)
                  - ((input.buttons & Btn.Back) ? 1 : 0);
        const right = ((input.buttons & Btn.Right) ? 1 : 0)
                    - ((input.buttons & Btn.Left) ? 1 : 0);
        if (fwd > 0 && right < 0 && hasRunFwdLeft) moveAnim = "runForwardLeft";
        else if (fwd > 0 && right > 0 && hasRunFwdRight) moveAnim = "runForwardRight";
        else if (fwd === 0 && right === -1 && hasRunLeft) moveAnim = "runLeft";
        else if (fwd === 0 && right === 1 && hasRunRight) moveAnim = "runRight";
      } else {
        // Remote: проекция велосити на forward/right игрока.
        const prevR = prevPlayerPos.get(id);
        if (prevR && instSpeed > 0.5) {
          const dtFrame = Math.max(1e-3, curT - prevR.t);
          const vxF = (inst.x - prevR.x) / dtFrame;
          const vyF = (inst.y - prevR.y) / dtFrame;
          const cy = Math.cos(inst.yaw), sy = Math.sin(inst.yaw);
          const fwdSpd = vxF * cy + vyF * sy;
          const rightSpd = vxF * sy - vyF * cy;
          const absF = Math.abs(fwdSpd), absR = Math.abs(rightSpd);
          if (absR > absF * STRAFE_DOMINANCE) {
            // Чистый стрейф.
            if (rightSpd < 0 && hasRunLeft) moveAnim = "runLeft";
            else if (rightSpd > 0 && hasRunRight) moveAnim = "runRight";
          } else if (fwdSpd > 0 && absF > DIAGONAL_MIN * instSpeed &&
                     absR > DIAGONAL_MIN * instSpeed) {
            // Диагональ вперёд + вбок.
            if (rightSpd < 0 && hasRunFwdLeft) moveAnim = "runForwardLeft";
            else if (rightSpd > 0 && hasRunFwdRight) moveAnim = "runForwardRight";
          }
        }
      }
      const MOVE_ANIMS = new Set([
        "run", "walk", "runLeft", "runRight", "runForwardLeft", "runForwardRight",
      ]);

      // ─── Jump/jumpDown driven by physics (Vz + in-air flag) ───────────
      // В воздухе: Vz > threshold → "jump" (взлёт), Vz < -threshold →
      // "jumpDown" (падение). На земле — jumpState удаляем.
      // "В воздухе" детектим по Vz: у локального мы берём predicted.vz,
      // у remote — по дельте Z. Если |Vz| > AIR_TH — считаем в воздухе.
      // Это надёжнее чем сравнение Z с полом (не знаем локально где
      // высокая платформа в текущей точке XY без ChunkRegistry, но Vz
      // становится ≠0 только в воздухе).
      const JUMP_VZ_UP = 0.6;    // выше этого vz — считаем "взлёт"
      const JUMP_VZ_DOWN = -0.6; // ниже — "падение"
      let js = jumpState.get(id);
      if (vz > JUMP_VZ_UP && hasJump) {
        if (!js || js.phase !== "up") {
          js = { phase: "up" };
          jumpState.set(id, js);
        }
      } else if (vz < JUMP_VZ_DOWN && hasJumpDown) {
        if (!js || js.phase !== "down") {
          js = { phase: "down" };
          jumpState.set(id, js);
        }
      } else if (js) {
        // |Vz| малое — проверяем: действительно приземлился (Z ≈ floor
        // и держится ≥ 50мс), тогда выходим из прыжка. Для локального
        // grounded приходит из предсказания — используем его напрямую.
        const onGround = id === myId ? predicted.grounded : Math.abs(vz) < 0.1;
        if (onGround) {
          jumpState.delete(id);
          js = undefined;
        }
      }

      // ─── Crouch (hold C — переключение между crouchIdle/crouchWalk) ───
      // Только для локального игрока на этом этапе (state не шарится по сети).
      const crouchDown = id === myId && hasCrouchIdle &&
        (input.buttons & Btn.Crouch) !== 0;

      // ─── Stop transition (runToStop) ─────────────────────────────────
      // Цель: более отзывчивая визуальная остановка. Когда игрок отпускает
      // WASD после бега/ходьбы — мгновенно запускаем one-shot runToStop,
      // в то время как сглаженная speed плавно падает до idle-порога.
      // Триггер:
      //   - anim currently run/walk (т.е. игрок был в движении);
      //   - instSpeed < STOP_INST_TH (для local — поддерживается ещё и
      //     состоянием WASD для мгновенного ответа на keyup);
      //   - runToStop доступен, нет активного прыжка/смерти/приседа.
      const STOP_INST_TH = 0.5; // м/с — "практически остановился"
      const curAnim = inst.state.baseAnim;
      const wasMoving = MOVE_ANIMS.has(curAnim);
      const noMoveKeys = id === myId
        ? (input.buttons & (Btn.Forward | Btn.Back | Btn.Left | Btn.Right)) === 0
        : instSpeed < STOP_INST_TH;
      const nowStopping = wasMoving && noMoveKeys && instSpeed < STOP_INST_TH;
      if (nowStopping && !js && !crouchDown && hasRunToStop && !stopTransition.has(id)) {
        stopTransition.add(id);
      }
      // Отмена stopTransition — по НАДЁЖНЫМ сигналам "игрок реально бежит":
      //   - Local: пользователь нажал клавишу движения (детерминированно,
      //     мгновенно, без зависимости от reconciliation).
      //   - Remote: сглаженная скорость выше 2 м/с (устойчива к 1-кадровым
      //     spike'ам от snapshot reconciliation, которые давали мигание).
      if (stopTransition.has(id)) {
        const resumed = id === myId
          ? (input.buttons & (Btn.Forward | Btn.Back | Btn.Left | Btn.Right)) !== 0
          : speed > 2.0;
        if (resumed) stopTransition.delete(id);
      }

      let target = inst.state.baseAnim;
      if (id === myId && baseAnimOverride !== null) {
        target = baseAnimOverride;
      } else if (js) {
        // Пока активен прыжок — полностью удерживаем клип, speed не смотрим.
        target = js.phase === "up" ? "jump" : "jumpDown";
      } else if (crouchDown) {
        const cur = inst.state.baseAnim;
        const crouchMove = hasCrouchWalk ? "crouchWalk" : "crouchIdle";
        if (cur === "crouchIdle") {
          if (speed > TH_IDLE_UP) target = crouchMove;
        } else if (cur === "crouchWalk") {
          if (speed < TH_WALK_DOWN) target = "crouchIdle";
        } else {
          target = speed > TH_IDLE_UP ? crouchMove : "crouchIdle";
        }
      } else if (stopTransition.has(id)) {
        // Играем runToStop. Выход — когда клип полностью проигран.
        const clip = animsAvail.get("runToStop");
        const dur = clip?.duration ?? 0;
        if (inst.state.baseAnim === "runToStop" && dur > 0 &&
            inst.state.baseTime >= dur) {
          stopTransition.delete(id);
          target = "idle";
        } else {
          target = "runToStop";
        }
      } else if (id === myId) {
        // LOCAL: прямая button-driven логика. Пользователь нажал движение —
        // мгновенно играет moveAnim (run/runLeft/runRight). Отпустил —
        // мгновенно idle. Никакого сглаживания и гистерезиса — они
        // нужны только для remote (где нет прямого доступа к input'у).
        // Это убирает 150мс лаг между keyDown и визуальным стартом бега.
        const moveKeys = (input.buttons & (Btn.Forward | Btn.Back | Btn.Left | Btn.Right)) !== 0;
        target = moveKeys ? moveAnim : "idle";
      } else {
        // REMOTE: speed-driven с гистерезисом (нет прямого доступа к input'у).
        // Smoothed speed + thresholds защищают от дрожания на границах.
        const cur = inst.state.baseAnim;
        if (cur === "idle") {
          if (speed > TH_IDLE_UP) target = speed > TH_RUN_UP ? moveAnim : walkOrRun;
        } else if (cur === "walk") {
          if (speed < TH_WALK_DOWN) target = "idle";
          else if (speed > TH_RUN_UP) target = moveAnim;
        } else if (MOVE_ANIMS.has(cur)) {
          if (speed < TH_RUN_DOWN) {
            target = speed < TH_WALK_DOWN ? "idle" : walkOrRun;
          } else if (cur !== moveAnim) {
            target = moveAnim;
          }
        } else {
          if (speed < TH_IDLE_UP) target = "idle";
          else if (speed < TH_RUN_UP) target = walkOrRun;
          else target = moveAnim;
        }
      }
      if (inst.state.baseAnim !== target) setBaseAnim(inst.state, target);

      // Upper layer. Назначение:
      //   - "shoot" — когда стреляем (firingRifle поверх).
      //   - "idle"  — когда базовый клип это чистый стрейф (`runLeft` /
      //               `runRight`): запечёный Mixamo-клип двигает ВСЁ тело
      //               (в т.ч. руки), но в шутерном стрейфе мы хотим
      //               чтобы ноги шагали в сторону, а руки/корпус
      //               оставались на стволе. Overlay'им rifleIdle поверх —
      //               верхняя часть получает "держу винтовку"-позу,
      //               нижняя (не входит в upper-body mask) продолжает
      //               играть strafe-клип.
      //   - null    — во всех остальных случаях base play'ит всё тело.
      // ── Hurt-flinch: триггерится по детектируемому падению hp в snapshot'е
      //   Пока flinch активен (по таймеру HURT_IDLE_TIMEOUT_MS от последнего
      //   хита ИЛИ пока клип играет) — полностью пропускаем обычную
      //   логику upperLayer (shoot / strafe-hold). Flinch имеет высший
      //   приоритет для визуальной обратной связи "в меня попадают".
      //
      //   ВАЖНО: проверяем `clipName === "hurt"` БЕЗ weight>0 — свежий
      //   layer стартует с weight=0 (fade-in не начался), и требовать >0
      //   привело бы к тому что shoot в том же кадре перезапишет hurt.
      const lastHit = playerLastHitAt.get(id);
      const hurtByTimer = lastHit !== undefined &&
        (now - lastHit) < HURT_IDLE_TIMEOUT_MS;
      const curUpper0 = inst.state.upperLayer;
      const hurtPlaying = curUpper0 !== null && curUpper0.clipName === "hurt";
      if (hurtByTimer) {
        triggerHurtFlinch(inst.state, inst.mesh);
      } else if (hurtPlaying) {
        stopHurtFlinchIfDone(inst.state, inst.mesh,
          lastHit ?? -Infinity, now);
      }
      // Пока hurt-layer ещё живёт (fade-in или steady; fade-out тоже
      // блокирует до полного удаления) — не трогаем upperLayer.
      const hurtBlocking = inst.state.upperLayer !== null &&
        inst.state.upperLayer.clipName === "hurt";
      if (hurtBlocking) {
        updatePlayerFootstepSound(id, inst, speed, vz, jumpState.has(id), dtSec);
        renderer.updateMeshInstancePose(inst, dtSec);
        continue;
      }

      let wantShoot = fireDown;
      if (id === myId && upperAnimOverride !== null) wantShoot = upperAnimOverride;
      const curAnimForUpper = inst.state.baseAnim;
      // Базовые клипы, у которых Mixamo запёк движение рук — но в шутере
      // мы хотим нижнюю часть (ноги/Hips) из клипа, а верх держит ствол.
      // Список: чистые/диагональные стрейфы + присед-idle/walk.
      const strafeHoldUpper =
        curAnimForUpper === "runLeft" ||
        curAnimForUpper === "runRight" ||
        curAnimForUpper === "runForwardLeft" ||
        curAnimForUpper === "runForwardRight" ||
        curAnimForUpper === "crouchIdle" ||
        curAnimForUpper === "crouchWalk";
      let desiredUpper: string | null = null;
      if (wantShoot) desiredUpper = "shoot";
      else if (strafeHoldUpper) desiredUpper = "idle";
      const curU = inst.state.upperLayer;
      if (desiredUpper !== null) {
        // "shoot" чуть быстрее — клип короче полуавтоматического cooldown (~220мс).
        const speed = desiredUpper === "shoot" ? 1.35 : 1.0;
        const needStart = !curU || curU.weight < 0 || curU.clipName !== desiredUpper;
        if (needStart) startUpperLayer(inst.state, desiredUpper, true, speed);
        else if (curU) curU.speed = speed;
      } else {
        if (curU && curU.weight > 0) stopUpperLayer(inst.state);
      }
    }

    updatePlayerFootstepSound(id, inst, speed, vz, jumpState.has(id), dtSec);
    renderer.updateMeshInstancePose(inst, dtSec);
  }
}

function fixedTick(dt: number): void {
  const sample = input.sample();
  let effButtons = sample.buttons;
  if (!gameInputActive() || localPlayerDead()) {
    effButtons = 0;
  }
  if (localPlayerControlActive()) {
    const auth = snapBuf.latestPlayer(myId);
    if (auth?.bodyVox && !bodyVoxLegsCanSupportClient(auth.bodyVox)) {
      effButtons = sample.buttons & ~(Btn.Forward | Btn.Back | Btn.Left | Btn.Right | Btn.Jump);
    }
  }
  // Паузы матча (countdown / конец раунда): огонь запрещён — сервер всё равно
  // отбросит, а локальные вспышка/звук без выстрела выглядели бы рассинхроном.
  if (matchFireBlocked()) {
    effButtons &= ~Btn.Fire;
  }

  // weapons: спуск по оружию в руках (темп, очередь/одиночные, патроны) —
  // предсказание для звука/вспышки/отдачи; урон считает сервер.
  const fireHeld = (effButtons & Btn.Fire) !== 0;
  const firePrev = (prevButtons & Btn.Fire) !== 0;
  if (weapons.fixedTick(fireHeld, firePrev, localPlayerControlActive())) {
    if (FP_VIDEO_WEAPON) playFpFireFlash();
    remoteShootUntil.set(myId, performance.now() + REMOTE_SHOOT_HOLD_MS);
  }
  predictedAmmoRounds = weapons.activeAmmo();
  const inGame = transport !== null && myId >= 0 && localPlayerControlActive();
  if (!inGame) {
    killFireSound();
  }

  // Локальное предсказание движения (горизонталь + вертикаль + присед)
  const r = applyInput(
    map, chunks, damagedTiles,
    predicted.x, predicted.y, predicted.z, predicted.vx, predicted.vy, predicted.vz,
    predicted.grounded, predicted.crouching, predicted.stairContactPrev,
    effButtons, prevButtons, sample.yaw, dt,
  );
  predicted.x = r.x;
  predicted.y = r.y;
  predicted.z = r.z;
  predicted.vx = r.vx;
  predicted.vy = r.vy;
  predicted.vz = r.vz;
  predicted.grounded = r.grounded;
  predicted.crouching = r.crouching;
  predicted.stairContactPrev = r.stairContactPrev;
  predicted.yaw = sample.yaw;
  predicted.pitch = sample.pitch;
  lastInputDt = dt;

  if (transport && myId >= 0 && gameInputActive()) {
    const seq = nextSeq++;
    const netButtons = effButtons | (effectiveThirdPerson() ? Btn.ThirdPerson : 0) | weapons.selectBits(); // weapons: выбор оружия
    prediction.add({
      seq,
      buttons: netButtons,
      prevButtons,
      yaw: sample.yaw,
      pitch: sample.pitch,
      dt,
      resultX: predicted.x,
      resultY: predicted.y,
      resultZ: predicted.z,
      resultVz: predicted.vz,
      resultGrounded: predicted.grounded,
      resultCrouching: predicted.crouching,
      resultStairContactPrev: predicted.stairContactPrev,
    });
    writer.reset();
    writeInput(writer, {
      seq,
      dtMs: Math.min(65535, Math.round(dt * 1000)),
      buttons: netButtons,
      yaw: sample.yaw,
      pitch: sample.pitch,
    });
    // WebSocket — один канал, всё reliable. Сервер дедупит по c.Seq.
    transport.send(writer.bytes());
  }
  prevButtons = effButtons;

  if (pendingReconcileX !== 0 || pendingReconcileY !== 0 || pendingReconcileZ !== 0) {
    predicted.x += pendingReconcileX * RECON_DRAIN;
    predicted.y += pendingReconcileY * RECON_DRAIN;
    predicted.z += pendingReconcileZ * RECON_DRAIN;
    pendingReconcileX *= 1 - RECON_DRAIN;
    pendingReconcileY *= 1 - RECON_DRAIN;
    pendingReconcileZ *= 1 - RECON_DRAIN;
    if (Math.abs(pendingReconcileX) < 1e-7) pendingReconcileX = 0;
    if (Math.abs(pendingReconcileY) < 1e-7) pendingReconcileY = 0;
    if (Math.abs(pendingReconcileZ) < 1e-7) pendingReconcileZ = 0;
  }
  updateInteractHint();
}

function draw(now: number): void {
  // Позиция камеры/прицела: renderLerp* — интерполяция между шагами fixedTick (Gaffer),
  // плюс присед (eyeZSmoothed). Физика симуляции остаётся в `predicted`.
  // Плавное "оседание" камеры при приседе: интерполируем высоту глаз
  // между 1.6м (стоя) и 1.0м (присед) с постоянной времени ~80мс.
  const drawDtMs = Math.max(0, now - lastDrawTime);
  lastDrawTime = now;
  const drawFrameDtSec = Math.min(0.1, drawDtMs / 1000);
  const targetEyeZ = predicted.crouching ? 1.0 : 1.6;
  const eyeAlpha = Math.min(1, drawDtMs / 80);
  eyeZSmoothed += (targetEyeZ - eyeZSmoothed) * eyeAlpha;

  // Doom-style view bob: фаза от сглаженной скорости (иначе d(lerp)/dt шумит
  // и камера дёргается). Ниже амплитуда/чуть выше частота на полном беге —
  // ближе к плавному покачиванию Doom 2.
  const dtBob = Math.max(1e-4, (now - fpBobPrevTime) / 1000);
  fpBobPrevTime = now;
  let bobZ = 0;
  let bobRoll = 0;
  if (effectiveThirdPerson() || !gameInputActive() || myId < 0) {
    fpBobPrevLerpX = renderLerpX;
    fpBobPrevLerpY = renderLerpY;
    fpBobSpeedSmooth *= Math.exp(-dtBob * 14);
  } else {
    const vx = (renderLerpX - fpBobPrevLerpX) / dtBob;
    const vy = (renderLerpY - fpBobPrevLerpY) / dtBob;
    fpBobPrevLerpX = renderLerpX;
    fpBobPrevLerpY = renderLerpY;
    const speedRaw = Math.hypot(vx, vy);
    const tauSec = predicted.crouching ? 0.1 : 0.078;
    const a = 1 - Math.exp(-dtBob / tauSec);
    fpBobSpeedSmooth += (speedRaw - fpBobSpeedSmooth) * a;

    if (predicted.grounded && fpBobSpeedSmooth > 0.024) {
      const lo = 0.038;
      const hi = MOVE_SPEED * 1.02;
      let w = (fpBobSpeedSmooth - lo) / (hi - lo);
      if (w < 0) w = 0;
      else if (w > 1) w = 1;
      w = w * w * (3 - 2 * w);
      const freqBoost = 0.72 + 0.28 * (w * w);
      const strideM = predicted.crouching ? 0.98 : 1.22;
      fpBobPhase +=
        fpBobSpeedSmooth * ((Math.PI * 2) / strideM) * freqBoost * dtBob;
      const ampZ = predicted.crouching ? 0.028 : 0.041;
      bobZ = Math.sin(fpBobPhase) * ampZ * w;
      const cy = Math.cos(renderLerpYaw);
      const sy = Math.sin(renderLerpYaw);
      const fwd = vx * cy + vy * sy;
      const right = vx * sy - vy * cy;
      const strafeAmt = Math.min(1, Math.abs(right) / (Math.abs(fwd) + Math.abs(right) + 1e-4));
      const ampRoll = (predicted.crouching ? 0.014 : 0.021) * (0.52 + strafeAmt * 0.48);
      bobRoll = Math.sin(fpBobPhase * 0.5 + 0.9) * ampRoll * w;
    } else {
      fpBobSpeedSmooth *= Math.exp(-dtBob * 10);
    }
  }
  fpViewBobZ = bobZ;

  let cam: import("./engine/renderer").CameraState;
  if (effectiveThirdPerson()) {
    // TP chase: камера ближе и чуть выше плеча солдата.
    // Дистанция 1.6м — солдат занимает почти весь экран, видно отдачу/анимации.
    // Камера на уровне глаз (~1.7м), потолок не давит сверху.
    const cyaw = Math.cos(renderLerpYaw), syaw = Math.sin(renderLerpYaw);
    const TP_BACK = 1.6;
    const TP_UP = 0.1;          // EYE_HEIGHT(1.6) + 0.1 = 1.7м
    cam = {
      x: renderLerpX - cyaw * TP_BACK,
      y: renderLerpY - syaw * TP_BACK,
      yaw: renderLerpYaw,
      pitch: renderLerpPitch,
      bobZ: 0,
      bobRoll: 0,
      // Камера поднимается вместе с игроком (прыжок/платформа) + плавный присед.
      eyeZ: eyeZSmoothed + TP_UP + renderLerpZ,
    };
  } else {
    // FP: eyeZ поднимается с игроком; floor offset по умолчанию = EYE_HEIGHT,
    // тут добавляем прыжок/платформу поверх. Камера стоит в физическом центре
    // капсулы: forward-offset позволяет near-plane'у заглядывать за тонкие
    // стены, даже когда сама капсула уже заблокирована.
    //
    // `FP_EYE_BUMP` — Doom-style подъём глаз над физической позицией
    // персонажа. При стоянии оружие держится на уровне груди Spine, и без
    // подъёма ствол закрывает центр экрана. Поднимая камеру на +20см (стоя)
    // или +30см (присед), вытаскиваем ствол в нижнюю треть кадра, как
    // в шутерах Doom/Quake. Server-side прицеливание это не трогает —
    // serverFire считает луч от Z = EyeHeight=1.6, а TP-crosshair в UI
    // проецируется через `projectToScreen`, который учитывает cam.eyeZ.
    //
    // При приседе `FP_EYE_BUMP` больше стоячего — компенсируем низкую позу игрока.
    const FP_FORWARD = 0.0;
    const FP_EYE_BUMP = predicted.crouching ? 0.3 : 0.2;
    const fcy = Math.cos(renderLerpYaw), fsy = Math.sin(renderLerpYaw);
    cam = {
      x: renderLerpX + fcy * FP_FORWARD,
      y: renderLerpY + fsy * FP_FORWARD,
      yaw: renderLerpYaw,
      pitch: renderLerpPitch,
      bobZ,
      bobRoll,
      eyeZ: eyeZSmoothed + renderLerpZ + FP_EYE_BUMP,
    };
  }
  // weapons: вид от первого лица (3D-оружие + руки), тряска, приближение.
  weapons.beforeRender(cam, drawFrameDtSec);
  renderer.render(
    cam,
    chunks.all(),
    fallingGroups.all(),
    buildFrameLights(now),
    now / 1000,
    chunks.contentEpoch(),
  );
  renderColliderDebugOverlay(
    cam,
    renderLerpX,
    renderLerpY,
    renderLerpZ,
    predicted.crouching ? PLAYER_COLLIDER_HEIGHT_CROUCH : PLAYER_COLLIDER_HEIGHT,
  );
  const idleEl = _fpWeaponIdleVideoEl;
  const walkEl = _fpWeaponWalkVideoEl;
  if (!mobileLiteBase && (idleEl || walkEl)) {
    const showFpWeaponVideo =
      !effectiveThirdPerson() && gameInputActive();
    const hideWeapon =
      !showFpWeaponVideo || fpWeaponSuppressedForMuzzleFlash;

    if (myId >= 0) {
      const sp = playerSpeedSmoothed.get(myId) ?? 0;
      if (fpWeaponOverlayWalk) {
        if (sp < FP_WEAPON_WALK_EXIT) fpWeaponOverlayWalk = false;
      } else {
        if (sp > FP_WEAPON_WALK_ENTER) fpWeaponOverlayWalk = true;
      }
    } else {
      fpWeaponOverlayWalk = false;
    }

    const targetBlend = fpWeaponOverlayWalk ? 1 : 0;
    if (hideWeapon) {
      fpWeaponWalkBlend = targetBlend;
      idleEl?.classList.add("hidden");
      walkEl?.classList.add("hidden");
      if (idleEl) idleEl.style.opacity = "";
      if (walkEl) walkEl.style.opacity = "";
      try {
        idleEl?.pause();
        walkEl?.pause();
      } catch { /* */ }
    } else {
      fpWeaponWalkBlend += (targetBlend - fpWeaponWalkBlend)
        * (1 - Math.exp(-drawFrameDtSec * FP_WEAPON_BLEND_LAMBDA));
      if (fpWeaponWalkBlend < 1e-3) fpWeaponWalkBlend = 0;
      else if (fpWeaponWalkBlend > 1 - 1e-3) fpWeaponWalkBlend = 1;

      idleEl?.classList.remove("hidden");
      walkEl?.classList.remove("hidden");
      const w = fpWeaponWalkBlend;
      if (idleEl) idleEl.style.opacity = String(1 - w);
      if (walkEl) walkEl.style.opacity = String(w);

      const playIdle = w < 0.97;
      const playWalk = w > 0.03;
      if (idleEl) {
        if (playIdle) {
          if (idleEl.paused) {
            void idleEl.play().catch((e) => {
              console.warn("[fp-weapon-video-idle] play failed:", e);
            });
          }
        } else {
          try { idleEl.pause(); } catch { /* */ }
        }
      }
      if (walkEl) {
        if (playWalk) {
          if (walkEl.paused) {
            void walkEl.play().catch((e) => {
              console.warn("[fp-weapon-video-walk] play failed:", e);
            });
          }
        } else {
          try { walkEl.pause(); } catch { /* */ }
        }
      }
    }
  }
  if (_fpFireVideoEl && !mobileLiteGraphics) {
    const showFpFire =
      !effectiveThirdPerson() && gameInputActive();
    if (!showFpFire) {
      _fpFireVideoEl.pause();
      _fpFireVideoEl.classList.add("hidden");
      fpWeaponSuppressedForMuzzleFlash = false;
    }
  }
  compositeFpWeaponPixel();
  updateCrosshair();
  // HP-бар NPC использует свежий viewProj (projectToScreen), поэтому
  // вызываем ПОСЛЕ render, в том же месте где updateCrosshair.
  updateNpcHpBar();
  renderKillfeed(now);
}

/** Пиксель-арт для FP-оружия: видео-оверлеи (idle/walk/fire) — DOM поверх
 *  канваса и сами по себе не пикселизуются. Прячем их (visibility: hidden,
 *  декодирование продолжается) и каждый кадр рисуем в низкоресный канвас
 *  той же пиксельной плотности, что и мир (высота 360), с pixelated-апскейлом.
 *  Слои и их opacity читаем из существующей логики оверлеев — она не меняется. */
function compositeFpWeaponPixel(): void {
  const cv = _fpWeaponPixelCanvas;
  const ctx = _fpWeaponPixelCtx;
  if (!cv || !ctx) return;
  const pixelOn = renderer.getPixelArtMode() && !mobileLiteBase && fpWeaponPixelComposite;
  const layers: Array<[HTMLVideoElement | null, number]> = [
    [_fpWeaponIdleVideoEl, _fpWeaponIdleVideoEl ? parseFloat(_fpWeaponIdleVideoEl.style.opacity || "1") : 0],
    [_fpWeaponWalkVideoEl, _fpWeaponWalkVideoEl ? parseFloat(_fpWeaponWalkVideoEl.style.opacity || "1") : 0],
    [_fpFireVideoEl, 1],
  ];
  if (!pixelOn) {
    cv.classList.add("hidden");
    for (const [el] of layers) el?.classList.remove("fp-pixelsrc");
    return;
  }
  for (const [el] of layers) el?.classList.add("fp-pixelsrc");

  // Пиксельная плотность мира: backing-строк 360 на высоту окна.
  const ps = Math.max(1, window.innerHeight / 360);
  const bw = Math.max(1, Math.round(window.innerWidth / ps));
  const bh = 360;
  if (cv.width !== bw || cv.height !== bh) {
    cv.width = bw;
    cv.height = bh;
  }
  ctx.clearRect(0, 0, bw, bh);
  let drewAny = false;
  for (const [el, alpha] of layers) {
    if (!el || el.classList.contains("hidden") || alpha <= 0.01) continue;
    if (el.readyState < 2) continue; // кадра ещё нет
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) continue;
    ctx.globalAlpha = Math.min(1, alpha);
    ctx.drawImage(el, r.left / ps, r.top / ps, r.width / ps, r.height / ps);
    drewAny = true;
  }
  ctx.globalAlpha = 1;
  cv.classList.toggle("hidden", !drewAny);
}

/** Позиционирует прицел через CSS transform (GPU layer, не вызывает
 *  layout reflow — в отличие от style.left/top, которые каждый кадр
 *  триггерили synchronous layout через последующий getBoundingClientRect). */
let lastCrosshairPx = -1, lastCrosshairPy = -1;
let lastCrosshairHidden = false;
function updateCrosshair(): void {
  const cy = Math.cos(renderLerpYaw), sy = Math.sin(renderLerpYaw);
  const cp = Math.cos(renderLerpPitch), sp = Math.sin(renderLerpPitch);
  const AIM_DIST = 15;
  let aimOx: number;
  let aimOy: number;
  let aimOz: number;
  if (effectiveThirdPerson()) {
    const TP_BACK = 1.6;
    const TP_UP = 0.1;
    aimOx = renderLerpX - cy * TP_BACK;
    aimOy = renderLerpY - sy * TP_BACK;
    aimOz = eyeZSmoothed + TP_UP + renderLerpZ;
  } else {
    const FP_FORWARD = predicted.crouching ? 0.05 : 0.18;
    const FP_BUMP = predicted.crouching ? 0.3 : 0.2;
    const fcy = Math.cos(renderLerpYaw), fsy = Math.sin(renderLerpYaw);
    aimOx = renderLerpX + fcy * FP_FORWARD;
    aimOy = renderLerpY + fsy * FP_FORWARD;
    aimOz = eyeZSmoothed + renderLerpZ + FP_BUMP + fpViewBobZ;
  }
  const aimX = aimOx + cy * cp * AIM_DIST;
  const aimY = aimOy + sy * cp * AIM_DIST;
  const aimZ = aimOz + sp * AIM_DIST;
  const scr = renderer.projectToScreen(aimX, aimY, aimZ);
  if (!scr) {
    if (!lastCrosshairHidden) {
      crosshairEl.style.display = "none";
      lastCrosshairHidden = true;
    }
    return;
  }
  if (lastCrosshairHidden) {
    crosshairEl.style.display = "";
    lastCrosshairHidden = false;
  }
  // Скругляем до целого px — меньше DOM-обновлений при микро-дрожании.
  const px = Math.round(scr.px);
  const py = Math.round(scr.py);
  if (px === lastCrosshairPx && py === lastCrosshairPy) return;
  lastCrosshairPx = px;
  lastCrosshairPy = py;
  // translate3d(...) форсирует compositor layer, не триггерит layout.
  crosshairEl.style.transform = `translate3d(${px - 9}px, ${py - 9}px, 0)`;
}

let lastKillfeedHtml = "";
function renderKillfeed(now: number): void {
  let html = "";
  for (const k of killfeed) {
    if (now > k.until) continue;
    html += `<div class="line">${escapeHtml(k.text)}</div>`;
  }
  if (html !== lastKillfeedHtml) {
    lastKillfeedHtml = html;
    killfeedEl.innerHTML = html;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c] as string));
}

/** DOOM-style HP HUD — большие цифры внизу. Обновляем DOM ТОЛЬКО когда
 *  hp реально изменился (иначе DOM-writes каждый кадр каскадно триггерят
 *  reflow при следующем getBoundingClientRect). */
let lastHpRendered = -1;
function updateHpHud(): void {
  const me = scores.get(myId);
  const hp = me ? me.hp : PLAYER_MAX_HP;
  if (hp === lastHpRendered) return;
  lastHpRendered = hp;
  hpValueEl.textContent = String(hp);
  hpBarFillEl.style.width = `${Math.min(100, Math.round((hp * 100) / maxHpOf(myId)))}%`; // characters
  hpOverlayEl.classList.toggle("critical", hp > 0 && hp <= 25);
  hpOverlayEl.classList.toggle("dead", hp === 0);
  hpDeadEl.classList.toggle("hidden", hp > 0);
}

/** Нижний бар: патроны рядом с HP (Doom-style). */
let lastAmmoHudKey = "";
function updateAmmoHud(): void {
  if (myId < 0) {
    ammoStatEl.classList.add("hidden");
    lastAmmoHudKey = "";
    return;
  }
  ammoStatEl.classList.remove("hidden");
  // weapons: патроны и вместимость оружия в руках.
  const maxAmmo = Math.max(1, weapons.activeMaxAmmo());
  const inf = predictedAmmoRounds < 0;
  const cur = inf ? 0 : Math.max(0, predictedAmmoRounds);
  const key = `${inf}:${cur}:${maxAmmo}`;
  if (key === lastAmmoHudKey) return;
  lastAmmoHudKey = key;
  if (inf) {
    ammoValueEl.textContent = "∞";
    ammoMaxEl.textContent = "";
    ammoBarFillEl.style.width = "100%";
  } else {
    ammoValueEl.textContent = String(cur);
    ammoMaxEl.textContent = `/ ${maxAmmo}`;
    ammoBarFillEl.style.width = `${Math.min(1, cur / maxAmmo) * 100}%`;
  }
  ammoStatEl.classList.toggle("low", !inf && cur <= Math.max(1, Math.floor(maxAmmo * 0.15)));
}

/** Короткий красный flash по краям экрана — обратная связь «в меня попали».
 *  При частых попаданиях (NPC поливает очередью) критично НЕ вызывать
 *  forced reflow (offsetHeight): каждый такой вызов — synchronous layout
 *  всего документа. Вместо этого — requestAnimationFrame: браузер
 *  автоматически форсит reflow перед styles, но по своему расписанию. */
let damageFlashTimer: number | null = null;
let damageFlashHideTimer: number | null = null;
let damageFlashActive = false;
function triggerDamageFlash(): void {
  if (mobileLiteGraphics) return;
  // Если уже в активном flash-режиме — просто продлеваем (retrigger).
  if (damageFlashTimer !== null) clearTimeout(damageFlashTimer);
  if (damageFlashHideTimer !== null) clearTimeout(damageFlashHideTimer);
  if (!damageFlashActive) {
    damageFlashEl.classList.add("active");
    damageFlashActive = true;
    // rAF: браузер сам сделает layout перед применением next styles,
    // нам не нужно форсить offsetHeight вручную.
    requestAnimationFrame(() => {
      damageFlashEl.classList.add("flash");
    });
  } else {
    // Уже виден — просто убедимся что .flash стоит (для retrigger'а).
    damageFlashEl.classList.add("flash");
  }
  damageFlashTimer = window.setTimeout(() => {
    damageFlashEl.classList.remove("flash");
    damageFlashTimer = null;
    damageFlashHideTimer = window.setTimeout(() => {
      damageFlashEl.classList.remove("active");
      damageFlashActive = false;
      damageFlashHideTimer = null;
    }, 380);
  }, 60);
}

// ── v22: матч-HUD (таймер/раунды/счёт команд) + баннеры фаз ────────────
let lastMatchHudKey = "";
let lastMatchBannerHtml = "";

/** Матч идёт (DM-комната с настроенным матчем)? SP → HUD скрыт. */
function matchActive(): boolean {
  return transport !== null && matchRoundsTotal > 0 && matchState.phase !== MatchPhase.Idle;
}

/** Стрельба запрещена фазой матча (countdown / конец раунда / конец матча). */
function matchFireBlocked(): boolean {
  if (!matchActive()) return false;
  const ph = matchState.phase;
  return ph === MatchPhase.Countdown || ph === MatchPhase.RoundEnd || ph === MatchPhase.MatchEnd;
}

function fmtMatchTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

function updateMatchHud(): void {
  if (!matchActive()) {
    if (lastMatchHudKey !== "off") {
      lastMatchHudKey = "off";
      matchHudEl.classList.add("hidden");
      matchBannerEl.classList.add("hidden");
      lastMatchBannerHtml = "";
    }
    return;
  }
  const st = matchState;
  const tdm = matchMode === MatchMode.TDM;
  const key = `${st.phase}:${st.roundIdx}:${st.secsLeft}:${st.teamAWins}:${st.teamBWins}:${st.teamAKills}:${st.teamBKills}:${tdm}`;
  if (key !== lastMatchHudKey) {
    lastMatchHudKey = key;
    matchHudEl.classList.remove("hidden");
    // Таймер.
    if (st.phase === MatchPhase.Playing || st.phase === MatchPhase.RoundEnd || st.phase === MatchPhase.Countdown) {
      matchTimerEl.textContent = fmtMatchTime(st.secsLeft);
    } else {
      matchTimerEl.textContent = "--:--";
    }
    matchTimerEl.classList.toggle("low",
      st.phase === MatchPhase.Playing && st.secsLeft <= 30);
    // Раунд.
    if (st.phase === MatchPhase.Warmup) {
      matchRoundEl.textContent = t("ожидание игроков");
    } else if (st.phase === MatchPhase.MatchEnd) {
      matchRoundEl.textContent = t("матч окончен");
    } else {
      matchRoundEl.textContent = t("Раунд {n}/{total}", { n: st.roundIdx + 1, total: matchRoundsTotal });
    }
    // Счёт команд (TDM).
    if (tdm) {
      matchTeamsEl.classList.remove("hidden");
      matchTeamAKillsEl.textContent = String(st.teamAKills);
      matchTeamBKillsEl.textContent = String(st.teamBKills);
      matchTeamsWinsEl.textContent = t("раунды {a}:{b}", { a: st.teamAWins, b: st.teamBWins });
    } else {
      matchTeamsEl.classList.add("hidden");
    }
  }
  // Баннер: транзиент (итог раунда) приоритетнее фазового текста.
  let html = "";
  const now = performance.now();
  if (matchBannerTransient && now < matchBannerTransientUntil) {
    html = matchBannerTransient;
  } else if (st.phase === MatchPhase.Warmup) {
    html = `${t("Ожидание игроков…")}<span class="sub">${t("матч начнётся, когда зайдёт соперник")}</span>`;
  } else if (st.phase === MatchPhase.Countdown) {
    html = t("Бой через {n}…", { n: st.secsLeft }) +
      (tdm ? `<span class="sub">${t("вы за {team}", { team: teamLabel(myTeam()) || "—" })}</span>` : "");
  }
  if (html !== lastMatchBannerHtml) {
    lastMatchBannerHtml = html;
    if (html) {
      matchBannerEl.innerHTML = html;
      matchBannerEl.classList.remove("hidden");
    } else {
      matchBannerEl.classList.add("hidden");
    }
  }
}

/** Транзиентный баннер на holdMs (старт раунда, итог раунда). */
function showMatchBanner(html: string, holdMs: number): void {
  matchBannerTransient = html;
  matchBannerTransientUntil = performance.now() + holdMs;
}

/** Финальный экран матча (EvMatchEnd). Комнату сервер закроет сам через ~20с. */
function showMatchEndOverlay(ev: Extract<GameEvent, { type: typeof EvType.MatchEnd }>): void {
  const tdm = matchMode === MatchMode.TDM;
  let title: string;
  if (tdm) {
    title = ev.winnerTeam === Team.None
      ? t("Ничья")
      : `${t("Победили")} <span class="winner ${ev.winnerTeam === Team.A ? "tA" : "tB"}">${teamLabel(ev.winnerTeam)}</span>`;
    if (ev.winnerTeam !== Team.None && myTeam() === ev.winnerTeam) title += t(" — победа!");
  } else {
    const winNick = nicknames.get(ev.winnerPlayerId) ?? `#${ev.winnerPlayerId}`;
    title = ev.winnerPlayerId === myId
      ? `${t("Победа!")} <span class="winner">${escapeHtml(winNick)}</span>`
      : `${t("Победил")} <span class="winner">${escapeHtml(winNick)}</span>`;
  }
  matchEndTitleEl.innerHTML = title;
  const rows = [...ev.scores].sort((a, b) =>
    b.roundWins - a.roundWins || b.kills - a.kills || a.deaths - b.deaths);
  matchEndRowsEl.innerHTML = rows.map((s, i) => {
    const nick = nicknames.get(s.playerId) ?? `#${s.playerId}`;
    const tCls = s.team === Team.A ? "tA" : s.team === Team.B ? "tB" : "";
    return `<tr class="${s.playerId === myId ? "me" : ""}">` +
      `<td>${i + 1}</td><td class="${tCls}">${escapeHtml(nick)}</td>` +
      `<td>${s.roundWins}</td><td>${s.kills}</td><td>${s.deaths}</td></tr>`;
  }).join("");
  matchEndEl.classList.remove("hidden");
  try { document.exitPointerLock(); } catch { /* ignore */ }
}

// HUD throttling — тексты debug'а + innerHTML scoreboard'а пишем не чаще
// 4 Гц (= раз в 250 мс). До этого обновление каждого кадра в сочетании
// с `getBoundingClientRect()` в projectToScreen давало layout trash'ing
// и FPS 14-17 вместо стабильных 60+.
let lastHudUpdateMs = 0;
let lastScoreboardHash = "";
const HUD_UPDATE_INTERVAL_MS = 250;
function updateHud(): void {
  const now = performance.now();
  if (now - lastHudUpdateMs < HUD_UPDATE_INTERVAL_MS) return;
  lastHudUpdateMs = now;

  updateMatchHud();

  // Scoreboard — только когда изменился состав/hp/score (всегда, даже если debug HUD скрыт).
  let hash = "";
  const sorted = [...scores.entries()].sort((a, b) =>
    a[1].team - b[1].team || b[1].score - a[1].score);
  for (const [id, s] of sorted) {
    hash += `${id}:${s.score}:${s.hp}:${s.deaths}:${s.team};`;
  }
  if (hash !== lastScoreboardHash) {
    lastScoreboardHash = hash;
    const tdm = matchMode === MatchMode.TDM && matchActive();
    let html = "<b>SCORES</b><br>";
    let lastTeam = -2;
    for (const [id, s] of sorted) {
      const nick = escapeHtml(nicknames.get(id) ?? `#${id}`);
      if (tdm && s.team !== lastTeam) {
        lastTeam = s.team;
        const cls = s.team === Team.A ? "teamA-txt" : s.team === Team.B ? "teamB-txt" : "";
        html += `<span class="${cls}" style="font-size:11px; letter-spacing:1px">— ${teamLabel(s.team) || t("БЕЗ КОМАНДЫ")} —</span><br>`;
      }
      const color = !tdm ? "" : s.team === Team.A ? "color:#f66" : s.team === Team.B ? "color:#6af" : "";
      html += `<span style="${color}">${nick}${id === myId ? t(" (вы)") : ""}</span>: ${s.score}` +
        ` &nbsp;<span style="color:#888">†${s.deaths} hp=${s.hp}</span><br>`;
    }
    scoreboard.innerHTML = html;
  }

  if (!gameDebugHudVisible) return;

  const me = scores.get(myId);
  let totalVisible = 0;
  for (const c of chunks.all()) totalVisible += c.visibleList.length;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mem = (performance as any).memory as { usedJSHeapSize?: number } | undefined;
  const memMB = mem?.usedJSHeapSize ? (mem.usedJSHeapSize / 1048576).toFixed(0) : "?";
  const chunkCount = chunks.all().length;
  const gfxPom = renderer.getPomLayers();
  const gfxRs = (renderer.getRenderScale() * 100).toFixed(0);
  const perf = DEBUG_PERF || profilerOverlayVisible ? renderer.getPerfBreakdown() : null;
  const perfLine = perf
    ? `perf ms: rc=${perf.raycastMs.toFixed(2)} fill=${perf.fillPrefixMs.toFixed(2)} ` +
      `static=${perf.staticChunksMs.toFixed(2)} buf=${perf.bufferUploadMs.toFixed(2)} ` +
      `voxDraw=${perf.voxelDrawMs.toFixed(2)} dyn=${perf.dynamicChunksMs.toFixed(2)} ` +
      `obj=${perf.objectChunksMs.toFixed(2)} mesh=${perf.meshesMs.toFixed(2)} post=${perf.postMs.toFixed(2)}\n`
    : "";
  const fpsLowHint =
    fpsValue > 0 && fpsValue < 42
      ? t("(low fps → грубее интерполяция; macOS энергосбережение≈30Hz; F7 разрешение ниже)") + "\n"
      : "";
  const realVoxLine = useRealVoxelBody
    ? `realBody prototype draws: ${renderer.lastRealVoxelBodyDrawCalls}  (?realBody=1)\n`
    : "";
  const ppd = renderer.getPostProcessDiagnostics();
  const ppHud =
    `post: want=${ppd.enabled ? "y" : "n"} fbo=${ppd.fboReady ? "ok" : "NO"} ` +
    `draw=${ppd.appliedLastFrame ? "y" : "n"}` +
    (ppd.fboFailHex ? ` ${ppd.fboFailHex}` : "") +
    `  buf ${ppd.canvasW}×${ppd.canvasH}\n`;
  hud.textContent =
    `${netStatus}\nid: ${myId}\n` +
    `pos: ${predicted.x.toFixed(2)}, ${predicted.y.toFixed(2)}\n` +
    `fps: ${fpsValue.toFixed(0)}  cubes: ${totalVisible}\n` +
    realVoxLine +
    fpsLowHint +
    `gfx: POM${gfxPom}  res${gfxRs}%  (F6/F7)  prof F10  Ctrl+L ${t("скрыть")}  chunk№${chunks.contentEpoch()}\n` +
    ppHud +
    perfLine +
    `chunks: ${renderer.lastDrawnChunks}/${chunkCount} (culled ${renderer.lastCulledCount})\n` +
    `mem: ${memMB} MB  dt: ${(lastInputDt * 1000).toFixed(1)}ms\n` +
    (me ? `hp: ${me.hp}  score: ${me.score}\n` : "");
}

/** SP/DM: legacy арены + level-pack с сервера (/api/scenes). */
async function refreshSceneList(): Promise<void> {
  try {
    const r = await fetch(`${location.origin}/api/scenes`, { credentials: "include" });
    if (!r.ok) return;
    const j = (await r.json()) as { scenes: string[] };
    if (!j.scenes?.length) return;
    spScene.replaceChildren(
      ...j.scenes.map((id) => {
        const o = document.createElement("option");
        o.value = id;
        o.textContent = id;
        return o;
      }),
    );
    dmScene.replaceChildren(
      ...j.scenes.map((id) => {
        const o = document.createElement("option");
        o.value = id;
        o.textContent = id;
        return o;
      }),
    );
  } catch {
    /* ignore */
  }
}
void refreshSceneList();

// ─── Заставка (src/intro) ────────────────────────────────────────────────
// Первый заход: «нажмите, чтобы начать» → заставка → меню. Повтор — кнопкой
// «Заставка» в меню. Пока она идёт, в фоне качается то, что нужно уровню.
initIntro({
  menuButton: document.getElementById("menuIntro"),
  // Жест на экране «нажмите»: разблокируем и музыку меню (iOS требует play() в жесте).
  onGesture: () => {
    menuBgMusic.muted = true;
    void menuBgMusic.play()
      .then(() => { menuBgMusic.pause(); menuBgMusic.currentTime = 0; })
      .catch(() => { /* autoplay */ })
      .finally(() => { menuBgMusic.muted = false; });
  },
  onReveal: () => updateAmbientMusic(),
  onFinish: () => updateAmbientMusic(),
  prefetch: () => {
    void warmLevel(spScene.value);
    void warmHttpCache([
      "/models/soldier.glb",
      "/models/soldier_animated_vox.glb",
      SFX_FIRE, SFX_OUT_OF_AMMO, SFX_RUN,
      ...PALETTE_MATERIAL_ORDER.flatMap((n) => [`/textures/${n}.jpg`, `/textures/${n}_depth.png`]),
      "/textures/chair_diffuse.jpg",
      "/textures/soldier_diffuse.jpg",
    ]);
  },
});

requestAnimationFrame(frame);
